import { useState, useMemo, useRef, useLayoutEffect, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Wallet, RefreshCw, Download, FileSpreadsheet, Gift, Ticket, Users, Clock,
  Coins, ReceiptText, TrendingUp, TrendingDown, Lock, Snowflake, UploadCloud,
  Settings2, CalendarDays, UserSearch, ShieldCheck, Save, Plug, AlertTriangle,
  Landmark, Briefcase, Building2, BarChart3, CalendarClock, ArrowRightLeft,
  Scale, PiggyBank, Link2, ChevronLeft, ChevronRight, CalendarRange,
  Target, Gauge, Trophy, Filter, ArrowUpDown, CheckCircle2, ArrowUp, ArrowDown, Layers,
  Receipt, Banknote, ArrowRight, Info, FileCheck2, CircleAlert, Stethoscope, ChevronDown, ChevronsUpDown, Check, Search,
  FlaskConical, Play, Copy, Send, Eye, Undo2, Sparkles, CalendarCheck2,
  Pencil, X, Plus, UserPlus, Sigma, History,
} from 'lucide-react';
import { useGet } from '@/lib/useData';
import api from '@/lib/api';
import { downloadGet } from '@/lib/download';
import { fmtEur, fmtInt, fmtNum, fmtPct } from '@/lib/format';
import { DecisionPanel } from '@/components/social/DecisionPanel';
import { PageHeader } from '@/components/common/PageHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  SelectGroup, SelectLabel, SelectSeparator,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from '@/components/ui/command';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip, TooltipTrigger, TooltipContent, TooltipProvider,
} from '@/components/ui/tooltip';

const MONTHS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet',
  'août', 'septembre', 'octobre', 'novembre', 'décembre'];

function labelYm(ym) {
  if (!ym) return '—';
  const [y, m] = ym.split('-');
  return `${MONTHS_FR[parseInt(m, 10) - 1]} ${y}`;
}

const accentStyle = { background: 'var(--tenant-accent-500)' };

// Onglets opérationnels regroupés dans le menu « Configuration »
const CONFIG_TABS = ['rattach', 'params', 'import'];
const CONFIG_LABELS = { rattach: 'Rattachement', params: 'Paramètres', import: 'Import heures' };

// Métadonnées de navigation (réutilisées pour le sélecteur mobile & les onglets desktop)
const MAIN_TABS = [
  { k: 'mois', label: 'Vue Mois', icon: Wallet },
  { k: 'pnl', label: 'P&L', icon: BarChart3 },
  { k: 'rentab', label: 'Rentabilité', icon: Gauge },
  { k: 'horaire', label: 'Analyse Horaire', icon: Clock },
  { k: 'paie', label: 'Paie indés', icon: Receipt },
  { k: 'payfit', label: 'Export PayFit', icon: FileSpreadsheet },
  { k: 'jour', label: 'Vue Jour', icon: CalendarDays },
  { k: 'collab', label: 'Collaborateur', icon: UserSearch },
];
const CONFIG_TAB_ITEMS = [
  { k: 'rattach', label: 'Rattachement', icon: Link2 },
  { k: 'params', label: 'Paramètres', icon: Settings2 },
  { k: 'import', label: 'Import heures', icon: UploadCloud },
];

// --------------------------------------------------------------------------- //
//  Small presentational helpers                                               //
// --------------------------------------------------------------------------- //
const Card = ({ children, className = '', ...rest }) => (
  <div className={`rounded-2xl border bg-card card-shadow ${className}`} {...rest}>{children}</div>
);

function Kpi({ icon: Icon, label, value, sub, delta, testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground">
        {Icon && <Icon className="h-4 w-4 accent-text" />}
        <span className="text-xs font-medium">{label}</span>
      </div>
      <div className="mt-2 kpi-value text-xl text-foreground tabular">{value}</div>
      <div className="mt-1 flex items-center gap-2">
        {sub && <span className="text-[11px] text-muted-foreground">{sub}</span>}
        {delta}
      </div>
    </Card>
  );
}

function Delta({ cur, prev, invert = false }) {
  if (prev === null || prev === undefined || !isFinite(prev) || prev === 0) return null;
  const pct = (cur - prev) / Math.abs(prev);
  if (!isFinite(pct)) return null;
  const up = pct >= 0;
  // Neutral, brand-consistent chip (no jarring red/green) — direction only.
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground tabular">
      <Icon className="h-3 w-3" />
      {`${up ? '+' : ''}${fmtNum(pct * 100, 1)}\u202f%`}
    </span>
  );
}

function TableWrap({ children }) {
  return <div className="table-wrap overflow-x-auto rounded-2xl border bg-card card-shadow">{children}</div>;
}

// Responsive table: identical markup on desktop; on mobile (< lg) CSS stacks each
// row into a labelled card. Labels are auto-derived from the column headers here
// (ref + observer) so no per-cell edits are needed and the values stay untouched.
function CardTable({ className = '', children, ...rest }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const table = ref.current;
    if (!table) return undefined;
    const apply = () => {
      const heads = Array.from(table.querySelectorAll('thead th')).map((th) => th.textContent.trim());
      table.querySelectorAll('tbody tr, tfoot tr').forEach((tr) => {
        let col = 0;
        Array.from(tr.children).forEach((cell) => {
          if (cell.tagName !== 'TD') return;
          const span = parseInt(cell.getAttribute('colspan') || '1', 10);
          if (col === 0 && span === 1) {
            cell.classList.add('card-title');
            cell.removeAttribute('data-label');
          } else if (span === 1) {
            cell.setAttribute('data-label', heads[col] || '');
          }
          col += span;
        });
      });
    };
    apply();
    const obs = new MutationObserver(apply);
    obs.observe(table, { childList: true, subtree: true });
    return () => obs.disconnect();
  });
  return <table ref={ref} className={`table-cards ${className}`} {...rest}>{children}</table>;
}

function EmptyState({ title, hint }) {
  return (
    <Card className="flex flex-col items-center justify-center gap-2 p-10 text-center">
      <AlertTriangle className="h-6 w-6 text-muted-foreground" />
      <p className="font-display text-sm font-semibold">{title}</p>
      {hint && <p className="max-w-md text-xs text-muted-foreground">{hint}</p>}
    </Card>
  );
}

// --------------------------------------------------------------------------- //
//  Shared — populations (Lot 1)                                                //
// --------------------------------------------------------------------------- //
const POP_META = {
  salaries: { label: 'Salariés', icon: Users, testid: 'salaries' },
  forfait: { label: 'Indés — forfait', icon: Briefcase, testid: 'forfait' },
  bulletin: { label: 'Indés — bulletin', icon: ReceiptText, testid: 'bulletin' },
  structure: { label: 'Structure', icon: Landmark, testid: 'structure' },
};

// Cost card per population, with share bar (part of total cost).
function PopCostCard({ metaKey, cost, count, total, prefix = 'pop', rentInfo }) {
  const meta = POP_META[metaKey];
  const share = total ? Math.max(0, Math.min(1, cost / total)) : 0;
  return (
    <Card className="p-4" data-testid={`${prefix}-${meta.testid}`}>
      <div className="flex items-center gap-2 text-muted-foreground">
        <meta.icon className="h-4 w-4 accent-text" />
        <span className="text-xs font-medium">{meta.label}</span>
        {rentInfo && <RentabDot info={rentInfo} />}
        {count !== undefined && count !== null && (
          <Badge variant="secondary" className="ml-auto text-[10px]">{count}</Badge>
        )}
      </div>
      <div className="mt-2 kpi-value text-xl text-foreground tabular">{fmtEur(cost, 2)}</div>
      <div className="mt-1 text-[11px] text-muted-foreground">{fmtPct(share, 0)} du coût total</div>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted" aria-hidden>
        <div className="h-full rounded-full transition-[width] duration-200" style={{ width: `${share * 100}%`, background: 'var(--tenant-accent-500)' }} />
      </div>
    </Card>
  );
}

// CDD / CDI status pill — CDI = « sans congés ni précarité » (Lot 1 rule).
function StatutEmploiBadge({ statut }) {
  const cdi = String(statut || '').toLowerCase() === 'cdi';
  return (
    <Badge variant="outline" className={`gap-1 text-[10px] ${cdi ? 'border-amber-300 text-amber-700' : 'border-border text-muted-foreground'}`}>
      {cdi ? 'CDI · sans congés/préca' : 'CDD'}
    </Badge>
  );
}

// Section title + optional right slot, consistent with module style.
function SectionTitle({ icon: Icon, title, sub, right }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 accent-text" />}
        <h3 className="font-display text-sm font-semibold">{title}</h3>
        {sub && <span className="text-[11px] text-muted-foreground">{sub}</span>}
      </div>
      {right}
    </div>
  );
}

// --------------------------------------------------------------------------- //
//  Vue Mois                                                                    //
// --------------------------------------------------------------------------- //

// Cellule TR éditable : corrige manuellement le nombre de titres-restaurant d'un
// collaborateur pour le mois (ex. journée de formation comptabilisée à tort). La
// valeur auto reste consultable et le calcul (net, coût) se recalcule en live.
function TrCell({ row, ym, frozen, onSaved }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(String(row.nb_tr ?? 0));
  const [saving, setSaving] = useState(false);
  const overridden = !!row.tr_overridden;
  const auto = row.nb_tr_auto ?? row.nb_tr ?? 0;
  const hist = metaHint((row.manual_meta || {}).tr);

  useEffect(() => { if (open) setVal(String(row.nb_tr ?? 0)); }, [open, row.nb_tr]);

  const save = async () => {
    const n = parseInt(String(val).trim(), 10);
    if (Number.isNaN(n) || n < 0) { toast.error('Entrez un nombre de TR valide (≥ 0).'); return; }
    setSaving(true);
    try {
      await api.post('/social/manual/tr', { matricule: row.matricule, ym, tr: n });
      toast.success(`TR corrigé pour ${row.prenom} ${row.nom} : ${n} (auto ${auto}).`);
      setOpen(false);
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Correction impossible.'); }
    setSaving(false);
  };

  const reset = async () => {
    setSaving(true);
    try {
      await api.post('/social/manual/tr', { matricule: row.matricule, ym, tr: null });
      toast.success(`TR réinitialisé (calcul auto = ${auto}).`);
      setOpen(false);
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Réinitialisation impossible.'); }
    setSaving(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`social-tr-cell-${row.matricule}`}
          className={`group/tr inline-flex items-center justify-end gap-1 rounded-md px-1.5 py-0.5 text-right tabular transition-colors hover:bg-[color:var(--tenant-accent-050)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--tenant-accent-500)] ${overridden ? 'font-semibold text-[color:var(--tenant-accent-700)]' : 'text-foreground'}`}
          title={overridden ? `TR corrigé manuellement (auto : ${auto}).${hist ? ' ' + hist + '.' : ''} Cliquez pour modifier.` : 'Cliquez pour corriger le nombre de TR'}
        >
          {overridden && <span className="h-1.5 w-1.5 rounded-full" style={{ background: 'var(--tenant-accent-500)' }} aria-hidden />}
          <span>{fmtInt(row.nb_tr)}</span>
          <Pencil className="h-3 w-3 opacity-0 transition-opacity group-hover/tr:opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64" data-testid={`social-tr-popover-${row.matricule}`}>
        <div className="space-y-3">
          <div>
            <p className="text-sm font-semibold text-foreground">Titres-restaurant — {row.prenom} {row.nom}</p>
            <p className="text-[11px] text-muted-foreground">{labelYm(ym)} · calcul auto : <b>{auto}</b> TR{overridden ? ' · corrigé manuellement' : ''}</p>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="number" min="0" step="1" value={val}
              onChange={(e) => setVal(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
              className="h-9 w-24 tabular" data-testid={`social-tr-input-${row.matricule}`} autoFocus
            />
            <Button size="sm" className="h-9 flex-1 gap-1.5" onClick={save} disabled={saving}
              style={{ background: 'var(--tenant-accent-500)' }} data-testid={`social-tr-save-${row.matricule}`}>
              <Check className="h-4 w-4" /> Enregistrer
            </Button>
          </div>
          <p className="text-[11px] leading-snug text-muted-foreground">
            Une journée courte sans terrain (ex. formation) ne donne pas droit au TR.
            La correction recalcule aussitôt le net et le coût.
          </p>
          {overridden && hist && (
            <p className="flex items-start gap-1.5 rounded-md bg-muted/50 px-2 py-1.5 text-[11px] text-muted-foreground" data-testid={`social-tr-hist-${row.matricule}`}>
              <History className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {hist}
            </p>
          )}
          {overridden && (
            <Button variant="ghost" size="sm" className="h-8 w-full justify-center gap-1.5 text-muted-foreground hover:text-foreground"
              onClick={reset} disabled={saving} data-testid={`social-tr-reset-${row.matricule}`}>
              <Undo2 className="h-3.5 w-3.5" /> Réinitialiser (auto {auto})
            </Button>
          )}
          {frozen && (
            <p className="flex items-start gap-1.5 rounded-md bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Mois gelé : la correction sera prise en compte au recalcul.
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Formate l'historique d'une correction manuelle : « Saisi par X le JJ/MM/AAAA à HH:MM ».
function metaHint(meta) {
  if (!meta || !meta.at) return '';
  let when = '';
  try {
    const dt = new Date(meta.at);
    when = dt.toLocaleDateString('fr-FR') + ' à ' + dt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  } catch (e) { when = String(meta.at); }
  return meta.by ? `Saisi par ${meta.by} le ${when}` : `Saisi le ${when}`;
}

// Cellule générique d'un poste d'ajustement éditable (€) : transport / mutuelle /
// prime_macron. Ajoute/corrige le montant, impacte le net (net = … + champ) et donc
// le coût (coût = net / coeff). Recalcul live. L'historique (qui/quand) s'affiche au
// survol et dans le panneau. 0 € = aucun.
function ManualAmountCell({ row, ym, frozen, onSaved, field, label, allowNegative = false }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(String(row[field] ?? 0));
  const [saving, setSaving] = useState(false);
  const amount = Number(row[field] || 0);
  const has = Math.abs(amount) > 0.0001;
  const meta = (row.manual_meta || {})[field];
  const hist = metaHint(meta);

  useEffect(() => { if (open) setVal(String(row[field] ?? 0)); }, [open, row, field]);

  const persist = async (value) => {
    const n = Math.round((parseFloat(String(value).replace(',', '.')) || 0) * 100) / 100;
    if (Number.isNaN(n) || (!allowNegative && n < 0)) { toast.error('Entrez un montant valide' + (allowNegative ? '.' : ' (≥ 0 €).')); return; }
    setSaving(true);
    try {
      await api.post('/social/manual/field', { matricule: row.matricule, ym, field, value: n });
      toast.success(Math.abs(n) > 0.0001 ? `${label} ${fmtEur(n, 2)} enregistré pour ${row.prenom} ${row.nom}.` : `${label} retiré pour ${row.prenom} ${row.nom}.`);
      setOpen(false);
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Enregistrement impossible.'); }
    setSaving(false);
  };

  const cellTitle = has
    ? `${label} ${fmtEur(amount, 2)}.${hist ? ' ' + hist + '.' : ''} Cliquez pour modifier.`
    : `Cliquez pour ajouter : ${label}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`social-${field}-cell-${row.matricule}`}
          className={`group/amt inline-flex items-center justify-end gap-1 rounded-md px-1.5 py-0.5 text-right tabular transition-colors hover:bg-[color:var(--tenant-accent-050)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--tenant-accent-500)] ${has ? 'font-semibold text-[color:var(--tenant-accent-700)]' : 'text-muted-foreground'}`}
          title={cellTitle}
        >
          {has && <span className="h-1.5 w-1.5 rounded-full" style={{ background: 'var(--tenant-accent-500)' }} aria-hidden />}
          <span>{has ? fmtEur(amount, 2) : '—'}</span>
          <Pencil className="h-3 w-3 opacity-0 transition-opacity group-hover/amt:opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64" data-testid={`social-${field}-popover-${row.matricule}`}>
        <div className="space-y-3">
          <div>
            <p className="text-sm font-semibold text-foreground">{label} — {row.prenom} {row.nom}</p>
            <p className="text-[11px] text-muted-foreground">{labelYm(ym)}{has ? ` · actuel ${fmtEur(amount, 2)}` : ''}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Input
                type="number" min={allowNegative ? undefined : '0'} step="0.01" value={val}
                onChange={(e) => setVal(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') persist(val); }}
                className="h-9 w-full pr-6 tabular" data-testid={`social-${field}-input-${row.matricule}`} autoFocus
              />
              <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">€</span>
            </div>
            <Button size="sm" className="h-9 gap-1.5" onClick={() => persist(val)} disabled={saving}
              style={{ background: 'var(--tenant-accent-500)' }} data-testid={`social-${field}-save-${row.matricule}`}>
              <Check className="h-4 w-4" /> Enregistrer
            </Button>
          </div>
          <p className="text-[11px] leading-snug text-muted-foreground">
            Ce montant s'ajoute au net et ajuste le coût (coût = net / coefficient). Recalcul immédiat.
          </p>
          {hist && (
            <p className="flex items-start gap-1.5 rounded-md bg-muted/50 px-2 py-1.5 text-[11px] text-muted-foreground" data-testid={`social-${field}-hist-${row.matricule}`}>
              <History className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {hist}
            </p>
          )}
          {has && (
            <Button variant="ghost" size="sm" className="h-8 w-full justify-center gap-1.5 text-muted-foreground hover:text-foreground"
              onClick={() => persist(0)} disabled={saving} data-testid={`social-${field}-reset-${row.matricule}`}>
              <Undo2 className="h-3.5 w-3.5" /> Retirer
            </Button>
          )}
          {frozen && (
            <p className="flex items-start gap-1.5 rounded-md bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Mois gelé : la correction sera prise en compte au recalcul.
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Cellule PRIME éditable : prime automatique (barème BS) + bonus bruts manuels (en SUS),
// chacun avec un motif (ex. « rebook spot »). Les bonus sont de vrais bruts : ils s'ajoutent
// à la prime et impactent congés / précarité / URSSAF / net / coût (recalcul live). Plusieurs
// bonus cumulables par mois. 0 bonus => affiche la seule prime barème.
function PrimeBonusCell({ row, ym, frozen, onSaved }) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const bonuses = row.bonus_brut || [];
  const primeAuto = Number(row.prime_auto ?? row.prime ?? 0);
  const primeBonus = Number(row.prime_bonus ?? 0);
  const total = Number(row.prime ?? 0);
  const hasBonus = bonuses.length > 0 || primeBonus > 0.0001;

  useEffect(() => { if (open) { setLabel(''); setAmount(''); } }, [open]);

  const add = async () => {
    const lbl = label.trim();
    const n = Math.round((parseFloat(String(amount).replace(',', '.')) || 0) * 100) / 100;
    if (!lbl) { toast.error('Indiquez un motif pour le bonus.'); return; }
    if (Number.isNaN(n) || n <= 0) { toast.error('Entrez un montant strictement positif (€).'); return; }
    setSaving(true);
    try {
      await api.post('/social/manual/bonus', { matricule: row.matricule, ym, label: lbl, amount: n });
      toast.success(`Bonus « ${lbl} » de ${fmtEur(n, 2)} ajouté à ${row.prenom} ${row.nom}.`);
      setLabel(''); setAmount('');
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Ajout impossible.'); }
    setSaving(false);
  };

  const remove = async (b) => {
    setBusyId(b.id);
    try {
      await api.post('/social/manual/bonus/delete', { matricule: row.matricule, ym, bonus_id: b.id });
      toast.success(`Bonus « ${b.label} » retiré pour ${row.prenom} ${row.nom}.`);
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Suppression impossible.'); }
    setBusyId(null);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`social-prime-cell-${row.matricule}`}
          className={`group/prime inline-flex items-center justify-end gap-1 rounded-md px-1.5 py-0.5 text-right tabular transition-colors hover:bg-[color:var(--tenant-accent-050)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--tenant-accent-500)] ${hasBonus ? 'font-semibold text-[color:var(--tenant-accent-700)]' : 'text-foreground'}`}
          title={hasBonus ? `Prime ${fmtEur(total, 2)} (barème ${fmtEur(primeAuto, 2)} + bonus ${fmtEur(primeBonus, 2)}). Cliquez pour gérer les bonus.` : 'Cliquez pour ajouter un bonus brut'}
        >
          {hasBonus && <span className="h-1.5 w-1.5 rounded-full" style={{ background: 'var(--tenant-accent-500)' }} aria-hidden />}
          <span>{total ? fmtEur(total, 2) : '—'}</span>
          <Pencil className="h-3 w-3 opacity-0 transition-opacity group-hover/prime:opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80" data-testid={`social-prime-popover-${row.matricule}`}>
        <div className="space-y-3">
          <div>
            <p className="text-sm font-semibold text-foreground">Prime & bonus — {row.prenom} {row.nom}</p>
            <p className="text-[11px] text-muted-foreground">{labelYm(ym)}</p>
          </div>
          <div className="space-y-1 rounded-md bg-muted/50 px-2.5 py-2 text-[12px]">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Prime barème (auto)</span>
              <span className="tabular font-medium" data-testid={`social-prime-auto-${row.matricule}`}>{fmtEur(primeAuto, 2)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Bonus bruts manuels</span>
              <span className="tabular font-medium" data-testid={`social-prime-bonus-${row.matricule}`}
                style={primeBonus > 0 ? { color: 'var(--tenant-accent-700)' } : undefined}>
                {primeBonus > 0 ? '+ ' : ''}{fmtEur(primeBonus, 2)}
              </span>
            </div>
            <div className="flex items-center justify-between border-t pt-1 font-semibold">
              <span>Total prime</span>
              <span className="tabular" data-testid={`social-prime-total-${row.matricule}`}>{fmtEur(total, 2)}</span>
            </div>
          </div>

          {bonuses.length > 0 && (
            <ul className="space-y-1" data-testid={`social-bonus-list-${row.matricule}`}>
              {bonuses.map((b) => (
                <li key={b.id}
                  className="flex items-center gap-2 rounded-md border px-2 py-1.5 text-[12px]"
                  data-testid={`social-bonus-item-${row.matricule}-${b.id}`}>
                  <Gift className="h-3.5 w-3.5 shrink-0 text-[color:var(--tenant-accent-600)]" />
                  <span className="min-w-0 flex-1 truncate" title={metaHint({ at: b.at, by: b.by })}>{b.label}</span>
                  <span className="tabular font-semibold">{fmtEur(b.amount, 2)}</span>
                  <button type="button" onClick={() => remove(b)} disabled={busyId === b.id || frozen}
                    className="rounded p-0.5 text-muted-foreground transition-colors hover:text-rose-600 disabled:opacity-40"
                    title="Retirer ce bonus" data-testid={`social-bonus-del-${row.matricule}-${b.id}`}>
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {!frozen && (
            <div className="space-y-2 rounded-md border border-dashed p-2">
              <p className="text-[11px] font-medium text-muted-foreground">Ajouter un bonus brut</p>
              <Input value={label} onChange={(e) => setLabel(e.target.value)}
                placeholder="Motif (ex. rebook spot)" className="h-9"
                data-testid={`social-bonus-label-${row.matricule}`} />
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Input type="number" min="0" step="0.01" value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
                    placeholder="Montant" className="h-9 w-full pr-6 tabular"
                    data-testid={`social-bonus-amount-${row.matricule}`} />
                  <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">€</span>
                </div>
                <Button size="sm" className="h-9 gap-1.5" onClick={add} disabled={saving}
                  style={{ background: 'var(--tenant-accent-500)' }} data-testid={`social-bonus-add-${row.matricule}`}>
                  <Plus className="h-4 w-4" /> Ajouter
                </Button>
              </div>
            </div>
          )}

          <p className="text-[11px] leading-snug text-muted-foreground">
            Un bonus est un <b>vrai brut</b> : il s'ajoute à la prime et augmente congés, précarité,
            URSSAF, net et coût employeur. Recalcul immédiat.
          </p>

          {frozen && (
            <p className="flex items-start gap-1.5 rounded-md bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
              data-testid={`social-prime-frozen-${row.matricule}`}>
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Mois gelé : les bonus ne sont pas modifiables.
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}



function MonthView({ ym }) {
  const q = useGet('/social/month', { ym }, !!ym);
  const qc = useQueryClient();
  const refetchSocial = () => qc.invalidateQueries({ predicate: (query) => String(query.queryKey?.[0] || '').startsWith('/social') });
  const d = q.data;
  const rq = useGet('/social/rentabilite', { type: 'mois', ym }, !!ym);
  const rmap = useMemo(() => {
    const m = {};
    (rq.data?.rows || []).forEach((r) => { m[r.matricule] = r; });
    return m;
  }, [rq.data]);
  const popStatut = useMemo(() => {
    const agg = {};
    (rq.data?.rows || []).forEach((r) => {
      const a = agg[r.population] || (agg[r.population] = { ca: 0, cout: 0 });
      a.ca += r.ca_attribue_proj || 0;
      a.cout += r.cout_proj || 0;
    });
    const seuil = rq.data?.seuil;
    const bm = rq.data?.borne_min ?? 1;
    const out = {};
    Object.entries(agg).forEach(([pop, a]) => {
      const ratio = a.cout > 0 ? a.ca / a.cout : null;
      out[pop] = { statut: statutFromRatio(ratio, seuil, bm), ratio_proj: ratio, population: pop };
    });
    return out;
  }, [rq.data]);
  if (q.isLoading && !d) {
    return <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
      {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}
    </div>;
  }
  if (!d || !d.rows?.length) {
    return <EmptyState title="Aucune donnée pour ce mois"
      hint="Importez les heures du mois (onglet « Import heures ») ou choisissez un autre mois." />;
  }
  const t = d.totals || {};
  const pt = d.prev_totals || {};
  return (
    <TooltipProvider delayDuration={100}>
    <div className="space-y-4" data-testid="social-month-view">
      {/* Barre de contexte (source, périmètre, comparaison) */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border bg-muted/30 px-4 py-3 text-xs" data-testid="social-status-strip">
        <Badge variant="secondary" className="gap-1" data-testid="social-hours-source"><Clock className="h-3.5 w-3.5" /> Heures : {d.hours_source === 'booking' ? 'Booking' : 'Excel'}</Badge>
        <Badge variant="secondary" className="gap-1" data-testid="social-bs-source"><Gift className="h-3.5 w-3.5" /> BS : {d.bs_source_used === 'daily_records' ? 'Terrain (bs réel)' : d.bs_source_used === 'reference' ? 'Référence' : d.bs_source_used}</Badge>
        <Badge variant="secondary" className="gap-1"><Users className="h-3.5 w-3.5" /> {t.nb_collaborateurs} collaborateurs</Badge>
        {d.bs_sans_heures?.length > 0 && (
          <Badge variant="outline" className="gap-1 border-amber-300 text-amber-700 dark:text-amber-300">
            <AlertTriangle className="h-3.5 w-3.5" /> {d.bs_sans_heures.length} BS sans heures (P.A./sous-traitants)
          </Badge>
        )}
        {d.frozen && <Badge className="gap-1 text-white" style={accentStyle}><Snowflake className="h-3 w-3" /> Mois gelé</Badge>}
        <span className="ml-auto flex items-center gap-1.5 text-muted-foreground">
          <ArrowRightLeft className="h-3.5 w-3.5" /> Comparaison vs {labelYm(d.prev_ym)}
        </span>
      </div>

      {/* Bandeau rentabilité — visibilité instantanée (sans ouvrir l'onglet Rentabilité) */}
      <RentabLegend nb={rq.data?.nb} loading={rq.isLoading && !rq.data}
        contexte={rq.data ? 'projection fin de mois · détail au survol' : null} />

      {/* KPI grid */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        <Kpi icon={ReceiptText} label="Coût estimé total" value={fmtEur(t.cout, 2)} delta={<Delta cur={t.cout} prev={pt.cout} />} testid="kpi-cout" />
        <Kpi icon={Coins} label="Net estimé" value={fmtEur(t.net, 2)} delta={<Delta cur={t.net} prev={pt.net} />} testid="kpi-net" />
        <Kpi icon={Wallet} label="Brut de base" value={fmtEur(t.brut, 2)} delta={<Delta cur={t.brut} prev={pt.brut} />} testid="kpi-brut" />
        <Kpi icon={Clock} label="Heures" value={fmtNum(t.heures, 2)} sub={`${fmtInt(t.nb_tr)} titres resto`} delta={<Delta cur={t.heures} prev={pt.heures} />} testid="kpi-heures" />
        <Kpi icon={Gift} label="BS (bulletins)" value={fmtInt(t.bs)} delta={<Delta cur={t.bs} prev={pt.bs} />} testid="kpi-bs" />
        <Kpi icon={TrendingUp} label="Coût / acquisition" value={fmtEur(t.cout_acquisition, 2)} sub={`Taux CT/CA ${fmtPct(t.taux_ct_ca_global, 1)}`} testid="kpi-cout-acq" />
      </div>

      {/* Populations (Lot 1) — the 3 distinct populations + structure */}
      {d.pnl && (
        <div className="space-y-3" data-testid="social-populations">
          <SectionTitle
            icon={BarChart3}
            title="Coûts par population"
            sub="Salariés · Indés forfait · Indés bulletin · Structure"
            right={<Badge variant="outline" className="gap-1 text-[11px]"><Coins className="h-3.5 w-3.5" /> CA net {fmtEur(d.pnl.ca_net, 2)}</Badge>}
          />
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <PopCostCard metaKey="salaries" cost={d.pnl.cout_salaries} count={d.pnl.nb_salaries} total={d.pnl.cout_toutes_populations + d.pnl.structure} prefix="month-pop" rentInfo={popStatut.salarie} />
            <PopCostCard metaKey="forfait" cost={d.pnl.cout_inde_forfait} count={d.pnl.nb_inde_forfait} total={d.pnl.cout_toutes_populations + d.pnl.structure} prefix="month-pop" rentInfo={popStatut.inde_forfait} />
            <PopCostCard metaKey="bulletin" cost={d.pnl.cout_inde_bulletin} count={d.pnl.nb_inde_bulletin} total={d.pnl.cout_toutes_populations + d.pnl.structure} prefix="month-pop" rentInfo={popStatut.inde_bulletin} />
            <PopCostCard metaKey="structure" cost={d.pnl.structure} total={d.pnl.cout_toutes_populations + d.pnl.structure} prefix="month-pop" />
          </div>
        </div>
      )}

      {/* Detail table — population Salariés */}
      <SectionTitle icon={Users} title="Population — Salariés" sub={`${t.nb_collaborateurs} collaborateur(s)`} />
      <TableWrap>
        <CardTable className="w-full min-w-[1100px] text-sm">
          <thead>
            <tr className="border-b bg-muted/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
              <th className="px-3 py-2 font-medium">Collaborateur</th>
              <th className="px-2 py-2 text-right font-medium">Heures</th>
              <th className="px-2 py-2 text-right font-medium">TR</th>
              <th className="px-2 py-2 text-right font-medium">BS</th>
              <th className="px-2 py-2 text-right font-medium">Brut</th>
              <th className="px-2 py-2 text-right font-medium">Prime</th>
              <th className="px-2 py-2 text-right font-medium">Congés</th>
              <th className="px-2 py-2 text-right font-medium">Préca</th>
              <th className="px-2 py-2 text-right font-medium">Urssaf</th>
              <th className="px-2 py-2 text-right font-medium">Transport</th>
              <th className="px-2 py-2 text-right font-medium">Mutuelle</th>
              <th className="px-2 py-2 text-right font-medium">Prime Macron</th>
              <th className="px-2 py-2 text-right font-medium">Net est.</th>
              <th className="px-2 py-2 text-right font-medium">Coût est.</th>
              <th className="px-2 py-2 text-right font-medium">Coût/BS</th>
              <th className="px-3 py-2 text-right font-medium">Taux h.</th>
            </tr>
          </thead>
          <tbody className="tabular">
            {d.rows.map((r) => (
              <tr key={r.matricule} className="border-b transition-colors last:border-0 hover:bg-muted/40" data-testid={`social-row-${r.matricule}`}>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    <RentabDot info={rmap[r.matricule]} />
                    <span className="font-medium text-foreground">{r.prenom} {r.nom}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground">{r.matricule}{r.inconnu ? ' · inconnu' : ''}</div>
                </td>
                <td className="px-2 py-2 text-right">{fmtNum(r.heures, 2)}</td>
                <td className="px-2 py-2 text-right"><TrCell row={r} ym={ym} frozen={d.frozen} onSaved={refetchSocial} /></td>
                <td className="px-2 py-2 text-right">{fmtInt(r.bs)}</td>
                <td className="px-2 py-2 text-right">{fmtEur(r.brut, 2)}</td>
                <td className="px-2 py-2 text-right"><PrimeBonusCell row={r} ym={ym} frozen={d.frozen} onSaved={refetchSocial} /></td>
                <td className="px-2 py-2 text-right">{fmtEur(r.conges, 2)}</td>
                <td className="px-2 py-2 text-right">{fmtEur(r.preca, 2)}</td>
                <td className="px-2 py-2 text-right text-muted-foreground">{fmtEur(r.urssaf, 2)}</td>
                <td className="px-2 py-2 text-right"><ManualAmountCell row={r} ym={ym} frozen={d.frozen} onSaved={refetchSocial} field="transport" label="Remboursement transport" /></td>
                <td className="px-2 py-2 text-right"><ManualAmountCell row={r} ym={ym} frozen={d.frozen} onSaved={refetchSocial} field="mutuelle" label="Mutuelle" allowNegative /></td>
                <td className="px-2 py-2 text-right"><ManualAmountCell row={r} ym={ym} frozen={d.frozen} onSaved={refetchSocial} field="prime_macron" label="Prime Macron" /></td>
                <td className="px-2 py-2 text-right font-medium">{fmtEur(r.net, 2)}</td>
                <td className="px-2 py-2 text-right font-semibold accent-text">{fmtEur(r.cout, 2)}</td>
                <td className="px-2 py-2 text-right">{r.bs ? fmtEur(r.cout_acquisition, 2) : '—'}</td>
                <td className="px-3 py-2 text-right">{fmtEur(r.taux_horaire_affiche, 2)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 bg-muted/30 font-semibold tabular" data-testid="social-month-totals">
              <td className="px-3 py-2">Total ({t.nb_collaborateurs})</td>
              <td className="px-2 py-2 text-right">{fmtNum(t.heures, 2)}</td>
              <td className="px-2 py-2 text-right">{fmtInt(t.nb_tr)}</td>
              <td className="px-2 py-2 text-right">{fmtInt(t.bs)}</td>
              <td className="px-2 py-2 text-right">{fmtEur(t.brut, 2)}</td>
              <td className="px-2 py-2 text-right">{fmtEur(t.prime, 2)}</td>
              <td className="px-2 py-2 text-right">{fmtEur(t.conges, 2)}</td>
              <td className="px-2 py-2 text-right">{fmtEur(t.preca, 2)}</td>
              <td className="px-2 py-2 text-right">{fmtEur(t.urssaf, 2)}</td>
              <td className="px-2 py-2 text-right">{t.transport ? fmtEur(t.transport, 2) : '—'}</td>
              <td className="px-2 py-2 text-right">{t.mutuelle ? fmtEur(t.mutuelle, 2) : '—'}</td>
              <td className="px-2 py-2 text-right">{t.prime_macron ? fmtEur(t.prime_macron, 2) : '—'}</td>
              <td className="px-2 py-2 text-right">{fmtEur(t.net, 2)}</td>
              <td className="px-2 py-2 text-right accent-text">{fmtEur(t.cout, 2)}</td>
              <td className="px-2 py-2 text-right">{fmtEur(t.cout_acquisition, 2)}</td>
              <td className="px-3 py-2 text-right">{fmtEur(t.taux_horaire_charge, 2)}</td>
            </tr>
          </tfoot>
        </CardTable>
      </TableWrap>

      {/* Population — Indés au forfait */}
      {d.pnl?.inde_forfait?.length > 0 && (
        <div className="space-y-2" data-testid="month-inde-forfait">
          <SectionTitle icon={Briefcase} title="Population — Indés au forfait" sub="montant mensuel lissé sur les jours d'activité" />
          <TableWrap>
            <CardTable className="w-full min-w-[560px] text-sm">
              <thead>
                <tr className="border-b bg-muted/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Collaborateur</th>
                  <th className="px-2 py-2 text-right font-medium">Montant mensuel</th>
                  <th className="px-2 py-2 text-right font-medium">Coût / jour</th>
                  <th className="px-2 py-2 text-center font-medium">Statut</th>
                  <th className="px-3 py-2 text-right font-medium">Coût réalisé</th>
                </tr>
              </thead>
              <tbody className="tabular">
                {d.pnl.inde_forfait.map((r) => (
                  <tr key={r.matricule} className="border-b last:border-0 hover:bg-muted/40" data-testid={`month-forfait-row-${r.matricule}`}>
                    <td className="px-3 py-2"><span className="inline-flex items-center gap-1.5"><RentabDot info={rmap[r.matricule]} /><span className="font-medium">{r.prenom} {r.nom}</span></span> <span className="text-[11px] text-muted-foreground">{r.matricule}</span></td>
                    <td className="px-2 py-2 text-right">{fmtEur(r.montant, 2)}</td>
                    <td className="px-2 py-2 text-right">{fmtEur(r.cout_jour, 2)}</td>
                    <td className="px-2 py-2 text-center">
                      <Badge variant={r.actif ? 'secondary' : 'outline'} className="text-[10px]">{r.actif ? 'actif' : r.statut}</Badge>
                    </td>
                    <td className="px-3 py-2 text-right font-semibold accent-text">{fmtEur(r.cout, 2)}</td>
                  </tr>
                ))}
              </tbody>
            </CardTable>
          </TableWrap>
        </div>
      )}

      {/* Population — Indés au bulletin */}
      {d.pnl?.inde_bulletin?.length > 0 && (
        <div className="space-y-2" data-testid="month-inde-bulletin">
          <SectionTitle icon={ReceiptText} title="Population — Indés au bulletin" sub={`mode retenu : ${d.pnl.mode_inde_totaux === 'forecast' ? 'Forecast (taux bloqué)' : 'Réel contrat'}`} />
          <TableWrap>
            <CardTable className="w-full min-w-[620px] text-sm">
              <thead>
                <tr className="border-b bg-muted/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Collaborateur</th>
                  <th className="px-2 py-2 text-right font-medium">Taux</th>
                  <th className="px-2 py-2 text-right font-medium">BS bruts</th>
                  <th className="px-2 py-2 text-right font-medium">BS nets</th>
                  <th className="px-2 py-2 text-right font-medium">Forecast</th>
                  <th className="px-2 py-2 text-right font-medium">Réel</th>
                  <th className="px-3 py-2 text-right font-medium">Écart</th>
                </tr>
              </thead>
              <tbody className="tabular">
                {d.pnl.inde_bulletin.map((r) => (
                  <tr key={r.matricule} className="border-b last:border-0 hover:bg-muted/40" data-testid={`month-bulletin-row-${r.matricule}`}>
                    <td className="px-3 py-2"><span className="inline-flex items-center gap-1.5"><RentabDot info={rmap[r.matricule]} /><span className="font-medium">{r.prenom} {r.nom}</span></span> <span className="text-[11px] text-muted-foreground">{r.matricule}</span></td>
                    <td className="px-2 py-2 text-right">{fmtEur(r.taux, 2)}</td>
                    <td className="px-2 py-2 text-right">{fmtInt(r.bs_bruts)}</td>
                    <td className="px-2 py-2 text-right">{fmtInt(r.bs_nets)}</td>
                    <td className="px-2 py-2 text-right font-medium">{fmtEur(r.forecast, 2)}</td>
                    <td className="px-2 py-2 text-right text-muted-foreground">{fmtEur(r.reel, 2)}</td>
                    <td className="px-3 py-2 text-right">{fmtEur(r.ecart, 2)}</td>
                  </tr>
                ))}
              </tbody>
            </CardTable>
          </TableWrap>
        </div>
      )}
    </div>
    </TooltipProvider>
  );
}

// --------------------------------------------------------------------------- //
//  Export PayFit                                                               //
// --------------------------------------------------------------------------- //
const EXPORTS = [
  { kind: 'decompte', icon: FileSpreadsheet, title: 'Décompte au réel', desc: 'Heures mensuelles, taux horaire et « Salaire de base » par collaborateur.' },
  { kind: 'primes', icon: Gift, title: 'Primes', desc: 'Primes exceptionnelles calculées depuis le barème BS, datées au dernier jour du mois.' },
  { kind: 'titres', icon: Ticket, title: 'Titres restaurant', desc: 'Nombre de titres (jours ≥ 3h), valeur faciale et part employeur.' },
];

function PayFitExport({ ym }) {
  const [busy, setBusy] = useState(null);
  const [freezing, setFreezing] = useState(false);
  const journal = useGet('/social/exports');
  const qc = useQueryClient();

  const run = async (kind, title) => {
    setBusy(kind);
    try {
      await downloadGet(`/social/export/payfit/${kind}`, `PayFit_${title.replace(/\s/g, '_')}_${ym}.xlsx`, { ym });
      toast.success(`Export « ${title} » généré pour ${labelYm(ym)}`);
      journal.refetch();
    } catch (e) {
      toast.error("Export impossible");
    } finally { setBusy(null); }
  };

  const freeze = async () => {
    setFreezing(true);
    try {
      await api.post('/social/freeze', null, { params: { ym } });
      toast.success(`Mois ${labelYm(ym)} gelé (snapshot enregistré)`);
      qc.invalidateQueries({ predicate: (query) => String(query.queryKey?.[0] || '').startsWith('/social') });
    } catch (e) {
      toast.error('Gel impossible');
    } finally { setFreezing(false); }
  };

  return (
    <div className="space-y-5" data-testid="social-payfit">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Fichiers d'import <strong className="text-foreground">PayFit</strong> pour <strong className="text-foreground">{labelYm(ym)}</strong>.
          En-tête et identités préservés ; colonnes non concernées laissées vides.
        </p>
        <Button variant="outline" className="gap-2 rounded-xl" onClick={freeze} disabled={freezing} data-testid="social-freeze-btn">
          <Snowflake className="h-4 w-4" /> {freezing ? 'Gel…' : 'Geler le mois'}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {EXPORTS.map((c) => (
          <Card key={c.kind} className="flex flex-col p-6" data-testid={`social-export-card-${c.kind}`}>
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl accent-bg-100"><c.icon className="h-6 w-6 accent-text" /></div>
            <h3 className="mt-4 font-display text-lg font-semibold">{c.title}</h3>
            <p className="mt-2 flex-1 text-sm text-muted-foreground">{c.desc}</p>
            <Button className="mt-4 gap-2 rounded-xl text-white" style={accentStyle}
              onClick={() => run(c.kind, c.title)} disabled={busy === c.kind} data-testid={`social-export-btn-${c.kind}`}>
              {busy === c.kind ? 'Génération…' : <><Download className="h-4 w-4" /> Télécharger</>}
            </Button>
          </Card>
        ))}
      </div>

      {/* Journal */}
      <Card className="p-5">
        <h3 className="mb-3 font-display text-sm font-semibold">Journal des exports</h3>
        {!journal.data?.length ? (
          <p className="text-xs text-muted-foreground">Aucun export généré pour le moment.</p>
        ) : (
          <div className="space-y-2">
            {journal.data.slice(0, 8).map((e) => (
              <div key={e.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-muted/40 px-3 py-2 text-xs">
                <span className="font-medium">{e.kind} · {labelYm(e.ym)}</span>
                <span className="text-muted-foreground">{e.summary?.lignes_remplies} lignes · {e.summary?.nb_non_apparies || 0} non appariées · {new Date(e.created_at).toLocaleString('fr-FR')}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// --------------------------------------------------------------------------- //
//  Vue Jour                                                                    //
// --------------------------------------------------------------------------- //
function DayView({ ym }) {
  // La Vue Jour s'ouvre sur AUJOURD'HUI (date locale), pas sur le 1er du mois sélectionné.
  const [day, setDay] = useState(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  });
  const [scope, setScope] = useState('jour');
  const q = useGet('/social/period', { scope, day }, !!day);
  const d = q.data;
  const rym = (day || '').slice(0, 7);
  const rq = useGet('/social/rentabilite', { type: 'mois', ym: rym }, !!rym);
  const rmap = useMemo(() => {
    const m = {};
    (rq.data?.rows || []).forEach((r) => { m[r.matricule] = r; });
    return m;
  }, [rq.data]);
  const mColor = (v) => (v > 0 ? 'text-emerald-600 dark:text-emerald-400'
    : v < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-muted-foreground');
  const plShort = { jour: 'jour', semaine: 'semaine', mois: 'mois', annee: 'année' }[scope] || 'jour';
  const pl = { jour: 'du jour', semaine: 'de la semaine', mois: 'du mois', annee: "de l'année" }[scope] || 'du jour';
  const isPeriod = scope !== 'jour';
  const sumBy = (arr, k) => (arr || []).reduce((s, r) => s + (Number(r[k]) || 0), 0);
  const hasAny = d && ((d.rows?.length || 0) + (d.inde_forfait?.length || 0) + (d.inde_bulletin?.length || 0) > 0);
  const thR = 'px-2 py-2 text-right font-medium';
  return (
    <TooltipProvider delayDuration={100}>
    <div className="space-y-4" data-testid="social-day-view">
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-xl border bg-card p-0.5" data-testid="day-scope-toggle">
          {[['jour', 'Jour'], ['semaine', 'Semaine'], ['mois', 'Mois'], ['annee', 'Année']].map(([k, lbl]) => (
            <button key={k} type="button" onClick={() => setScope(k)}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${scope === k ? 'text-white' : 'text-muted-foreground hover:text-foreground'}`}
              style={scope === k ? { background: 'var(--tenant-accent-500)' } : {}}
              data-testid={`day-scope-${k}`}>{lbl}</button>
          ))}
        </div>
        <Label htmlFor="day-input" className="text-sm">{{ jour: 'Journée', semaine: 'Semaine du', mois: 'Mois', annee: 'Année' }[scope]}</Label>
        {scope === 'mois' ? (
          <Input id="day-input" type="month" value={day.slice(0, 7)}
            onChange={(e) => e.target.value && setDay(`${e.target.value}-01`)}
            className="w-44 rounded-xl" data-testid="social-day-input" />
        ) : scope === 'annee' ? (
          <Input id="day-input" type="number" min="2023" max="2100" value={day.slice(0, 4)}
            onChange={(e) => e.target.value && setDay(`${e.target.value}-01-01`)}
            className="w-28 rounded-xl" data-testid="social-day-input" />
        ) : (
          <Input id="day-input" type="date" value={day}
            onChange={(e) => e.target.value && setDay(e.target.value)}
            className="w-44 rounded-xl" data-testid="social-day-input" />
        )}
        {isPeriod && d?.jours_pris != null && (
          <span className="text-xs text-muted-foreground" data-testid="day-period-label">
            {scope === 'semaine' && d.iso_week ? `Semaine ${d.iso_week} · ` : ''}{d.start} → {d.end} · {d.jours_pris} j réalisés
          </span>
        )}
      </div>
      {!hasAny ? (
        <EmptyState title={`Aucune activité ${pl}`} hint="Sélectionnez une période contenant des jours travaillés." />
      ) : (
        <>
          <RentabLegend nb={rq.data?.nb} loading={rq.isLoading && !rq.data}
            contexte={rq.data ? `rentabilité du mois (${labelYm(rym)}) · détail au survol` : null} />
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            <Kpi icon={Clock} label={`Heures (${plShort})`} value={fmtNum(d.total_heures, 2)} testid="kpi-day-heures" />
            <Kpi icon={Users} label="Collaborateurs" value={fmtInt(d.nb_collaborateurs + (d.nb_inde_forfait || 0) + (d.nb_inde_bulletin || 0))} testid="kpi-day-collab" />
            <Kpi icon={Gift} label={`BS (bulletins, ${plShort})`} value={fmtInt(d.bs_bruts_jour)} testid="kpi-day-bs" />
            <Kpi icon={Coins} label={`CA net (${plShort})`} value={fmtEur(d.ca_net_jour, 2)} testid="kpi-day-ca" />
            <Kpi icon={ReceiptText} label={`Coût chargé (${plShort})`} value={fmtEur(d.cout_direct_jour, 2)} testid="kpi-day-cout" />
            <Kpi icon={Ticket} label={`Titres resto (${plShort})`} value={fmtInt(sumBy(d.rows, 'tr'))} testid="kpi-day-tr" />
          </div>

          {/* Marge — les 2 lectures, ce que la direction regarde en bas */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="day-marge-summary">
            <div className="rounded-2xl border bg-card p-4 card-shadow" data-testid="day-marge-directe">
              <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground"><Scale className="h-3.5 w-3.5" /> Marge {pl} — <span className="font-semibold text-foreground">sans</span> coûts affectés</div>
              <div className={`kpi-value mt-1 text-3xl tabular ${mColor(d.marge_directe_jour)}`}>{fmtEur(d.marge_directe_jour, 2)}</div>
              <div className="mt-1 text-[11px] text-muted-foreground">CA net {fmtEur(d.ca_net_jour, 0)} − coûts directs {fmtEur(d.cout_direct_jour, 0)}</div>
            </div>
            <div className="rounded-2xl border p-4 card-shadow" style={{ borderColor: 'var(--tenant-accent-300)', background: 'var(--tenant-accent-050)' }} data-testid="day-marge-nette">
              <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground"><Scale className="h-3.5 w-3.5 accent-text" /> Marge {pl} — <span className="font-semibold text-foreground">avec</span> coûts affectés (structure)</div>
              <div className={`kpi-value mt-1 text-3xl tabular ${mColor(d.marge_nette_jour)}`}>{fmtEur(d.marge_nette_jour, 2)}</div>
              <div className="mt-1 text-[11px] text-muted-foreground">− structure {pl} {fmtEur(d.structure_jour, 0)} · CA non attribué {fmtEur(d.ca_non_attribue_jour, 0)}</div>
            </div>
          </div>

          {/* Salariés */}
          {d.rows?.length > 0 && (
            <div className="space-y-2" data-testid="social-day-salaries">
              <SectionTitle icon={Users} title="Salariés" sub="heures, titre resto, coût chargé, BS & CA ramenés, marge du jour (sans / avec coûts affectés)" />
              <TableWrap>
                <CardTable className="w-full min-w-[900px] text-sm">
                  <thead>
                    <tr className="border-b bg-muted/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                      <th className="px-3 py-2 font-medium">Collaborateur</th>
                      <th className={thR}>Heures</th>
                      <th className="px-2 py-2 text-center font-medium">TR</th>
                      <th className={thR}>Taux</th>
                      <th className="px-3 py-2 text-right font-medium">Coût chargé</th>
                      <th className={thR}>BS</th>
                      <th className={thR}>CA ramené</th>
                      <th className={thR}>Marge directe</th>
                      <th className="px-3 py-2 text-right font-medium">Marge nette</th>
                    </tr>
                  </thead>
                  <tbody className="tabular">
                    {d.rows.map((r) => (
                      <tr key={r.matricule} className="border-b last:border-0 hover:bg-muted/40" data-testid={`day-sal-row-${r.matricule}`}>
                        <td className="px-3 py-2"><span className="inline-flex items-center gap-1.5"><RentabDot info={rmap[r.matricule]} /><span className="font-medium">{r.prenom} {r.nom}</span></span> <span className="text-[11px] text-muted-foreground">{r.matricule}</span></td>
                        <td className="px-2 py-2 text-right">{fmtNum(r.heures, 2)}</td>
                        <td className="px-2 py-2 text-center">{r.tr > 0 ? <span className="font-medium text-emerald-600 dark:text-emerald-400">{r.tr}</span> : <span className="text-muted-foreground">0</span>}</td>
                        <td className="px-2 py-2 text-right">{fmtEur(r.taux, 2)}</td>
                        <td className="px-3 py-2 text-right">{fmtEur(r.cout_charge_jour, 2)}</td>
                        <td className="px-2 py-2 text-right">{fmtInt(r.bs_bruts)}</td>
                        <td className="px-2 py-2 text-right">{fmtEur(r.ca_attribue, 2)}</td>
                        <td className={`px-2 py-2 text-right font-medium ${mColor(r.marge_directe)}`}>{fmtEur(r.marge_directe, 2)}</td>
                        <td className={`px-3 py-2 text-right font-semibold ${mColor(r.marge_nette)}`}>{fmtEur(r.marge_nette, 2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 bg-muted/50 font-semibold" data-testid="day-sal-total">
                      <td className="px-3 py-2">Total salariés ({d.rows.length})</td>
                      <td className="px-2 py-2 text-right">{fmtNum(sumBy(d.rows, 'heures'), 2)}</td>
                      <td className="px-2 py-2 text-center">{fmtInt(sumBy(d.rows, 'tr'))}</td>
                      <td className="px-2 py-2 text-right text-muted-foreground">—</td>
                      <td className="px-3 py-2 text-right">{fmtEur(sumBy(d.rows, 'cout_charge_jour'), 2)}</td>
                      <td className="px-2 py-2 text-right">{fmtInt(sumBy(d.rows, 'bs_bruts'))}</td>
                      <td className="px-2 py-2 text-right">{fmtEur(sumBy(d.rows, 'ca_attribue'), 2)}</td>
                      <td className={`px-2 py-2 text-right ${mColor(sumBy(d.rows, 'marge_directe'))}`}>{fmtEur(sumBy(d.rows, 'marge_directe'), 2)}</td>
                      <td className={`px-3 py-2 text-right ${mColor(sumBy(d.rows, 'marge_nette'))}`}>{fmtEur(sumBy(d.rows, 'marge_nette'), 2)}</td>
                    </tr>
                  </tfoot>
                </CardTable>
              </TableWrap>
            </div>
          )}

          {/* Indés au forfait */}
          {d.inde_forfait?.length > 0 && (
            <div className="space-y-2" data-testid="social-day-inde-forfait">
              <SectionTitle icon={Briefcase} title="Indés au forfait — coût lissé du jour" sub="montant mensuel / jours d'activité · BS & CA ramenés, marge du jour" />
              <TableWrap>
                <CardTable className="w-full min-w-[820px] text-sm">
                  <thead>
                    <tr className="border-b bg-muted/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                      <th className="px-3 py-2 font-medium">Indépendant</th>
                      <th className={thR}>Montant mensuel</th>
                      <th className={thR}>Coût du jour</th>
                      <th className={thR}>BS</th>
                      <th className={thR}>CA ramené</th>
                      <th className={thR}>Marge directe</th>
                      <th className="px-3 py-2 text-right font-medium">Marge nette</th>
                    </tr>
                  </thead>
                  <tbody className="tabular">
                    {d.inde_forfait.map((r) => (
                      <tr key={r.matricule} className="border-b last:border-0 hover:bg-muted/40" data-testid={`day-forfait-row-${r.matricule}`}>
                        <td className="px-3 py-2"><span className="inline-flex items-center gap-1.5"><RentabDot info={rmap[r.matricule]} /><span className="font-medium">{r.prenom} {r.nom}</span></span> <span className="text-[11px] text-muted-foreground">{r.matricule}</span></td>
                        <td className="px-2 py-2 text-right">{fmtEur(r.montant_mensuel, 2)}</td>
                        <td className="px-2 py-2 text-right accent-text font-semibold">{fmtEur(r.cout_jour, 2)}</td>
                        <td className="px-2 py-2 text-right">{fmtInt(r.bs_bruts)}</td>
                        <td className="px-2 py-2 text-right">{fmtEur(r.ca_attribue, 2)}</td>
                        <td className={`px-2 py-2 text-right font-medium ${mColor(r.marge_directe)}`}>{fmtEur(r.marge_directe, 2)}</td>
                        <td className={`px-3 py-2 text-right font-semibold ${mColor(r.marge_nette)}`}>{fmtEur(r.marge_nette, 2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 bg-muted/50 font-semibold" data-testid="day-forfait-total">
                      <td className="px-3 py-2">Total forfait ({d.inde_forfait.length})</td>
                      <td className="px-2 py-2 text-right text-muted-foreground">—</td>
                      <td className="px-2 py-2 text-right accent-text">{fmtEur(sumBy(d.inde_forfait, 'cout_jour'), 2)}</td>
                      <td className="px-2 py-2 text-right">{fmtInt(sumBy(d.inde_forfait, 'bs_bruts'))}</td>
                      <td className="px-2 py-2 text-right">{fmtEur(sumBy(d.inde_forfait, 'ca_attribue'), 2)}</td>
                      <td className={`px-2 py-2 text-right ${mColor(sumBy(d.inde_forfait, 'marge_directe'))}`}>{fmtEur(sumBy(d.inde_forfait, 'marge_directe'), 2)}</td>
                      <td className={`px-3 py-2 text-right ${mColor(sumBy(d.inde_forfait, 'marge_nette'))}`}>{fmtEur(sumBy(d.inde_forfait, 'marge_nette'), 2)}</td>
                    </tr>
                  </tfoot>
                </CardTable>
              </TableWrap>
            </div>
          )}

          {/* Indés au bulletin */}
          {d.inde_bulletin?.length > 0 && (
            <div className="space-y-2" data-testid="social-day-inde-bulletin">
              <SectionTitle icon={ReceiptText} title="Indés au bulletin — payés au BS" sub={`coût du jour = BS bruts × taux (mode ${d.mode_inde_totaux === 'reel' ? 'réel' : 'forecast'}) · CA ramené, marge du jour`} />
              <TableWrap>
                <CardTable className="w-full min-w-[820px] text-sm">
                  <thead>
                    <tr className="border-b bg-muted/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                      <th className="px-3 py-2 font-medium">Indépendant</th>
                      <th className={thR}>Taux / BS</th>
                      <th className={thR}>BS</th>
                      <th className={thR}>Coût du jour</th>
                      <th className={thR}>CA ramené</th>
                      <th className={thR}>Marge directe</th>
                      <th className="px-3 py-2 text-right font-medium">Marge nette</th>
                    </tr>
                  </thead>
                  <tbody className="tabular">
                    {d.inde_bulletin.map((r) => (
                      <tr key={r.matricule} className="border-b last:border-0 hover:bg-muted/40" data-testid={`day-bulletin-row-${r.matricule}`}>
                        <td className="px-3 py-2"><span className="inline-flex items-center gap-1.5"><RentabDot info={rmap[r.matricule]} /><span className="font-medium">{r.prenom} {r.nom}</span></span> <span className="text-[11px] text-muted-foreground">{r.matricule}</span></td>
                        <td className="px-2 py-2 text-right">{fmtEur(r.taux, 2)}</td>
                        <td className="px-2 py-2 text-right">{fmtInt(r.bs_bruts)}</td>
                        <td className="px-2 py-2 text-right accent-text font-semibold">{fmtEur(r.cout_jour, 2)}</td>
                        <td className="px-2 py-2 text-right">{fmtEur(r.ca_attribue, 2)}</td>
                        <td className={`px-2 py-2 text-right font-medium ${mColor(r.marge_directe)}`}>{fmtEur(r.marge_directe, 2)}</td>
                        <td className={`px-3 py-2 text-right font-semibold ${mColor(r.marge_nette)}`}>{fmtEur(r.marge_nette, 2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 bg-muted/50 font-semibold" data-testid="day-bulletin-total">
                      <td className="px-3 py-2">Total bulletin ({d.inde_bulletin.length})</td>
                      <td className="px-2 py-2 text-right text-muted-foreground">—</td>
                      <td className="px-2 py-2 text-right">{fmtInt(sumBy(d.inde_bulletin, 'bs_bruts'))}</td>
                      <td className="px-2 py-2 text-right accent-text">{fmtEur(sumBy(d.inde_bulletin, 'cout_jour'), 2)}</td>
                      <td className="px-2 py-2 text-right">{fmtEur(sumBy(d.inde_bulletin, 'ca_attribue'), 2)}</td>
                      <td className={`px-2 py-2 text-right ${mColor(sumBy(d.inde_bulletin, 'marge_directe'))}`}>{fmtEur(sumBy(d.inde_bulletin, 'marge_directe'), 2)}</td>
                      <td className={`px-3 py-2 text-right ${mColor(sumBy(d.inde_bulletin, 'marge_nette'))}`}>{fmtEur(sumBy(d.inde_bulletin, 'marge_nette'), 2)}</td>
                    </tr>
                  </tfoot>
                </CardTable>
              </TableWrap>
            </div>
          )}
        </>
      )}
    </div>
    </TooltipProvider>
  );
}

// --------------------------------------------------------------------------- //
//  Vue Collaborateur                                                           //
// --------------------------------------------------------------------------- //
// Explication du taux d'annulation : « X annulés sur Y traités ».
function annulSub(row) {
  if (!row || !row.nb_traite) return null;
  const a = row.nb_annul || 0;
  const t = row.nb_traite || 0;
  return `${fmtInt(a)} annulé${a > 1 ? 's' : ''} sur ${fmtInt(t)} traité${t > 1 ? 's' : ''}`;
}

const tauxAnnulCls = (t) => (t == null ? 'text-muted-foreground'
  : t <= 0.10 ? 'text-emerald-600' : t <= 0.25 ? 'text-amber-600' : 'text-rose-600');
const margeCls = (v) => (v >= 0 ? 'text-emerald-600' : 'text-rose-600');

const CANAL_LABELS = { porte_a_porte: 'Porte à Porte', sites_prives: 'Sites privés', rue: 'Rue', autre: 'Autre' };
// « Porte à Porte 45 · Sites privés 10 » à partir d'un mix {canal: nb_bs}.
function canalMixLabel(mix) {
  if (!mix) return null;
  const parts = Object.entries(mix)
    .filter(([, v]) => Number(v) > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([k, v]) => `${CANAL_LABELS[k] || k} ${fmtInt(v)}`);
  return parts.length ? parts.join(' · ') : null;
}

// Tuile de synthèse (header collaborateur) : libellé + valeur, ton neutre/amber/emerald.
function StatTile({ label, hint, value, sub, valueCls = 'text-foreground', tone = 'neutral', testid }) {
  const toneCls = tone === 'amber' ? 'border-amber-200 bg-amber-50/60'
    : tone === 'emerald' ? 'border-emerald-200 bg-emerald-50/60'
    : 'border-border bg-muted/30';
  return (
    <div className={`rounded-xl border px-3 py-2 ${toneCls}`}>
      <div className="flex items-baseline gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        <span className="truncate">{label}</span>
        {hint && <span className="opacity-70">{hint}</span>}
      </div>
      <div className={`font-display text-lg font-bold tabular ${valueCls}`} data-testid={testid}>{value}</div>
      {sub && <div className="text-[10px] leading-tight text-muted-foreground/80">{sub}</div>}
    </div>
  );
}

// Ligne « libellé (+ sous-libellé) ....... valeur » d'une carte mobile.
function CardLine({ label, value, sub, valueCls = '', strong = false, testid }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-border/50 py-1.5 last:border-0">
      <div className="min-w-0">
        <div className={`text-[13px] leading-tight ${strong ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>{label}</div>
        {sub && <div className="mt-0.5 text-[11px] leading-tight text-muted-foreground/80">{sub}</div>}
      </div>
      <div className={`shrink-0 tabular text-[15px] font-semibold ${valueCls}`} data-testid={testid}>{value}</div>
    </div>
  );
}

// Bloc « hypothèse » (prudent / optimiste) : CA net estimé, Marge (€), Rentabilité (%).
function ScenarioBlock({ tone, title, perBs, caNet, marge, rentabilite, rentable, testidMarge }) {
  const prudent = tone === 'prudent';
  return (
    <div className={`rounded-xl border p-3 ${prudent ? 'border-amber-200 bg-amber-50/60' : 'border-emerald-200 bg-emerald-50/60'}`}>
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <span className={`text-[12px] font-bold ${prudent ? 'text-amber-700' : 'text-emerald-700'}`}>{title}</span>
        <span className="text-[10px] text-muted-foreground">CA net ≈ {perBs} €/BS</span>
      </div>
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <span className="text-[12px] text-muted-foreground">CA net encaissé (estimé)</span>
          <span className="tabular text-[13px]">{fmtEur(caNet, 0)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[12px] text-muted-foreground">Marge (€)</span>
          <span className={`tabular text-[15px] font-bold ${margeCls(marge)}`} data-testid={testidMarge}>{fmtEur(marge, 0)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[12px] text-muted-foreground">Rentabilité (%)</span>
          <span className={`tabular text-[15px] font-bold ${rentable ? 'text-emerald-600' : 'text-rose-600'}`}>{fmtPct(rentabilite, 1)}</span>
        </div>
      </div>
    </div>
  );
}

// Carte mobile d'un mois (ou du total) — présentation claire et étiquetée.
function CollabMonthCard({ row, title, isTotal = false }) {
  const verdict = row.rentable
    ? <Badge variant="secondary" className="rounded-full bg-emerald-100 text-emerald-700">Rentable</Badge>
    : row.rentable_opt
      ? <Badge variant="secondary" className="rounded-full bg-amber-100 text-amber-700">Rentable si optimiste</Badge>
      : <Badge variant="secondary" className="rounded-full bg-rose-100 text-rose-700">Non rentable</Badge>;
  const isIndep = row.population === 'independant';
  const typeLabel = !isIndep ? null
    : row.cout_mode === 'forfait' ? 'Indépendant · forfait'
      : `Indépendant · au bulletin${row.cout_taux != null ? ` (${fmtEur(row.cout_taux, 0)}/BS)` : ''}`;
  const coutSub = !isIndep ? 'rémunération chargée (paie)'
    : row.cout_mode === 'forfait' ? 'forfait mensuel'
      : `${fmtInt(row.bs)} BS × ${fmtEur(row.cout_taux, 0)}`;
  return (
    <Card className={`p-4 ${isTotal ? 'border-2' : ''}`} style={isTotal ? { borderColor: 'var(--tenant-accent-300)', background: 'var(--tenant-accent-050)' } : undefined} data-testid={isTotal ? 'collab-card-total' : `collab-card-${row.ym}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-display text-base font-bold">{title}</h3>
          {typeLabel && <div className="text-[11px] font-medium text-muted-foreground">{typeLabel}</div>}
        </div>
        {verdict}
      </div>

      <div className="mt-3">
        <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Activité</div>
        {!isIndep && <CardLine label="Heures travaillées" value={fmtNum(row.heures, 2)} />}
        <CardLine label="Bulletins signés (BS)" value={fmtInt(row.bs)} />
        <CardLine label="Taux d'annulation" value={row.taux_annulation == null ? '—' : fmtPct(row.taux_annulation, 1)}
          sub={annulSub(row)} valueCls={tauxAnnulCls(row.taux_annulation)} testid={isTotal ? 'collab-card-total-taux' : `collab-card-taux-${row.ym}`} />
      </div>

      <div className="mt-3">
        <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Chiffre d'affaires &amp; coût</div>
        <CardLine label="CA généré (brut)" sub={`${fmtInt(row.bs)} BS × ${fmtEur(row.ca_par_bs_eff, 2)}/BS`} value={fmtEur(row.ca_brut, 0)} strong
          testid={isTotal ? 'collab-card-total-ca' : `collab-card-ca-${row.ym}`} />
        <CardLine label="Coût" sub={coutSub} value={fmtEur(row.cout, 0)} valueCls="accent-text" strong />
        {canalMixLabel(row.canal_mix) && (
          <div className="flex items-start gap-1.5 pt-1.5 text-[11px] text-muted-foreground" data-testid={isTotal ? 'collab-card-total-canal' : `collab-card-canal-${row.ym}`}>
            <Layers className="mt-0.5 h-3 w-3 shrink-0" />
            <span>Canal : {canalMixLabel(row.canal_mix)}{row.ca_par_bs_eff > 195 ? ' · porte-à-porte survalorisé' : ''}</span>
          </div>
        )}
      </div>

      <div className="mt-3">
        <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Rentabilité — selon le CA réellement encaissé</div>
        <div className="mt-2 space-y-2">
          <ScenarioBlock tone="prudent" title="Scénario prudent" perBs="118"
            caNet={row.ca_net} marge={row.marge} rentabilite={row.rentabilite} rentable={row.rentable}
            testidMarge={isTotal ? 'collab-card-total-marge' : `collab-card-marge-${row.ym}`} />
          <ScenarioBlock tone="optimiste" title="Scénario optimiste" perBs="150"
            caNet={row.ca_net_opt} marge={row.marge_opt} rentabilite={row.rentabilite_opt} rentable={row.rentable_opt}
            testidMarge={isTotal ? 'collab-card-total-marge-opt' : `collab-card-marge-opt-${row.ym}`} />
        </div>
      </div>
    </Card>
  );
}


// Encart admin — collaborateurs non rentables plusieurs mois consécutifs.
// Rouge = non rentable même en optimiste (critique) · Orange = non rentable
// en prudent seulement (à surveiller). Cliquable -> sélectionne le collaborateur.
function AlertesCard({ data, loading, onSelect, selectedMat }) {
  const d = data;
  if (loading && !d) return <Skeleton className="h-24 rounded-2xl" data-testid="collab-alertes-loading" />;
  if (!d) return null;
  if (!d.count) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-sm font-medium text-emerald-700" data-testid="collab-alertes-empty">
        <ShieldCheck className="h-4 w-4 shrink-0" />
        Aucun collaborateur non rentable {d.min_streak} mois consécutifs. Rien à signaler.
      </div>
    );
  }
  return (
    <Card className="p-4" data-testid="collab-alertes">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0 text-rose-600" />
          <h3 className="font-display text-sm font-bold">Collaborateurs à surveiller</h3>
          <Badge variant="secondary" className="rounded-full" data-testid="collab-alertes-count">{d.count}</Badge>
        </div>
        <div className="flex items-center gap-1.5 text-[11px]">
          {d.nb_critiques > 0 && <span className="rounded-full bg-rose-100 px-2 py-0.5 font-semibold text-rose-700">{d.nb_critiques} critique{d.nb_critiques > 1 ? 's' : ''}</span>}
          {d.nb_attention > 0 && <span className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-700">{d.nb_attention} à surveiller</span>}
        </div>
      </div>
      <p className="mt-1 flex items-start gap-1.5 text-[11px] leading-snug text-muted-foreground">
        <Lock className="mt-0.5 h-3 w-3 shrink-0" />
        <span>
          Non rentables ≥ {d.min_streak} mois consécutifs (visible admin uniquement).{' '}
          <span className="font-semibold text-rose-700">Critique</span> = non rentable même en optimiste ·{' '}
          <span className="font-semibold text-amber-700">À surveiller</span> = non rentable en prudent seulement.
        </span>
      </p>
      <div className="mt-3 max-h-72 space-y-1.5 overflow-y-auto pr-1">
        {d.alertes.map((a) => {
          const crit = a.severite === 'critique';
          const active = selectedMat === a.matricule;
          return (
            <button key={a.matricule} type="button" onClick={() => onSelect(a.matricule)}
              className={`flex w-full items-center gap-3 rounded-xl border px-3 py-2 text-left transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 ${crit ? 'border-rose-200 bg-rose-50/40' : 'border-amber-200 bg-amber-50/40'} ${active ? 'ring-2' : ''}`}
              style={active ? { '--tw-ring-color': 'var(--tenant-accent-400)' } : undefined}
              data-testid={`collab-alerte-${a.matricule}`}>
              <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${crit ? 'bg-rose-500' : 'bg-amber-500'}`} aria-hidden />
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                  <span className="truncate text-sm font-semibold">{a.prenom} {a.nom}</span>
                  <span className="text-[10px] text-muted-foreground">{a.matricule}</span>
                  <Badge variant="outline" className="rounded-full px-1.5 py-0 text-[9px] font-medium">{a.type === 'independant' ? 'Indé' : 'Salarié'}</Badge>
                </span>
                <span className="block text-[11px] text-muted-foreground">
                  {a.mois_consecutifs} mois consécutifs · depuis {labelYm(a.depuis)}
                </span>
              </span>
              <span className="shrink-0 text-right">
                <span className={`block text-[13px] font-bold ${crit ? 'text-rose-600' : 'text-amber-600'}`}>{crit ? 'Critique' : 'À surveiller'}</span>
                <span className="block text-[11px] text-muted-foreground">Marge {fmtEur(a.perte_prudent, 0)}</span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            </button>
          );
        })}
      </div>
    </Card>
  );
}



// Petite ligne « libellé .... valeur » pour les tuiles canal.
function CanalRow({ l, v, cls = '' }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{l}</span>
      <span className={`tabular ${cls}`}>{v}</span>
    </div>
  );
}

function CanalTile({ c }) {
  const good = c.rentable;
  return (
    <div className={`rounded-xl border p-3 ${good ? 'border-emerald-200 bg-emerald-50/40' : 'border-rose-200 bg-rose-50/40'}`} data-testid={`collab-canal-${c.canal}`}>
      <div className="flex items-center justify-between gap-1">
        <span className="text-[12px] font-bold">{CANAL_LABELS[c.canal] || c.canal}</span>
        <Badge variant="secondary" className={`rounded-full text-[9px] ${good ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>{good ? 'Rentable' : 'Non rentable'}</Badge>
      </div>
      <div className="mt-1.5 space-y-0.5 text-[12px]">
        <CanalRow l="Bulletins (BS)" v={fmtInt(c.bs)} />
        <CanalRow l="CA / BS" v={fmtEur(c.ca_par_bs, 2)} />
        <CanalRow l="CA net" v={fmtEur(c.ca_net, 0)} />
        <CanalRow l="Coût (prorata)" v={fmtEur(c.cout, 0)} cls="accent-text" />
        <CanalRow l="Marge" v={fmtEur(c.marge, 0)} cls={margeCls(c.marge) + ' font-bold'} />
        <div className="flex items-center justify-between border-t pt-1">
          <span className="text-muted-foreground">Rentabilité</span>
          <span className={`text-[15px] font-bold ${good ? 'text-emerald-600' : 'text-rose-600'}`}>{fmtPct(c.rentabilite, 1)}</span>
        </div>
      </div>
    </div>
  );
}

// Encart admin — rentabilité agrégée par canal (porte-à-porte vs sites privés vs rue).
function CanauxCard({ canaux, total, loading }) {
  if (loading && !canaux) return <Skeleton className="h-40 rounded-2xl" data-testid="collab-canaux-loading" />;
  if (!canaux) return null;
  const order = ['porte_a_porte', 'sites_prives', 'rue', 'autre'];
  const rows = order.map((k) => canaux[k]).filter((c) => c && c.bs > 0);
  if (!rows.length) return null;
  return (
    <Card className="p-4" data-testid="collab-canaux">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4 shrink-0 accent-text" />
          <h3 className="font-display text-sm font-bold">Rentabilité par canal</h3>
        </div>
        {total?.bs > 0 && (
          <span className={`text-[13px] font-bold ${total.rentable ? 'text-emerald-600' : 'text-rose-600'}`}>
            Global {fmtPct(total.rentabilite, 1)} · Marge {fmtEur(total.marge, 0)}
          </span>
        )}
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground">Coût réparti au prorata des BS · CA/BS : porte-à-porte survalorisé. Fenêtre récente, visible admin uniquement.</p>
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {rows.map((c) => <CanalTile key={c.canal} c={c} />)}
      </div>
    </Card>
  );
}

// Simulateur « et si ? » — rejoue la rentabilité des mois passés avec d'autres prix/BS.
function SimulateurCard({ matricule, basePrices, current }) {
  const [pap, setPap] = useState(basePrices?.pap ?? 202.5);
  const [sp, setSp] = useState(basePrices?.sp ?? 195);
  const [rue, setRue] = useState(basePrices?.rue ?? 195);
  const [active, setActive] = useState(false);
  useEffect(() => {
    setPap(basePrices?.pap ?? 202.5); setSp(basePrices?.sp ?? 195); setRue(basePrices?.rue ?? 195);
    setActive(false);
  }, [matricule, basePrices?.pap, basePrices?.sp, basePrices?.rue]);
  const q = useGet(`/social/collaborateur/${matricule}`,
    { sim_pap: pap, sim_sp: sp, sim_rue: rue }, active && !!matricule);
  const sim = q.data;
  const curTot = current?.totals;
  const simTot = sim?.totals;
  const deltaRent = (simTot && curTot) ? (simTot.rentabilite - curTot.rentabilite) : null;
  const simByYm = {};
  (sim?.history || []).forEach((h) => { simByYm[h.ym] = h; });
  return (
    <Card className="p-4" data-testid="collab-simulateur">
      <div className="flex items-center gap-2">
        <FlaskConical className="h-4 w-4 shrink-0 accent-text" />
        <h3 className="font-display text-sm font-bold">Simulateur de prix « et si ? »</h3>
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground">Ajustez les prix/BS par canal et voyez la rentabilité recalculée sur les mois passés (sans rien enregistrer).</p>
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
        {[['Porte à Porte', pap, setPap], ['Sites privés', sp, setSp], ['Rue', rue, setRue]].map(([lbl, val, set]) => (
          <div key={lbl}>
            <Label className="text-xs">{lbl} (€/BS)</Label>
            <Input type="number" step="0.5" value={val}
              onChange={(e) => { set(e.target.value === '' ? '' : parseFloat(e.target.value)); }}
              className="mt-1 rounded-xl tabular" data-testid={`sim-price-${lbl === 'Porte à Porte' ? 'pap' : lbl === 'Sites privés' ? 'sp' : 'rue'}`} />
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={() => setActive(true)} className="rounded-xl" style={accentStyle} data-testid="sim-run">
          <Play className="mr-1.5 h-3.5 w-3.5" /> Simuler
        </Button>
        <Button size="sm" variant="outline" className="rounded-xl"
          onClick={() => { setPap(basePrices?.pap ?? 202.5); setSp(basePrices?.sp ?? 195); setRue(basePrices?.rue ?? 195); setActive(false); }}
          data-testid="sim-reset">Réinitialiser</Button>
        {q.isFetching && <span className="text-[11px] text-muted-foreground">Calcul…</span>}
      </div>

      {active && simTot && curTot && (
        <div className="mt-3" data-testid="sim-results">
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border bg-muted/30 px-3 py-2">
            <span className="text-sm font-semibold">Rentabilité globale</span>
            <span className="flex items-center gap-2 text-sm">
              <span className={curTot.rentable ? 'text-emerald-600' : 'text-rose-600'}>{fmtPct(curTot.rentabilite, 1)}</span>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
              <span className={`font-bold ${simTot.rentable ? 'text-emerald-600' : 'text-rose-600'}`} data-testid="sim-total-rent">{fmtPct(simTot.rentabilite, 1)}</span>
              {deltaRent != null && (
                <Badge variant="secondary" className={`rounded-full ${deltaRent >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                  {deltaRent >= 0 ? '+' : ''}{fmtPct(deltaRent, 1)}
                </Badge>
              )}
            </span>
          </div>
          <div className="mt-2 max-h-60 space-y-1 overflow-y-auto pr-1">
            {(current?.history || []).map((h) => {
              const s = simByYm[h.ym];
              if (!s) return null;
              const dm = s.marge - h.marge;
              return (
                <div key={h.ym} className="flex items-center justify-between gap-2 border-b border-border/50 py-1 text-[12px] last:border-0" data-testid={`sim-row-${h.ym}`}>
                  <span className="font-medium">{labelYm(h.ym)}</span>
                  <span className="flex items-center gap-1.5">
                    <span className="text-muted-foreground">{fmtPct(h.rentabilite, 1)}</span>
                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                    <span className={`font-semibold ${s.rentable ? 'text-emerald-600' : 'text-rose-600'}`}>{fmtPct(s.rentabilite, 1)}</span>
                    <span className={`ml-1 text-[11px] ${dm >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>({dm >= 0 ? '+' : ''}{fmtEur(dm, 0)})</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
}



// Optimiseur de prix idéal par canal — calcule le prix/BS PLANCHER (point mort) et
// le prix/BS CIBLE (marge visée) MALGRÉ les pertes (annulation appel, jamais prélevé,
// remboursement). Recalcul 100 % client à partir des agrégats (bs, coût) — relation
// linéaire CA net = BS × net_ratio × prix. Admin only (données sous /social).
const OPTIM_STATUS = {
  atteint: { cls: 'border-emerald-200 bg-emerald-50/40', badge: 'bg-emerald-100 text-emerald-700', label: 'Prix optimal atteint', dot: 'bg-emerald-500' },
  sous_objectif: { cls: 'border-amber-200 bg-amber-50/40', badge: 'bg-amber-100 text-amber-700', label: 'À renégocier', dot: 'bg-amber-500' },
  perte: { cls: 'border-rose-200 bg-rose-50/40', badge: 'bg-rose-100 text-rose-700', label: 'Sous le point mort', dot: 'bg-rose-500' },
  na: { cls: 'border-border bg-muted/30', badge: 'bg-muted text-muted-foreground', label: '—', dot: 'bg-muted-foreground' },
};

// Indice de fiabilité du prix idéal selon le volume de bulletins agrégé.
const FIABILITE = {
  fiable: { label: 'Fiable', cls: 'text-emerald-600', dot: 'bg-emerald-500' },
  modere: { label: 'Fiabilité modérée', cls: 'text-amber-600', dot: 'bg-amber-500' },
  faible: { label: 'Faible volume', cls: 'text-rose-600', dot: 'bg-rose-500' },
};

function OptimTile({ c, marge, vue, nr }) {
  const st = OPTIM_STATUS[c.statut] || OPTIM_STATUS.na;
  const livre = vue === 'livre';
  const f = livre ? nr : 1;              // facteur d'affichage principal
  const g = livre ? 1 : nr;              // facteur du sous-libellé (l'autre convention)
  const unite = livre ? '(livré)' : '(garanti)';
  const sousLbl = livre ? 'avec garantie' : 'sans garantie';
  const eur = (v) => (v == null ? '—' : fmtEur(v * f, 2));
  return (
    <div className={`rounded-xl border p-3 ${st.cls}`} data-testid={`optim-canal-${c.key}`}>
      <div className="flex items-center justify-between gap-1">
        <span className="text-[12px] font-bold">{CANAL_LABELS[c.key] || c.key}</span>
        <Badge variant="secondary" className={`rounded-full text-[9px] ${st.badge}`} data-testid={`optim-status-${c.key}`}>{st.label}</Badge>
      </div>
      <div className="mt-1.5 space-y-0.5 text-[12px]">
        <CanalRow l="Bulletins (BS)" v={fmtInt(c.bs)} />
        <CanalRow l={`Prix actuel ${unite}`} v={eur(c.actuel)} />
        <CanalRow l={`Point mort ${unite}`} v={eur(c.plancher)} cls="text-muted-foreground" />
        <div className="flex items-center justify-between border-t pt-1">
          <span className="text-muted-foreground">{c.atteint ? 'Prix optimal (garder)' : `Prix recommandé (${Math.round(marge * 100)}%)`}</span>
          <span className="tabular text-[15px] font-bold accent-text" data-testid={`optim-reco-${c.key}`}>{eur(c.recommande)}</span>
        </div>
        <div className="flex items-center justify-end pt-0.5 text-[10px] text-muted-foreground"
          title="Bascule entre prix « avec garantie » (net réel, par don validé) et « sans garantie » (par bulletin livré) = prix garanti × taux d'encaissement net.">
          {c.recommande == null ? '' : <>≈ {fmtEur(c.recommande * g, 2)} {sousLbl}</>}
        </div>
        <div className="flex items-center justify-between border-t pt-1">
          <span className="text-muted-foreground">{c.atteint ? 'Marge de sécurité / BS' : 'À renégocier / BS'}</span>
          <span className={`tabular font-semibold ${c.footerVal == null ? 'text-muted-foreground' : c.atteint ? 'text-emerald-600' : c.statut === 'perte' ? 'text-rose-600' : 'text-amber-600'}`} data-testid={`optim-ecart-${c.key}`}>
            {c.footerVal == null ? '—' : `+${fmtEur(c.footerVal * f, 2)}`}
          </span>
        </div>
        {c.statut === 'perte' && (
          <p className="pt-0.5 text-[10px] leading-snug text-rose-600">
            Coût/bulletin élevé : difficile à rattraper par le prix seul — revoir la rémunération ou le rendement.
          </p>
        )}
        {c.fiabilite && (
          <div className="mt-1 flex items-center gap-1.5 border-t pt-1 text-[10px]"
            title={`Fiabilité basée sur le volume (${fmtInt(c.bs)} bulletins). Un faible volume rend le prix idéal plus sensible à un mois atypique.`}>
            <span className={`h-1.5 w-1.5 rounded-full ${(FIABILITE[c.fiabilite] || {}).dot || 'bg-muted-foreground'}`} aria-hidden />
            <span className="text-muted-foreground">Fiabilité :</span>
            <span className={`font-semibold ${(FIABILITE[c.fiabilite] || {}).cls || ''}`} data-testid={`optim-fiabilite-${c.key}`}>
              {(FIABILITE[c.fiabilite] || {}).label || '—'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function OptimiseurCard({ optim, loading }) {
  const [margePct, setMargePct] = useState(30);
  const [scenario, setScenario] = useState('prudent'); // 'prudent' | 'optimiste'
  const [vue, setVue] = useState('garantie');          // 'garantie' | 'livre'
  const [rates, setRates] = useState([]);              // taux de pertes éditables (fractions)
  const [defaults, setDefaults] = useState([]);
  useEffect(() => {
    if (!optim) return;
    const r = (optim.taux_pertes || []).map((t) => t.taux);
    setRates(r);
    setDefaults(r);
    setMargePct(Math.round((optim.marge_cible ?? 0.3) * 100));
  }, [optim]);

  if (loading && !optim) return <Skeleton className="h-56 rounded-2xl" data-testid="collab-optimiseur-loading" />;
  if (!optim) return null;

  const labels = (optim.taux_pertes || []).map((t) => t.label);
  const order = ['porte_a_porte', 'sites_prives', 'rue', 'autre'];
  const canaux = optim.canaux || {};
  const baseRows = order.map((k) => ({ key: k, ...(canaux[k] || {}) })).filter((c) => c && c.bs > 0);
  if (!baseRows.length) return null;

  const marge = Math.min(0.95, Math.max(0, margePct / 100));
  // Ratios d'encaissement recalculés depuis les taux édités :
  //  • Prudent   = produit de (1 - taux) sur TOUTES les pertes
  //  • Optimiste = uniquement la dernière (attrition/remboursement long terme)
  const nrPrudent = rates.reduce((acc, r) => acc * (1 - (Number(r) || 0)), 1);
  const lastR = rates.length ? Number(rates[rates.length - 1]) || 0 : 0;
  const nrOpt = 1 - lastR;
  const nr = scenario === 'prudent' ? nrPrudent : nrOpt;

  const rows = baseRows.map((c) => {
    const bs = c.bs || 0;
    const cout = c.cout || 0;
    const actuel = c.prix_actuel || 0;
    const plancher = bs > 0 && nr > 0 ? cout / (bs * nr) : null;
    const cible = bs > 0 && nr > 0 && marge < 1 ? cout / (bs * nr * (1 - marge)) : null;
    let statut = 'na';
    if (cible != null && plancher != null) {
      statut = actuel >= cible ? 'atteint' : actuel >= plancher ? 'sous_objectif' : 'perte';
    }
    const atteint = statut === 'atteint';
    // Prix recommandé : plafonné au prix actuel (on ne recommande jamais de baisser).
    const recommande = cible != null ? Math.max(cible, actuel) : null;
    // Équivalent « sans garantie » (par bulletin livré) = prix garanti × taux d'encaissement.
    const recoSansGarantie = recommande != null ? recommande * nr : null;
    // Écart : « à renégocier » (sous objectif) ou « marge de sécurité » (déjà atteint).
    const footerVal = cible == null ? null : (atteint ? (actuel - cible) : (cible - actuel));
    return { key: c.key, bs, cout, actuel, plancher, cible, recommande, recoSansGarantie, statut, atteint, footerVal, fiabilite: c.fiabilite };
  });

  const copyRecos = async () => {
    const hyp = scenario === 'prudent' ? 'prudente' : 'optimiste';
    const livre = vue === 'livre';
    const f = livre ? nr : 1;
    const vueLbl = livre ? 'SANS GARANTIE (par bulletin livré)' : 'AVEC GARANTIE (net réel)';
    const lines = [
      `Prix recommandés par canal — ${vueLbl} · marge nette ${margePct}% · hypothèse ${hyp} · net encaissé ${fmtPct(nr, 0)}`,
      '',
    ];
    rows.forEach((c) => {
      if (c.recommande == null) return;
      const nom = CANAL_LABELS[c.key] || c.key;
      const corps = c.atteint
        ? `prix optimal atteint — garder ${fmtEur(c.actuel * f, 2)}/BS (marge de sécurité +${fmtEur(c.footerVal * f, 2)})`
        : `passer à ${fmtEur(c.recommande * f, 2)}/BS (actuel ${fmtEur(c.actuel * f, 2)}, +${fmtEur(c.footerVal * f, 2)} à renégocier)`;
      const flag = c.statut === 'perte' ? ' [sous le point mort]' : '';
      lines.push(`• ${nom} : ${corps}${flag}`);
    });
    lines.push('', '— Généré depuis SHARITI · Suivi Social');
    const text = lines.join('\n');
    const fallbackCopy = () => {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        return ok;
      } catch (e) {
        return false;
      }
    };
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else if (!fallbackCopy()) {
        throw new Error('no-clipboard');
      }
      toast.success('Recommandations copiées', { description: 'Collez-les dans votre email de négociation.' });
    } catch (e) {
      if (fallbackCopy()) {
        toast.success('Recommandations copiées', { description: 'Collez-les dans votre email de négociation.' });
      } else {
        toast.error('Copie impossible', { description: "Autorisez l'accès au presse-papiers du navigateur." });
      }
    }
  };

  const setRate = (i, v) => {
    const pct = v === '' ? 0 : parseFloat(v);
    setRates((prev) => prev.map((r, idx) => (idx === i ? (isNaN(pct) ? 0 : pct / 100) : r)));
  };
  const isCustom = JSON.stringify(rates) !== JSON.stringify(defaults) || margePct !== Math.round((optim.marge_cible ?? 0.3) * 100);

  const segBtn = (val, label, tone) => (
    <button
      type="button"
      onClick={() => setScenario(val)}
      className={`flex-1 rounded-lg px-2.5 py-1 text-[12px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 ${scenario === val ? (tone === 'amber' ? 'bg-amber-500 text-white shadow-sm' : 'bg-emerald-600 text-white shadow-sm') : 'text-muted-foreground hover:bg-background'}`}
      data-testid={`optim-scenario-${val}`}
      aria-pressed={scenario === val}>
      {label}
    </button>
  );

  return (
    <Card className="p-4" data-testid="collab-optimiseur">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4 shrink-0 accent-text" />
          <h3 className="font-display text-sm font-bold">Optimiseur de prix idéal par canal</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold tabular" data-testid="optim-net-ratio">
            Net encaissé : {fmtPct(nr, 0)}
          </span>
          <Button size="sm" variant="outline" className="h-7 rounded-lg text-[11px]" onClick={copyRecos} data-testid="optim-copy">
            <Copy className="mr-1 h-3 w-3" /> Copier
          </Button>
        </div>
      </div>
      <p className="mt-1 flex items-start gap-1.5 text-[11px] leading-snug text-muted-foreground">
        <Lock className="mt-0.5 h-3 w-3 shrink-0" />
        <span>
          Prix/BS à viser pour rester rentable <strong className="text-foreground">malgré les pertes</strong> (annulation à l'appel, jamais prélevé, remboursement). Tous les prix sont <strong className="text-foreground">avec garantie</strong> (net réel, par don validé — comme ce que vous facturez) ; l'équivalent <em>sans garantie</em> (par bulletin livré) est indiqué en gris.{' '}
          <strong className="text-foreground">Point mort</strong> = marge nulle · <strong className="text-foreground">Prix recommandé</strong> = marge visée, jamais sous le prix actuel. Visible admin uniquement.
        </span>
      </p>

      {/* Vue des prix : avec garantie (net réel) vs sans garantie (bulletin livré) */}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-semibold text-muted-foreground">Vue des prix :</span>
        <div className="flex gap-1 rounded-xl border bg-muted p-1">
          <button type="button" onClick={() => setVue('garantie')}
            className={`rounded-lg px-3 py-1 text-[12px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 ${vue === 'garantie' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-background'}`}
            data-testid="optim-vue-garantie" aria-pressed={vue === 'garantie'}>Avec garantie</button>
          <button type="button" onClick={() => setVue('livre')}
            className={`rounded-lg px-3 py-1 text-[12px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 ${vue === 'livre' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-background'}`}
            data-testid="optim-vue-livre" aria-pressed={vue === 'livre'}>Sans garantie</button>
        </div>
        <span className="text-[10px] text-muted-foreground">
          {vue === 'garantie' ? 'Net réel encaissé (par don validé — ce que vous facturez).' : 'Par bulletin livré (avant attrition).'}
        </span>
      </div>

      {/* Contrôles : marge cible + scénario */}
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="rounded-xl border bg-muted/30 p-3">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-semibold">Marge nette cible</Label>
            <span className="tabular text-sm font-bold accent-text" data-testid="optim-marge-value">{margePct}%</span>
          </div>
          <Slider value={[margePct]} min={0} max={60} step={1}
            onValueChange={(v) => setMargePct(v[0])} className="mt-3" data-testid="optim-marge-slider" />
          <p className="mt-1.5 text-[10px] text-muted-foreground">Résultat net visé (part du CA réellement encaissé, avec garantie).</p>
        </div>
        <div className="rounded-xl border bg-muted/30 p-3">
          <Label className="text-xs font-semibold">Hypothèse d'encaissement</Label>
          <div className="mt-2 flex gap-1 rounded-xl border bg-muted p-1">
            {segBtn('prudent', 'Prudent', 'amber')}
            {segBtn('optimiste', 'Optimiste', 'emerald')}
          </div>
          <p className="mt-1.5 text-[10px] text-muted-foreground">
            {scenario === 'prudent'
              ? 'Toutes les pertes appliquées (le plus réaliste).'
              : 'Seule l\'attrition long terme (peu d\'annulations).'}
          </p>
        </div>
      </div>

      {/* Éditeur des taux de pertes */}
      <div className="mt-3 rounded-xl border p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs font-semibold">Taux de pertes (hypothèses)</span>
          {isCustom && (
            <Button size="sm" variant="outline" className="h-7 rounded-lg text-[11px]"
              onClick={() => { setRates(defaults); setMargePct(Math.round((optim.marge_cible ?? 0.3) * 100)); }}
              data-testid="optim-reset">
              <RefreshCw className="mr-1 h-3 w-3" /> Réinitialiser
            </Button>
          )}
        </div>
        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
          {rates.map((r, i) => {
            const dim = scenario === 'optimiste' && i < rates.length - 1;
            return (
              <div key={i} className={dim ? 'opacity-40' : ''}>
                <Label className="text-[11px]">{labels[i] || `Perte ${i + 1}`} (%)</Label>
                <Input type="number" step="0.5" min="0" max="95"
                  value={Number((r * 100).toFixed(2))}
                  onChange={(e) => setRate(i, e.target.value)}
                  disabled={dim}
                  className="mt-1 rounded-xl tabular" data-testid={`optim-perte-${i}`} />
              </div>
            );
          })}
        </div>
        {scenario === 'optimiste' && (
          <p className="mt-1.5 text-[10px] text-muted-foreground">En optimiste, seule « {labels[labels.length - 1] || 'attrition'} » est prise en compte.</p>
        )}
      </div>

      {/* Résultats par canal */}
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {rows.map((c) => <OptimTile key={c.key} c={c} marge={marge} vue={vue} nr={nr} />)}
      </div>
    </Card>
  );
}


// Simulateur global « et si ? » — rejoue la rentabilité de TOUTE l'entreprise sur la
// fenêtre récente avec d'autres prix/BS par canal. Recalcul 100 % client : le CA net
// est LINÉAIRE en prix (CA_net_sim = CA_net × prix_sim / prix_actuel), coûts inchangés.
function GlobalSimulateurCard({ canaux, total, mensuel, loading }) {
  const [scenario, setScenario] = useState('prudent');
  const [showMonthly, setShowMonthly] = useState(false);
  const [prices, setPrices] = useState(null);
  const base = {
    porte_a_porte: canaux?.porte_a_porte?.ca_par_bs || 202.5,
    sites_prives: canaux?.sites_prives?.ca_par_bs || 195,
    rue: canaux?.rue?.ca_par_bs || 195,
  };
  useEffect(() => {
    if (!canaux) return;
    setPrices({
      porte_a_porte: canaux.porte_a_porte?.ca_par_bs || 202.5,
      sites_prives: canaux.sites_prives?.ca_par_bs || 195,
      rue: canaux.rue?.ca_par_bs || 195,
    });
  }, [canaux?.porte_a_porte?.ca_par_bs, canaux?.sites_prives?.ca_par_bs, canaux?.rue?.ca_par_bs]);

  if (loading && !canaux) return <Skeleton className="h-40 rounded-2xl" data-testid="collab-globalsim-loading" />;
  if (!canaux || !prices) return null;

  const order = ['porte_a_porte', 'sites_prives', 'rue', 'autre'];
  const withData = order.filter((k) => canaux[k] && canaux[k].bs > 0);
  if (!withData.length) return null;

  let curNet = 0, simNet = 0, cout = 0;
  const perCanal = withData.map((k) => {
    const c = canaux[k];
    const caNetCur = scenario === 'prudent' ? (c.ca_net || 0) : (c.ca_net_opt || 0);
    const prixCur = c.ca_par_bs || 0;
    const simPrice = k === 'autre' ? prixCur : (Number(prices[k]) || 0);
    const caNetSim = prixCur > 0 ? caNetCur * (simPrice / prixCur) : caNetCur;
    const cCout = c.cout || 0;
    curNet += caNetCur; simNet += caNetSim; cout += cCout;
    return { key: k, prixCur, simPrice, margeCur: caNetCur - cCout, margeSim: caNetSim - cCout };
  });
  const margeCur = curNet - cout, margeSim = simNet - cout;
  const rentCur = curNet ? margeCur / curNet : 0;
  const rentSim = simNet ? margeSim / simNet : 0;
  const deltaRent = rentSim - rentCur;
  const deltaMarge = margeSim - margeCur;
  const isCustom = JSON.stringify(prices) !== JSON.stringify(base);

  // Impact mois par mois (recalcul linéaire par mois, mêmes prix simulés).
  const monthly = (mensuel || []).map((mo) => {
    let mc = 0, ms = 0, mcout = 0;
    Object.entries(mo.canaux || {}).forEach(([k, c]) => {
      const caNetCur = scenario === 'prudent' ? (c.ca_net || 0) : (c.ca_net_opt || 0);
      const prixCur = c.ca_par_bs || 0;
      const simPrice = k === 'autre' ? prixCur : (Number(prices[k]) || 0);
      const caNetSim = prixCur > 0 ? caNetCur * (simPrice / prixCur) : caNetCur;
      mc += caNetCur; ms += caNetSim; mcout += c.cout || 0;
    });
    return {
      mois: mo.mois,
      rentCur: mc ? (mc - mcout) / mc : 0,
      rentSim: ms ? (ms - mcout) / ms : 0,
      margeSim: ms - mcout,
      deltaMarge: (ms - mcout) - (mc - mcout),
    };
  });

  const setPrice = (k, v) => setPrices((p) => ({ ...p, [k]: v === '' ? '' : parseFloat(v) }));
  const seg = (val, label, tone) => (
    <button type="button" onClick={() => setScenario(val)}
      className={`flex-1 rounded-lg px-2.5 py-1 text-[12px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 ${scenario === val ? (tone === 'amber' ? 'bg-amber-500 text-white shadow-sm' : 'bg-emerald-600 text-white shadow-sm') : 'text-muted-foreground hover:bg-background'}`}
      data-testid={`globalsim-scenario-${val}`} aria-pressed={scenario === val}>{label}</button>
  );

  return (
    <Card className="p-4" data-testid="collab-globalsim">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <FlaskConical className="h-4 w-4 shrink-0 accent-text" />
          <h3 className="font-display text-sm font-bold">Simulateur global de prix « et si ? »</h3>
        </div>
        <div className="flex w-full gap-1 rounded-xl border bg-muted p-1 sm:w-auto">
          {seg('prudent', 'Prudent', 'amber')}
          {seg('optimiste', 'Optimiste', 'emerald')}
        </div>
      </div>
      <p className="mt-1 flex items-start gap-1.5 text-[11px] leading-snug text-muted-foreground">
        <Lock className="mt-0.5 h-3 w-3 shrink-0" />
        <span>Rejoue la rentabilité de <strong className="text-foreground">toute l'entreprise</strong> (fenêtre récente) avec d'autres prix/BS <strong className="text-foreground">avec garantie</strong> (net réel), sans rien enregistrer. Estimation (coûts inchangés). Visible admin uniquement.</span>
      </p>

      {/* Saisie des prix par canal */}
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
        {['porte_a_porte', 'sites_prives', 'rue'].map((k) => (
          <div key={k}>
            <Label className="text-xs">{CANAL_LABELS[k]} (€/BS, garanti)</Label>
            <Input type="number" step="0.5" value={prices[k]}
              onChange={(e) => setPrice(k, e.target.value)}
              className="mt-1 rounded-xl tabular" data-testid={`globalsim-price-${k}`} />
          </div>
        ))}
      </div>
      {isCustom && (
        <div className="mt-2">
          <Button size="sm" variant="outline" className="h-7 rounded-lg text-[11px]" onClick={() => setPrices(base)} data-testid="globalsim-reset">
            <RefreshCw className="mr-1 h-3 w-3" /> Réinitialiser
          </Button>
        </div>
      )}

      {/* Résultat global */}
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div className="rounded-xl border bg-muted/30 px-3 py-2">
          <div className="text-[11px] text-muted-foreground">Rentabilité globale</div>
          <div className="mt-0.5 flex items-center gap-2 text-sm">
            <span className={rentCur >= 0 ? 'text-emerald-600' : 'text-rose-600'}>{fmtPct(rentCur, 1)}</span>
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
            <span className={`font-bold ${rentSim >= 0 ? 'text-emerald-600' : 'text-rose-600'}`} data-testid="globalsim-rent-sim">{fmtPct(rentSim, 1)}</span>
            <Badge variant="secondary" className={`rounded-full ${deltaRent >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
              {deltaRent >= 0 ? '+' : ''}{fmtPct(deltaRent, 1)}
            </Badge>
          </div>
        </div>
        <div className="rounded-xl border bg-muted/30 px-3 py-2">
          <div className="text-[11px] text-muted-foreground">Marge globale</div>
          <div className="mt-0.5 flex items-center gap-2 text-sm">
            <span className={margeCls(margeCur)}>{fmtEur(margeCur, 0)}</span>
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
            <span className={`font-bold ${margeCls(margeSim)}`} data-testid="globalsim-marge-sim">{fmtEur(margeSim, 0)}</span>
            <Badge variant="secondary" className={`rounded-full ${deltaMarge >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
              {deltaMarge >= 0 ? '+' : ''}{fmtEur(deltaMarge, 0)}
            </Badge>
          </div>
        </div>
      </div>

      {/* Détail par canal */}
      <div className="mt-2 space-y-1">
        {perCanal.map((c) => {
          const dm = c.margeSim - c.margeCur;
          return (
            <div key={c.key} className="flex items-center justify-between gap-2 border-b border-border/50 py-1 text-[12px] last:border-0" data-testid={`globalsim-row-${c.key}`}>
              <span className="font-medium">{CANAL_LABELS[c.key]}
                {c.key !== 'autre' && Number(c.simPrice) !== Number(c.prixCur) && (
                  <span className="ml-1.5 text-[10px] text-muted-foreground">{fmtEur(c.prixCur, 0)} → {fmtEur(c.simPrice, 0)}</span>
                )}
              </span>
              <span className="flex items-center gap-1.5">
                <span className={margeCls(c.margeCur)}>{fmtEur(c.margeCur, 0)}</span>
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
                <span className={`font-semibold ${margeCls(c.margeSim)}`}>{fmtEur(c.margeSim, 0)}</span>
                <span className={`ml-1 text-[11px] ${dm >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>({dm >= 0 ? '+' : ''}{fmtEur(dm, 0)})</span>
              </span>
            </div>
          );
        })}
      </div>

      {/* Détail mois par mois */}
      {monthly.length > 0 && (
        <div className="mt-2 border-t pt-2">
          <button type="button" onClick={() => setShowMonthly((s) => !s)}
            className="flex w-full items-center justify-between rounded-lg px-1 py-1 text-[12px] font-semibold text-muted-foreground transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2"
            data-testid="globalsim-toggle-monthly" aria-expanded={showMonthly}>
            <span>Détail mois par mois ({monthly.length})</span>
            <ChevronDown className={`h-4 w-4 transition-transform ${showMonthly ? 'rotate-180' : ''}`} />
          </button>
          {showMonthly && (
            <div className="mt-1 space-y-0.5" data-testid="globalsim-monthly">
              {monthly.map((m) => (
                <div key={m.mois}
                  className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5 border-b border-border/40 px-1 py-1 text-[12px] last:border-0"
                  data-testid={`globalsim-month-${m.mois}`}>
                  <span className="font-medium capitalize">{labelYm(m.mois)}</span>
                  <span className="flex items-center gap-1.5">
                    <span className={m.rentCur >= 0 ? 'text-emerald-600' : 'text-rose-600'}>{fmtPct(m.rentCur, 1)}</span>
                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                    <span className={`font-semibold ${m.rentSim >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{fmtPct(m.rentSim, 1)}</span>
                    <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${m.deltaMarge >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                      {m.deltaMarge >= 0 ? '+' : ''}{fmtEur(m.deltaMarge, 0)}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}


function CollabView() {
  const list = useGet('/social/collaborateurs');
  const insights = useGet('/social/insights');
  const ins = insights.data;
  const [mat, setMat] = useState(null);
  const [open, setOpen] = useState(false);
  const [canalFilter, setCanalFilter] = useState('all');
  const q = useGet(`/social/collaborateur/${mat}`, null, !!mat);
  const d = q.data;
  const collaborateurs = list.data || [];
  const collabCanal = ins?.collab_canal || {};
  const filtered = canalFilter === 'all'
    ? collaborateurs
    : collaborateurs.filter((c) => collabCanal[c.matricule]?.principal === canalFilter);
  const canauxCanaux = ins?.canaux || {};
  const basePrices = {
    pap: canauxCanaux.porte_a_porte?.ca_par_bs || 202.5,
    sp: canauxCanaux.sites_prives?.ca_par_bs || 195,
    rue: canauxCanaux.rue?.ca_par_bs || 195,
  };
  const selected = collaborateurs.find((c) => c.matricule === mat);
  const rentBadge = (rentable) => (
    <Badge variant="secondary" className={`rounded-full ${rentable ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
      {rentable ? 'Rentable' : 'Non rentable'}
    </Badge>
  );
  const eur = 'px-2 py-2 text-right';
  const tauxCls = (t) => (t == null ? 'text-muted-foreground'
    : t <= 0.10 ? 'text-emerald-600' : t <= 0.25 ? 'text-amber-600' : 'text-rose-600');
  return (
    <div className="space-y-4" data-testid="social-collab-view">
      <CanauxCard canaux={ins?.canaux} total={ins?.canaux_total} loading={insights.isLoading} />
      <OptimiseurCard optim={ins?.optimisation} loading={insights.isLoading} />
      <GlobalSimulateurCard canaux={ins?.canaux} total={ins?.canaux_total} mensuel={ins?.mensuel} loading={insights.isLoading} />
      <AlertesCard data={ins?.alertes} loading={insights.isLoading} onSelect={setMat} selectedMat={mat} />
      <div className="flex flex-wrap items-center gap-3">
        <Label className="text-sm">Collaborateur</Label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" role="combobox" aria-expanded={open}
              className="w-80 justify-between rounded-xl font-normal" data-testid="social-collab-select">
              <span className="truncate">
                {selected ? `${selected.prenom} ${selected.nom} · ${selected.matricule}` : 'Sélectionner un collaborateur'}
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-0" align="start">
            <Command
              filter={(value, search) => (value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}>
              <CommandInput placeholder="Rechercher (nom, prénom, matricule)…" data-testid="social-collab-search" />
              <CommandList>
                <CommandEmpty>Aucun collaborateur trouvé.</CommandEmpty>
                <CommandGroup>
                  {filtered.map((c) => (
                    <CommandItem
                      key={c.matricule}
                      value={`${c.prenom} ${c.nom} ${c.matricule}`}
                      onSelect={() => { setMat(c.matricule); setOpen(false); }}
                      data-testid={`social-collab-option-${c.matricule}`}>
                      <Check className={`mr-2 h-4 w-4 ${mat === c.matricule ? 'opacity-100' : 'opacity-0'}`} />
                      <span className="truncate">{c.prenom} {c.nom}</span>
                      <span className="ml-auto pl-2 text-xs text-muted-foreground">{c.matricule}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {/* Filtre par canal principal */}
        <Select value={canalFilter} onValueChange={(v) => setCanalFilter(v)}>
          <SelectTrigger className="w-52 rounded-xl" data-testid="collab-canal-filter">
            <div className="flex items-center gap-2"><Filter className="h-4 w-4 opacity-70" /><SelectValue /></div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="collab-canal-filter-all">Tous les canaux</SelectItem>
            <SelectItem value="porte_a_porte" data-testid="collab-canal-filter-pap">Porte à Porte</SelectItem>
            <SelectItem value="sites_prives" data-testid="collab-canal-filter-sp">Sites privés</SelectItem>
            <SelectItem value="rue" data-testid="collab-canal-filter-rue">Rue</SelectItem>
          </SelectContent>
        </Select>
        {canalFilter !== 'all' && (
          <span className="text-xs text-muted-foreground" data-testid="collab-canal-filter-count">{filtered.length} collaborateur{filtered.length > 1 ? 's' : ''}</span>
        )}
        {selected && d?.totals && (
          d.totals.rentable
            ? rentBadge(true)
            : d.totals.rentable_opt
              ? <Badge variant="secondary" className="rounded-full bg-amber-100 text-amber-700">Rentable seulement en optimiste</Badge>
              : rentBadge(false)
        )}
      </div>
      {!mat ? (
        <EmptyState title="Sélectionnez un collaborateur" hint="Son historique mensuel (CA généré, coût, marge, rentabilité) s'affichera ici." />
      ) : !d ? (
        <Skeleton className="h-40 rounded-2xl" />
      ) : (
        <>
          <Card className="flex flex-wrap items-center gap-4 p-4">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl accent-bg-100 font-display font-bold accent-text">
              {(d.collaborateur?.prenom?.[0] || '') + (d.collaborateur?.nom?.[0] || '')}
            </div>
            <div>
              <div className="font-display font-semibold">{d.collaborateur?.prenom} {d.collaborateur?.nom}</div>
              <div className="text-xs text-muted-foreground">
                {d.collaborateur?.matricule} · {d.collaborateur?.remu_label || 'Salarié'}{d.collaborateur?.taux_horaire ? ` · ${fmtEur(d.collaborateur.taux_horaire, 2)}/h` : ''} · Source {d.collaborateur?.source || '—'}
              </div>
            </div>
            {d.totals && (
              <div className="ml-auto grid w-full grid-cols-2 gap-2 sm:w-auto sm:grid-cols-3 lg:grid-cols-5">
                <StatTile label="CA généré" value={fmtEur(d.totals.ca_brut, 0)} testid="collab-total-ca" />
                <StatTile label="Coût" value={fmtEur(d.totals.cout, 0)} valueCls="accent-text" />
                <StatTile label="Taux annul." value={d.totals.taux_annulation == null ? '—' : fmtPct(d.totals.taux_annulation, 1)}
                  sub={annulSub(d.totals)} valueCls={tauxAnnulCls(d.totals.taux_annulation)} testid="collab-total-taux" />
                <StatTile label="Rentab. prudente" hint="≈118 €/BS" tone="amber"
                  value={fmtPct(d.totals.rentabilite, 1)} valueCls={d.totals.rentable ? 'text-emerald-600' : 'text-rose-600'} testid="collab-total-rentab" />
                <StatTile label="Rentab. optimiste" hint="≈150 €/BS" tone="emerald"
                  value={fmtPct(d.totals.rentabilite_opt, 1)} valueCls={d.totals.rentable_opt ? 'text-emerald-600' : 'text-rose-600'} testid="collab-total-rentab-opt" />
              </div>
            )}
          </Card>
          <div className="flex items-start gap-2 rounded-xl border bg-muted/30 px-3 py-2 text-[11px] leading-snug text-muted-foreground" data-testid="collab-legend">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-text" />
            <span>
              <strong className="text-foreground">CA généré</strong> = BS × prix/BS du canal (Porte-à-Porte survalorisé vs Sites privés/Rue ; affiché par mois). Le CA réellement encaissé est incertain (annulations, refus, remboursements), d'où <strong className="text-foreground">2 hypothèses</strong> :{' '}
              <strong className="text-amber-700">Prudente</strong> (≈118 €/BS, après toute l'attrition) et <strong className="text-emerald-700">Optimiste</strong> (≈150 €/BS, peu d'annulations).
              Pour chacune : <strong className="text-foreground">Marge (€)</strong> = CA net − coût · <strong className="text-foreground">Rentabilité (%)</strong> = marge ÷ CA net.
              Le <strong className="text-foreground">taux d'annulation</strong> = bulletins annulés ÷ bulletins traités (indique l'hypothèse la plus réaliste).
              {d.collaborateur?.is_independant && (
                <> <strong className="text-foreground">Indépendant</strong> : pas d'heures — le coût est la rémunération {d.collaborateur?.remu_mode === 'forfait' ? 'au forfait mensuel' : 'au bulletin (BS × taux)'}.</>
              )}
            </span>
          </div>
          {!d.history?.length ? (
            <EmptyState title="Aucun historique" />
          ) : (
            <>
              {/* Desktop — tableau détaillé (masqué sous lg pour éviter le décalage des libellés) */}
              <div className="hidden lg:block">
                <TableWrap>
                  <table className="w-full min-w-[1180px] text-sm">
                    <thead>
                      <tr className="border-b bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                        <th className="px-3 py-2 text-left font-medium" rowSpan={2}>Mois</th>
                        <th className="px-2 py-2 text-right font-medium" rowSpan={2}>Heures</th>
                        <th className="px-2 py-2 text-right font-medium" rowSpan={2}>BS</th>
                        <th className="px-2 py-2 text-right font-medium" rowSpan={2}>CA généré</th>
                        <th className="px-2 py-2 text-right font-medium" rowSpan={2}>Coût</th>
                        <th className="px-2 py-2 text-right font-medium" rowSpan={2}>Taux annul.</th>
                        <th className="border-l border-amber-200 bg-amber-50/70 px-2 py-2 text-center font-semibold text-amber-700" colSpan={3}>Scénario prudent · CA net ≈118 €/BS</th>
                        <th className="border-l border-emerald-200 bg-emerald-50/70 px-2 py-2 text-center font-semibold text-emerald-700" colSpan={3}>Scénario optimiste · CA net ≈150 €/BS</th>
                      </tr>
                      <tr className="border-b bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                        <th className="border-l border-amber-200 px-2 py-2 text-right font-medium">CA net (€)</th>
                        <th className="px-2 py-2 text-right font-medium">Marge (€)</th>
                        <th className="px-2 py-2 text-right font-medium">Rentab. (%)</th>
                        <th className="border-l border-emerald-200 px-2 py-2 text-right font-medium">CA net (€)</th>
                        <th className="px-2 py-2 text-right font-medium">Marge (€)</th>
                        <th className="px-3 py-2 text-right font-medium">Rentab. (%)</th>
                      </tr>
                    </thead>
                    <tbody className="tabular">
                      {d.history.map((h) => (
                        <tr key={h.ym} className="border-b last:border-0 hover:bg-muted/40" data-testid={`collab-row-${h.ym}`}>
                          <td className="px-3 py-2 font-medium">{labelYm(h.ym)}</td>
                          <td className={eur}>{fmtNum(h.heures, 2)}</td>
                          <td className={eur}>{fmtInt(h.bs)}</td>
                          <td className={eur} data-testid={`collab-ca-${h.ym}`}>
                            {fmtEur(h.ca_brut, 2)}
                            <div className="text-[10px] font-normal text-muted-foreground" title={canalMixLabel(h.canal_mix) || ''}>
                              {fmtEur(h.ca_par_bs_eff, 2)}/BS{h.ca_par_bs_eff > 195 ? ' · PAP' : ''}
                            </div>
                          </td>
                          <td className={`${eur} accent-text font-semibold`}>
                            {fmtEur(h.cout, 2)}
                            {h.population === 'independant' && (
                              <div className="text-[10px] font-normal text-muted-foreground">
                                {h.cout_mode === 'forfait' ? 'forfait mensuel' : `${fmtInt(h.bs)} BS × ${fmtEur(h.cout_taux, 0)}`}
                              </div>
                            )}
                          </td>
                          <td className={`${eur} font-medium ${tauxCls(h.taux_annulation)}`} data-testid={`collab-taux-${h.ym}`}>
                            {h.taux_annulation == null ? '—' : fmtPct(h.taux_annulation, 1)}
                            {annulSub(h) && <div className="text-[10px] font-normal text-muted-foreground">{annulSub(h)}</div>}
                          </td>
                          {/* Scénario prudent */}
                          <td className={`${eur} border-l border-amber-100`}>{fmtEur(h.ca_net, 2)}</td>
                          <td className={`${eur} font-semibold ${margeCls(h.marge)}`} data-testid={`collab-marge-${h.ym}`}>{fmtEur(h.marge, 2)}</td>
                          <td className={`${eur} font-semibold ${h.rentable ? 'text-emerald-600' : 'text-rose-600'}`}>{fmtPct(h.rentabilite, 1)}</td>
                          {/* Scénario optimiste */}
                          <td className={`${eur} border-l border-emerald-100`}>{fmtEur(h.ca_net_opt, 2)}</td>
                          <td className={`${eur} font-semibold ${margeCls(h.marge_opt)}`} data-testid={`collab-marge-opt-${h.ym}`}>{fmtEur(h.marge_opt, 2)}</td>
                          <td className={`px-3 py-2 text-right font-semibold ${h.rentable_opt ? 'text-emerald-600' : 'text-rose-600'}`}>{fmtPct(h.rentabilite_opt, 1)}</td>
                        </tr>
                      ))}
                    </tbody>
                    {d.totals && (
                      <tfoot>
                        <tr className="border-t-2 bg-muted/50 font-semibold" data-testid="collab-totals-row">
                          <td className="px-3 py-2">Total ({d.totals.nb_mois} mois)</td>
                          <td className={eur}>{fmtNum(d.totals.heures, 2)}</td>
                          <td className={eur}>{fmtInt(d.totals.bs)}</td>
                          <td className={eur}>{fmtEur(d.totals.ca_brut, 2)}</td>
                          <td className={`${eur} accent-text`}>{fmtEur(d.totals.cout, 2)}</td>
                          <td className={`${eur} ${tauxCls(d.totals.taux_annulation)}`}>
                            {d.totals.taux_annulation == null ? '—' : fmtPct(d.totals.taux_annulation, 1)}
                            {annulSub(d.totals) && <div className="text-[10px] font-normal text-muted-foreground">{annulSub(d.totals)}</div>}
                          </td>
                          <td className={`${eur} border-l border-amber-100`}>{fmtEur(d.totals.ca_net, 2)}</td>
                          <td className={`${eur} ${margeCls(d.totals.marge)}`}>{fmtEur(d.totals.marge, 2)}</td>
                          <td className={`${eur} ${d.totals.rentable ? 'text-emerald-600' : 'text-rose-600'}`}>{fmtPct(d.totals.rentabilite, 1)}</td>
                          <td className={`${eur} border-l border-emerald-100`}>{fmtEur(d.totals.ca_net_opt, 2)}</td>
                          <td className={`${eur} ${margeCls(d.totals.marge_opt)}`}>{fmtEur(d.totals.marge_opt, 2)}</td>
                          <td className={`px-3 py-2 text-right ${d.totals.rentable_opt ? 'text-emerald-600' : 'text-rose-600'}`}>{fmtPct(d.totals.rentabilite_opt, 1)}</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </TableWrap>
              </div>

              {/* Mobile / tablette — cartes claires et étiquetées */}
              <div className="space-y-3 lg:hidden" data-testid="collab-mobile-cards">
                {d.history.map((h) => (
                  <CollabMonthCard key={h.ym} row={h} title={labelYm(h.ym)} />
                ))}
                {d.totals && (
                  <CollabMonthCard row={d.totals} title={`Total (${d.totals.nb_mois} mois)`} isTotal />
                )}
              </div>
            </>
          )}
          {d.history?.length > 0 && (
            <SimulateurCard matricule={mat} basePrices={basePrices} current={d} />
          )}
        </>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------- //
//  Paramètres (versioned)                                                      //
// --------------------------------------------------------------------------- //
const NUM_FIELDS = [
  { k: 'taux_horaire_defaut', label: 'Taux horaire par défaut (€)', step: '0.01' },
  { k: 'tr_seuil_heures', label: 'Seuil TR (heures/jour)', step: '0.5' },
  { k: 'conges_pct', label: 'Congés (%)', pct: true },
  { k: 'precarite_pct', label: 'Précarité (%)', pct: true },
  { k: 'urssaf_pct', label: 'URSSAF (%)', pct: true },
  { k: 'cout_tr_unitaire', label: 'Coût TR unitaire (€)', step: '0.5' },
  { k: 'coeff_cout', label: 'Coefficient coût (net / x)', step: '0.01' },
  { k: 'ca_par_bs', label: 'CA par BS (€)', step: '1' },
  { k: 'tr_valeur_faciale', label: 'TR — valeur faciale (€)', step: '0.5' },
  { k: 'tr_part_employeur', label: 'TR — part employeur (€)', step: '0.5' },
];

function ParamsView({ ym }) {
  const q = useGet('/social/params', { ym });
  const qc = useQueryClient();
  const [draft, setDraft] = useState(null);
  const [effective, setEffective] = useState(`${ym}-01`);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const current = q.data?.current;
  const model = draft || current;

  const setField = (k, v, pct = false) => {
    setDraft({ ...(draft || current), [k]: pct ? (parseFloat(v) / 100) : (v === '' ? '' : parseFloat(v)) });
  };
  const setBaremeMontant = (i, v) => {
    const b = (draft || current).bareme_prime.map((tier, idx) => idx === i ? { ...tier, montant: parseFloat(v) || 0 } : tier);
    setDraft({ ...(draft || current), bareme_prime: b });
  };
  const setArr = (k, i, v) => {
    const base = draft || current;
    const arr = [...(base[k] || [])];
    arr[i] = v === '' ? '' : (parseFloat(v) || 0);
    setDraft({ ...base, [k]: arr });
  };
  const setCanalPrix = (key, v) => {
    const base = draft || current;
    const table = { ...(base.ca_par_bs_canal || {}) };
    table[key] = v === '' ? '' : (parseFloat(v) || 0);
    setDraft({ ...base, ca_par_bs_canal: table });
  };
  const setHebdo = (i, v) => {
    const base = draft || current;
    const arr = (base.inde_bareme_hebdo || []).map((t, idx) => idx === i ? { ...t, montant: parseFloat(v) || 0 } : t);
    setDraft({ ...base, inde_bareme_hebdo: arr });
  };
  const setKey = (k, v) => setDraft({ ...(draft || current), [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      await api.post('/social/params', { params: model, effective_date: effective, note });
      toast.success('Nouvelle version de paramètres enregistrée');
      setDraft(null); setNote('');
      qc.invalidateQueries({ predicate: (query) => String(query.queryKey?.[0] || '').startsWith('/social') });
    } catch (e) {
      toast.error('Enregistrement impossible');
    } finally { setSaving(false); }
  };

  if (!model) return <Skeleton className="h-64 rounded-2xl" />;
  const dirty = !!draft;

  return (
    <div className="space-y-5" data-testid="social-params-view">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-xl text-sm text-muted-foreground">
          Paramètres <strong className="text-foreground">versionnés</strong> par date d'effet : une modification ne recalcule jamais les mois antérieurs à sa prise d'effet.
        </p>
        <Badge variant="secondary" className="gap-1"><Settings2 className="h-3.5 w-3.5" /> {q.data?.versions?.length || 0} version(s)</Badge>
      </div>

      <Card className="p-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {NUM_FIELDS.map((f) => (
            <div key={f.k}>
              <Label className="text-xs">{f.label}</Label>
              <Input type="number" step={f.pct ? '0.1' : (f.step || '0.01')}
                value={f.pct ? (model[f.k] * 100) : (model[f.k] ?? '')}
                onChange={(e) => setField(f.k, e.target.value, f.pct)}
                className="mt-1 rounded-xl tabular" data-testid={`param-${f.k}`} />
            </div>
          ))}
          <div>
            <Label className="text-xs">Libellé prime (PayFit)</Label>
            <Input value={model.prime_type_libelle || ''} onChange={(e) => setDraft({ ...(draft || current), prime_type_libelle: e.target.value })}
              className="mt-1 rounded-xl" data-testid="param-prime-libelle" />
          </div>
          <div>
            <Label className="text-xs">Source BS</Label>
            <Select value={model.bs_source || 'bs_reel'} onValueChange={(v) => setDraft({ ...(draft || current), bs_source: v })}>
              <SelectTrigger className="mt-1 rounded-xl" data-testid="param-bs-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="bs_reel">BS réel (terrain)</SelectItem>
                <SelectItem value="bs_rue">BS rue</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Separator className="my-5" />
        <h4 className="mb-2 font-display text-sm font-semibold">Barème des primes (par nombre de BS)</h4>
        <div className="overflow-x-auto">
          <CardTable className="w-full min-w-[520px] text-sm">
            <thead>
              <tr className="border-b text-left text-[11px] uppercase text-muted-foreground">
                <th className="px-2 py-1.5 font-medium">BS min</th>
                <th className="px-2 py-1.5 font-medium">BS max</th>
                <th className="px-2 py-1.5 font-medium">Montant (€)</th>
                <th className="px-2 py-1.5 font-medium">Bonus / BS</th>
              </tr>
            </thead>
            <tbody className="tabular">
              {(model.bareme_prime || []).map((tier, i) => (
                <tr key={i} className="border-b last:border-0">
                  <td className="px-2 py-1.5">{tier.min}</td>
                  <td className="px-2 py-1.5">{tier.max ?? '∞'}</td>
                  <td className="px-2 py-1.5">
                    <Input type="number" value={tier.montant} onChange={(e) => setBaremeMontant(i, e.target.value)}
                      className="h-8 w-28 rounded-lg tabular" data-testid={`bareme-${i}`} />
                  </td>
                  <td className="px-2 py-1.5 text-muted-foreground">{tier.plus_per_bs ? `+${tier.plus_per_bs} €/BS` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </CardTable>
        </div>

        <Separator className="my-5" />
        <h4 className="mb-3 font-display text-sm font-semibold">Paramètres P&amp;L « toutes populations » (Lot 1)</h4>
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2" data-testid="params-lot1">
          {/* Structure */}
          <div className="rounded-2xl border bg-muted/20 p-4" data-testid="params-structure">
            <div className="mb-3 flex items-center gap-2 text-xs font-semibold text-muted-foreground"><Landmark className="h-4 w-4 accent-text" /> STRUCTURE (mensuel, lissé)</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Holding ECH GROUP (€)</Label>
                <Input type="number" step="100" value={model.structure_holding ?? ''} onChange={(e) => setField('structure_holding', e.target.value)} className="mt-1 rounded-xl tabular" data-testid="param-structure_holding" />
              </div>
              <div>
                <Label className="text-xs">Frais généraux (€)</Label>
                <Input type="number" step="100" value={model.structure_frais_generaux ?? ''} onChange={(e) => setField('structure_frais_generaux', e.target.value)} className="mt-1 rounded-xl tabular" data-testid="param-structure_frais_generaux" />
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between rounded-xl bg-background/60 px-3 py-2">
              <Label htmlFor="param-dimanche-ouvre" className="text-xs">Dimanche = jour d'activité (dénominateur de lissage)</Label>
              <Switch id="param-dimanche-ouvre" checked={!!model.inde_dimanche_est_ouvre} onCheckedChange={(v) => setKey('inde_dimanche_est_ouvre', v)} data-testid="param-dimanche-ouvre" />
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">Lissage structure &amp; forfaits sur les jours d'activité attendus du mois (lun-sam par défaut).</p>
          </div>

          {/* Facteurs CA */}
          <div className="rounded-2xl border bg-muted/20 p-4" data-testid="params-ca-factors">
            <div className="mb-3 flex items-center gap-2 text-xs font-semibold text-muted-foreground"><Coins className="h-4 w-4 accent-text" /> FACTEURS CA (BS bruts → CA net)</div>
            <div className="grid grid-cols-3 gap-3">
              {(model.ca_diviseurs || []).map((v, i) => (
                <div key={i}>
                  <Label className="text-xs">Diviseur {i + 1}</Label>
                  <Input type="number" step="0.01" value={v} onChange={(e) => setArr('ca_diviseurs', i, e.target.value)} className="mt-1 rounded-xl tabular" data-testid={`param-ca-div-${i}`} />
                </div>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">CA net = BS bruts / {(model.ca_diviseurs || []).join(' / ')} × prix/BS du canal.</p>
          </div>

          {/* Prix / BS par canal */}
          <div className="rounded-2xl border bg-muted/20 p-4" data-testid="params-ca-canal">
            <div className="mb-3 flex items-center gap-2 text-xs font-semibold text-muted-foreground"><Coins className="h-4 w-4 accent-text" /> PRIX / BS PAR CANAL (CA brut)</div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Porte à Porte (€)</Label>
                <Input type="number" step="0.5" value={model.ca_par_bs_canal?.porte_a_porte ?? ''} onChange={(e) => setCanalPrix('porte_a_porte', e.target.value)} className="mt-1 rounded-xl tabular" data-testid="param-ca-canal-pap" />
              </div>
              <div>
                <Label className="text-xs">Sites privés (€)</Label>
                <Input type="number" step="0.5" value={model.ca_par_bs_canal?.sites_prives ?? ''} onChange={(e) => setCanalPrix('sites_prives', e.target.value)} className="mt-1 rounded-xl tabular" data-testid="param-ca-canal-sp" />
              </div>
              <div>
                <Label className="text-xs">Rue (€)</Label>
                <Input type="number" step="0.5" value={model.ca_par_bs_canal?.rue ?? ''} onChange={(e) => setCanalPrix('rue', e.target.value)} className="mt-1 rounded-xl tabular" data-testid="param-ca-canal-rue" />
              </div>
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">Le porte-à-porte se vend généralement 5 à 10 € de plus qu'en sites privés. Le prix s'applique bulletin par bulletin selon son canal ; le CA/BS d'un collaborateur devient la moyenne pondérée de ses canaux.</p>
          </div>

          {/* Indés bulletin */}
          <div className="rounded-2xl border bg-muted/20 p-4" data-testid="params-inde-bareme">
            <div className="mb-3 flex items-center gap-2 text-xs font-semibold text-muted-foreground"><ReceiptText className="h-4 w-4 accent-text" /> INDÉS AU BULLETIN</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Mode retenu (totaux)</Label>
                <Select value={model.inde_mode_totaux || 'forecast'} onValueChange={(v) => setKey('inde_mode_totaux', v)}>
                  <SelectTrigger className="mt-1 rounded-xl" data-testid="param-inde-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="forecast">Forecast (taux bloqué)</SelectItem>
                    <SelectItem value="reel">Réel contrat (barème)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Taux bulletin par défaut (€)</Label>
                <Input type="number" step="1" value={model.inde_taux_bulletin_defaut ?? ''} onChange={(e) => setField('inde_taux_bulletin_defaut', e.target.value)} className="mt-1 rounded-xl tabular" data-testid="param-inde_taux_bulletin_defaut" />
              </div>
            </div>
            <div className="mt-3 overflow-x-auto">
              <CardTable className="w-full min-w-[360px] text-sm">
                <thead>
                  <tr className="border-b text-left text-[11px] uppercase text-muted-foreground">
                    <th className="px-2 py-1.5 font-medium">BS/sem. min</th>
                    <th className="px-2 py-1.5 font-medium">max</th>
                    <th className="px-2 py-1.5 font-medium">Montant (€)</th>
                  </tr>
                </thead>
                <tbody className="tabular">
                  {(model.inde_bareme_hebdo || []).map((t, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="px-2 py-1.5">{t.min}</td>
                      <td className="px-2 py-1.5">{t.max ?? '∞'}</td>
                      <td className="px-2 py-1.5">
                        <Input type="number" value={t.montant} onChange={(e) => setHebdo(i, e.target.value)} className="h-8 w-24 rounded-lg tabular" data-testid={`param-hebdo-${i}`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </CardTable>
            </div>
            <div className="mt-3 flex items-center justify-between rounded-xl bg-background/60 px-3 py-2">
              <Label htmlFor="param-inde-dimanche" className="text-xs">Dimanche rattaché à la semaine qui s'achève</Label>
              <Switch id="param-inde-dimanche" checked={!!model.inde_dimanche_semaine_precedente} onCheckedChange={(v) => setKey('inde_dimanche_semaine_precedente', v)} data-testid="param-inde-dimanche" />
            </div>
          </div>

          {/* Dividendes */}
          <div className="rounded-2xl border bg-muted/20 p-4" data-testid="params-dividendes">
            <div className="mb-3 flex items-center gap-2 text-xs font-semibold text-muted-foreground"><PiggyBank className="h-4 w-4 accent-text" /> DIVIDENDES (facteurs)</div>
            <div className="grid grid-cols-3 gap-3">
              {(model.dividende_facteurs || []).map((v, i) => (
                <div key={i}>
                  <Label className="text-xs">Facteur {i + 1}</Label>
                  <Input type="number" step="0.001" value={v} onChange={(e) => setArr('dividende_facteurs', i, e.target.value)} className="mt-1 rounded-xl tabular" data-testid={`param-div-${i}`} />
                </div>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">Dividendes = MAX(0 ; résultat × {(model.dividende_facteurs || []).join(' × ')}).</p>
          </div>

          {/* Rentabilité RD (aide à la décision) */}
          <div className="rounded-2xl border bg-muted/20 p-4 lg:col-span-2" data-testid="params-rentab">
            <div className="mb-3 flex items-center gap-2 text-xs font-semibold text-muted-foreground"><Gauge className="h-4 w-4 accent-text" /> RENTABILITÉ (aide à la décision de reconduction)</div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <Label className="text-xs">Cible résultat net après IS (%)</Label>
                <Input type="number" step="0.5" value={model.rentab_cible_resultat_net != null ? model.rentab_cible_resultat_net * 100 : ''} onChange={(e) => setField('rentab_cible_resultat_net', e.target.value, true)} className="mt-1 rounded-xl tabular" data-testid="param-rentab_cible_resultat_net" />
              </div>
              <div>
                <Label className="text-xs">Taux d'IS (%)</Label>
                <Input type="number" step="0.5" value={model.rentab_taux_is != null ? model.rentab_taux_is * 100 : ''} onChange={(e) => setField('rentab_taux_is', e.target.value, true)} className="mt-1 rounded-xl tabular" data-testid="param-rentab_taux_is" />
              </div>
              <div>
                <Label className="text-xs">Mode de seuil</Label>
                <Select value={model.rentab_seuil_mode || 'dynamique'} onValueChange={(v) => setKey('rentab_seuil_mode', v)}>
                  <SelectTrigger className="mt-1 rounded-xl" data-testid="param-rentab_seuil_mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dynamique">Dynamique (dérivé cible)</SelectItem>
                    <SelectItem value="manuel">Manuel (figé)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Seuil manuel (×)</Label>
                <Input type="number" step="0.1" value={model.rentab_seuil_manuel ?? ''} onChange={(e) => setField('rentab_seuil_manuel', e.target.value)} className="mt-1 rounded-xl tabular" data-testid="param-rentab_seuil_manuel" />
              </div>
              <div>
                <Label className="text-xs">Borne « non rentable » (ratio min ×)</Label>
                <Input type="number" step="0.1" value={model.rentab_borne_min ?? ''} onChange={(e) => setField('rentab_borne_min', e.target.value)} className="mt-1 rounded-xl tabular" data-testid="param-rentab_borne_min" />
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between rounded-xl bg-background/60 px-3 py-2">
              <Label htmlFor="param-rentab-alloc" className="text-xs">Allouer la structure au prorata du coût (marge nette)</Label>
              <Switch id="param-rentab-alloc" checked={!!model.rentab_allouer_structure} onCheckedChange={(v) => setKey('rentab_allouer_structure', v)} data-testid="param-rentab_allouer_structure" />
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">Seuil dynamique ×min = 1 / (1 − part structure − cible avant IS) ; structure allouée → 1 / (1 − cible avant IS).</p>
          </div>
        </div>

        <Separator className="my-5" />
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">Date d'effet</Label>
            <Input type="date" value={effective} onChange={(e) => setEffective(e.target.value)} className="mt-1 w-44 rounded-xl" data-testid="param-effective-date" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs">Note (optionnel)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex : revalorisation SMIC" className="mt-1 rounded-xl" data-testid="param-note" />
          </div>
          <Button className="gap-2 rounded-xl text-white" style={accentStyle} onClick={save} disabled={saving || !dirty} data-testid="param-save-btn">
            <Save className="h-4 w-4" /> {saving ? 'Enregistrement…' : 'Enregistrer une version'}
          </Button>
        </div>
        {dirty && <p className="mt-2 text-[11px] text-amber-600">Modifications non enregistrées — une nouvelle version sera créée à la date d'effet indiquée.</p>}
      </Card>

      {/* Versions history */}
      {q.data?.versions?.length > 0 && (
        <Card className="p-5">
          <h4 className="mb-3 font-display text-sm font-semibold">Historique des versions</h4>
          <div className="space-y-2">
            {q.data.versions.slice().reverse().map((v) => (
              <div key={v.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-muted/40 px-3 py-2 text-xs">
                <span className="font-medium">v{v.version} · effet {v.effective_date}</span>
                <span className="text-muted-foreground">{v.note || '—'} · {v.created_by}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------- //
//  Import heures                                                               //
// --------------------------------------------------------------------------- //
function ImportView() {
  const [file, setFile] = useState(null);
  const [month, setMonth] = useState('');
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const journal = useGet('/social/imports');
  const qc = useQueryClient();

  const upload = async () => {
    if (!file) { toast.error('Sélectionnez un fichier Excel'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (month) fd.append('month', month);
      const { data } = await api.post('/social/import/hours', fd);
      setReport(data);
      toast.success(`Import réussi : ${data.months?.length || 0} mois, ${data.collaborateurs} collaborateurs`);
      journal.refetch();
      qc.invalidateQueries({ predicate: (query) => String(query.queryKey?.[0] || '').startsWith('/social') });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import impossible');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="social-import-view">
      <Card className="p-6">
        <div className="flex items-start gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl accent-bg-100"><UploadCloud className="h-6 w-6 accent-text" /></div>
          <div>
            <h3 className="font-display text-lg font-semibold">Importer des heures</h3>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
              Fichier Excel au format « HR/TR » (grille HR/TR par jour, une feuille par mois). Les heures importées
              remplacent celles du mois concerné. <strong className="text-foreground">hr_hours</strong> n'est pas modifiée.
            </p>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">Fichier (.xlsx)</Label>
            <Input type="file" accept=".xlsx,.xlsm" onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="mt-1 w-72 rounded-xl" data-testid="social-import-file" />
          </div>
          <div>
            <Label className="text-xs">Mois ciblé (optionnel)</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1 w-44 rounded-xl" data-testid="social-import-month" />
          </div>
          <Button className="gap-2 rounded-xl text-white" style={accentStyle} onClick={upload} disabled={busy} data-testid="social-import-btn">
            <UploadCloud className="h-4 w-4" /> {busy ? 'Import…' : 'Importer'}
          </Button>
        </div>

        {report && (
          <div className="mt-4 rounded-xl bg-muted/50 p-3 text-xs" data-testid="social-import-report">
            <div className="font-medium">Rapport d'import ({report.source})</div>
            <div className="mt-1 text-muted-foreground">{report.collaborateurs} collaborateurs · {report.months?.length || 0} mois : {report.months?.map((m) => `${labelYm(m.ym)} (${m.collaborateurs})`).join(', ')}</div>
          </div>
        )}
      </Card>

      <Card className="p-5">
        <h4 className="mb-3 font-display text-sm font-semibold">Journal des imports</h4>
        {!journal.data?.length ? (
          <p className="text-xs text-muted-foreground">Aucun import enregistré.</p>
        ) : (
          <div className="space-y-2">
            {journal.data.slice(0, 8).map((e) => (
              <div key={e.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-muted/40 px-3 py-2 text-xs">
                <span className="font-medium">{e.report?.source || e.kind} · {e.report?.collaborateurs || 0} collaborateurs</span>
                <span className="text-muted-foreground">{e.report?.months?.length || 0} mois · {new Date(e.created_at).toLocaleString('fr-FR')}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
// --------------------------------------------------------------------------- //
//  Paie indés au bulletin (architecture à deux temps)                          //
// --------------------------------------------------------------------------- //
function FunnelStep({ label, value, sub, tone = 'default', arrow = true }) {
  const tones = {
    default: 'border-border bg-background text-foreground',
    ever: 'border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-200',
    livr: 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-300',
    mand: 'border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-900 dark:bg-indigo-950/40 dark:text-indigo-300',
    ded: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300',
    net: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300',
  };
  return (
    <>
      <div className={`flex min-w-[92px] flex-col items-center rounded-xl border px-3 py-2 ${tones[tone]}`}>
        <span className="kpi-value text-lg tabular">{value}</span>
        <span className="mt-0.5 text-[10px] font-medium uppercase tracking-wide opacity-80">{label}</span>
        {sub && <span className="text-[10px] opacity-70">{sub}</span>}
      </div>
      {arrow && <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
    </>
  );
}

function ReferenceFunnel({ inde }) {
  if (!inde) return null;
  const line = (inde.lines || []).find((l) => l.consolide && l.enregistres > 0)
    || (inde.lines || []).find((l) => l.enregistres > 0);
  if (!line) return null;
  const ded = (line.preattritions || 0) + (line.refus || 0);
  return (
    <Card className="p-5" data-testid="paie-funnel-card">
      <SectionTitle icon={Filter} title="Entonnoir de référence"
        sub={`${inde.prenom} ${inde.nom} · ${inde.matricule} · semaine ${line.label}`} />
      <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="paie-funnel">
        <FunnelStep label="Enregistrés" value={fmtInt(line.enregistres)} sub="tous statuts" tone="ever" />
        <FunnelStep label="Livrés" value={fmtInt(line.evergiving_livres)} sub="Appr∩IBAN" tone="livr" />
        <FunnelStep label="Mandats créés" value={line.mandats_crees == null ? '—' : fmtInt(line.mandats_crees)} sub="fichier asso" tone="mand" />
        <FunnelStep label="Déductions" value={ded ? `−${fmtInt(ded)}` : '0'} sub="pré-attr + refus" tone="ded" />
        <FunnelStep label="Net payable" value={fmtInt(line.net_payable)} sub={`${fmtEur(line.montant, 2)}`} tone="net" arrow={false} />
      </div>
      <p className="mt-3 text-[11px] text-muted-foreground">
        Le « 17→14 » vit dans cet entonnoir (enregistrés → livrés). Le net payable ne dépend que du fichier
        association : <strong className="text-foreground">mandats créés − pré-attritions − refus</strong>. Barème à taux plein rétroactif.
      </p>
    </Card>
  );
}

// --- Arbitrage ligne : quelle source « fait foi » pour les mandats ---------- #
function SourceFaitFoiToggle({ value, disabled, onPick, testid }) {
  const opt = (val, label) => {
    const active = value === val;
    return (
      <button type="button" disabled={disabled} onClick={() => !active && onPick(val)}
        data-testid={testid ? `${testid}-${val}` : undefined}
        className={`px-2 py-0.5 text-[10px] font-medium rounded-md border transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
          active ? 'border-transparent text-white' : 'border-border bg-background text-muted-foreground hover:bg-muted/60'}`}
        style={active ? accentStyle : undefined}>{label}</button>
    );
  };
  return (
    <span className="inline-flex items-center gap-1 rounded-lg border border-border/50 p-0.5" data-testid={testid}>
      {opt('fichier', 'Fichier')}
      {opt('evergiving', 'Evergiving')}
    </span>
  );
}

// --- Cellule pré-attrition éditable (admin) — invisible pour l'indé ---------- #
function PreAttrCell({ line, disabled, onSave, onReset, testid }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  if (line.preattritions == null) return <span className="text-muted-foreground">—</span>;
  const overridden = !!line.preattritions_override;
  const commit = () => {
    const n = Math.max(0, parseInt(val || '0', 10) || 0);
    onSave(n);
    setEditing(false);
  };
  if (editing) {
    return (
      <span className="inline-flex items-center justify-end gap-1" data-testid={`${testid}-editing`}>
        <input type="number" min="0" value={val} autoFocus
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
          className="w-14 rounded-md border border-border bg-background px-1.5 py-0.5 text-right text-xs tabular focus:outline-none focus:ring-2 focus:ring-[var(--tenant-accent-300)]"
          data-testid={`${testid}-input`} />
        <button type="button" onClick={commit} title="Enregistrer" className="rounded p-0.5 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30" data-testid={`${testid}-save`}><Check className="h-3.5 w-3.5" /></button>
        <button type="button" onClick={() => setEditing(false)} title="Annuler" className="rounded p-0.5 text-muted-foreground hover:bg-muted"><X className="h-3.5 w-3.5" /></button>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center justify-end gap-1" data-testid={testid}>
      <span className={`tabular ${overridden ? 'font-semibold text-amber-700 dark:text-amber-400' : 'text-amber-700 dark:text-amber-400'}`}>
        {line.preattritions ? `−${fmtInt(line.preattritions)}` : '0'}
      </span>
      {overridden && <span title="Pré-attrition ajustée manuellement" className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" data-testid={`${testid}-dot`} />}
      {!disabled && (
        <button type="button" onClick={() => { setVal(String(line.preattritions ?? 0)); setEditing(true); }}
          title="Ajuster la pré-attrition" className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground" data-testid={`${testid}-edit`}>
          <Pencil className="h-3 w-3" />
        </button>
      )}
      {overridden && !disabled && (
        <button type="button" onClick={onReset} title="Rétablir la valeur du fichier"
          className="rounded p-0.5 text-muted-foreground hover:bg-muted" data-testid={`${testid}-reset`}>
          <Undo2 className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}

// --- Cellule hebdo éditable : force Net / €/BS / Refus (motif obligatoire) -- #
function ForcedCell({ title, effective, origine, forced, kind = 'int', prefix = '', disabled, meta, onSave, onReset, testid }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState('');
  const [motif, setMotif] = useState('');
  const fmtV = (v) => (kind === 'eur' ? fmtNum(v, 2) : fmtInt(v));
  const openEdit = () => { setVal(String(effective ?? 0)); setMotif(meta?.motif || ''); setOpen(true); };
  const save = () => {
    const n = kind === 'eur' ? parseFloat(String(val).replace(',', '.')) : parseInt(val || '0', 10);
    if (!(n >= 0)) { toast.error('Valeur invalide'); return; }
    if (!motif.trim()) { toast.error('Un motif est obligatoire'); return; }
    onSave(n, motif.trim()); setOpen(false);
  };
  const disp = (v) => `${v > 0 && prefix ? prefix : ''}${fmtV(v)}`;
  return (
    <span className="inline-flex items-center justify-end gap-1" data-testid={testid}>
      <span className={`tabular ${forced ? 'font-semibold text-amber-700 dark:text-amber-300' : (prefix ? 'text-amber-700 dark:text-amber-400' : '')}`}>{disp(effective ?? 0)}</span>
      {forced && <span title={`Forcé — valeur calculée : ${disp(origine ?? 0)}${meta?.motif ? ' · ' + meta.motif : ''}`} className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" data-testid={`${testid}-dot`} />}
      {!disabled && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" onClick={openEdit} title={`Forcer ${title}`} className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground" data-testid={`${testid}-edit`}>
              <Pencil className="h-3 w-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-64" data-testid={`${testid}-popover`}>
            <div className="space-y-2">
              <div className="text-xs font-semibold">Forcer {title} · cette semaine</div>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <label className="text-[10px] text-muted-foreground">Valeur forcée</label>
                  <Input type="number" min="0" step={kind === 'eur' ? '0.5' : '1'} value={val} autoFocus onChange={(e) => setVal(e.target.value)} className="h-8 text-right text-sm tabular" data-testid={`${testid}-input`} />
                </div>
                <span className="pb-1.5 text-[10px] text-muted-foreground">calc. {disp(origine ?? 0)}</span>
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground">Motif (obligatoire)</label>
                <Input value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Ajustement manuel" className="h-8 text-sm" data-testid={`${testid}-motif`} />
              </div>
              {meta?.by && <p className="flex items-center gap-1 text-[10px] text-muted-foreground"><History className="h-3 w-3" /> {meta.by} · {fmtDateFr(meta.at)}</p>}
              <div className="flex items-center justify-between gap-2 pt-0.5">
                {forced ? <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs text-muted-foreground" onClick={() => { onReset(); setOpen(false); }} data-testid={`${testid}-reset`}><Undo2 className="h-3 w-3" /> Rétablir le calcul</Button> : <span />}
                <Button size="sm" className="h-7 gap-1 text-xs text-white" style={accentStyle} onClick={save} data-testid={`${testid}-save`}><Check className="h-3 w-3" /> Appliquer</Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </span>
  );
}

// --- Badge €/BS éditable : plafond contractuel (avenant) — admin ---------- #
function RatePlafondBadge({ inde, frozen, onSetRate, onClearRate }) {
  const cap = inde.plafond;   // {plafond,date_debut,date_fin,motif,updated_by,updated_at}
  const [open, setOpen] = useState(false);
  const [plafond, setPlafond] = useState('65');
  const [deb, setDeb] = useState('');
  const [fin, setFin] = useState('');
  const [motif, setMotif] = useState('');
  useEffect(() => {
    setPlafond(cap ? String(cap.plafond) : String(Math.min(65, inde.taux || 65)));
    setDeb(cap?.date_debut || ''); setFin(cap?.date_fin || ''); setMotif(cap?.motif || '');
  }, [cap, inde.taux]);
  const save = () => {
    const p = parseFloat(String(plafond).replace(',', '.'));
    if (!(p > 0)) { toast.error('Plafond €/BS invalide'); return; }
    if (!motif.trim()) { toast.error('Un motif est obligatoire'); return; }
    onSetRate({ matricule: inde.matricule, plafond: p,
      date_debut: deb || null, date_fin: fin || null, motif: motif.trim() });
    setOpen(false);
  };
  const restore = () => { onClearRate({ matricule: inde.matricule }); setOpen(false); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button"
          className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium transition-colors hover:bg-muted ${cap ? 'border-amber-400 bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300' : 'border-border text-foreground'}`}
          data-testid={`paie-rate-badge-${inde.matricule}`}>
          {cap ? <Lock className="h-2.5 w-2.5" /> : <Pencil className="h-2.5 w-2.5 opacity-50" />}
          {cap ? `${fmtNum(cap.plafond, 2)} €/BS · plafond` : `${fmtNum(inde.taux, 2)} €/BS`}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80" data-testid={`paie-rate-popover-${inde.matricule}`}>
        <div className="space-y-2.5">
          <div className="flex items-center gap-2 text-sm font-semibold"><Scale className="h-4 w-4 accent-text" /> Plafond €/BS (avenant)</div>
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            Plafonne la grille sur une période. La valeur calculée (barème) est conservée et réappliquée hors période. N'affecte que <strong>{inde.prenom} {inde.nom}</strong>.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-muted-foreground">Plafond €/BS</label>
              <Input type="number" min="0" step="0.5" value={plafond} onChange={(e) => setPlafond(e.target.value)} className="h-8 text-sm tabular" data-testid={`paie-rate-plafond-${inde.matricule}`} />
            </div>
            <div className="flex items-end pb-1 text-[10px] text-muted-foreground">Barème : jusqu'à {fmtNum(inde.taux, 2)} €/BS</div>
            <div>
              <label className="text-[10px] text-muted-foreground">À partir du</label>
              <Input type="date" value={deb} onChange={(e) => setDeb(e.target.value)} className="h-8 text-sm" data-testid={`paie-rate-debut-${inde.matricule}`} />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground">Jusqu'au</label>
              <Input type="date" value={fin} onChange={(e) => setFin(e.target.value)} className="h-8 text-sm" data-testid={`paie-rate-fin-${inde.matricule}`} />
            </div>
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground">Motif (obligatoire)</label>
            <Input value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Avenant n°1 — plafond contractuel" className="h-8 text-sm" data-testid={`paie-rate-motif-${inde.matricule}`} />
          </div>
          {cap?.updated_by && (
            <p className="flex items-center gap-1 text-[10px] text-muted-foreground"><History className="h-3 w-3" /> Modifié par {cap.updated_by} le {fmtDateFr(cap.updated_at)}</p>
          )}
          {frozen && <p className="text-[10px] text-amber-600">Paie figée : défigez-la pour modifier le plafond.</p>}
          <div className="flex items-center justify-between gap-2 pt-1">
            {cap
              ? <Button size="sm" variant="ghost" className="h-8 gap-1 text-xs text-muted-foreground" onClick={restore} data-testid={`paie-rate-restore-${inde.matricule}`}><Undo2 className="h-3.5 w-3.5" /> Rétablir le calcul</Button>
              : <span />}
            <Button size="sm" className="h-8 gap-1 text-xs text-white" style={accentStyle} onClick={save} disabled={frozen} data-testid={`paie-rate-save-${inde.matricule}`}><Check className="h-3.5 w-3.5" /> Appliquer</Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function IndeStatement({ inde, onToggleVisible, onPreview, visBusy, editable = false, frozen = false, onOverride, onClearOverride, onAddBonus, onUpdateBonus, onDeleteBonus, onSetRate, onClearRate, onAddCoord, onDelCoord }) {
  const [open, setOpen] = useState(true);
  // « paie réelle » = total de TOUS les montants hebdo (payable consolidé + estimation
  // provisoire des semaines en attente) ; ne pas afficher 0 € s'il y a des bulletins.
  const totalPaie = inde.total_montant_estime ?? inde.total_montant;
  const ecartPaie = inde.ecart_forecast_estime ?? inde.ecart_forecast;
  const negatif = ecartPaie < 0;
  const estProvisoire = (inde.total_provisoire_estime || 0) > 0 && (inde.total_montant || 0) === 0;
  const visible = inde.paie_visible === true;
  return (
    <Card className="overflow-hidden" data-testid={`paie-inde-${inde.matricule}`}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="flex w-full flex-wrap items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/40"
        data-testid={`paie-inde-toggle-${inde.matricule}`}>
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl accent-bg-100"><Receipt className="h-4 w-4 accent-text" /></div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-display text-sm font-semibold">{inde.prenom} {inde.nom}</span>
              <Badge variant="secondary" className="text-[10px]">{inde.matricule}</Badge>
              <Badge variant="outline" className={`text-[10px] ${inde.plafond ? 'border-amber-400 text-amber-700 dark:text-amber-300' : ''}`}>
                {inde.plafond ? `${fmtNum(inde.plafond.plafond, 2)} €/BS · plafond` : `${fmtNum(inde.taux, 2)} €/BS`}
              </Badge>
              {!inde.actif && <Badge variant="outline" className="border-rose-300 text-[10px] text-rose-600">inactif</Badge>}
            </div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">
              {fmtInt(inde.total_net_payable)} BS nets payables · {inde.lines.filter((l) => l.en_attente).length > 0
                ? `${inde.lines.filter((l) => l.en_attente).length} sem. en attente` : 'toutes semaines consolidées'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4 text-right">
          <div>
            <div className="kpi-value text-lg tabular" data-testid={`paie-inde-total-${inde.matricule}`}>{fmtEur(totalPaie, 2)}</div>
            <div className="text-[10px] text-muted-foreground">paie réelle{estProvisoire ? ' (estimée)' : ''}</div>
          </div>
          <div>
            <div className={`kpi-value text-sm tabular ${negatif ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>{fmtEur(ecartPaie, 2)}</div>
            <div className="text-[10px] text-muted-foreground">vs forecast</div>
          </div>
          {open ? <ChevronLeft className="h-4 w-4 rotate-90 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 rotate-90 text-muted-foreground" />}
        </div>
      </button>
      {onToggleVisible && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t bg-muted/20 px-4 py-2" data-testid={`paie-inde-actions-${inde.matricule}`}>
          <div className="flex items-center gap-2">
            <Switch checked={visible} disabled={visBusy}
              onCheckedChange={(v) => onToggleVisible(inde.matricule, v)}
              data-testid={`paie-visible-switch-${inde.matricule}`} />
            <span className="text-xs font-medium">Peut voir sa paie</span>
            <Badge variant={visible ? 'secondary' : 'outline'} className="gap-1 text-[10px]"
              data-testid={`paie-visible-badge-${inde.matricule}`}>
              {visible ? <><Eye className="h-3 w-3" /> Visible dans son espace</> : <>Masqué</>}
            </Badge>
            {onSetRate && (
              <>
                <span className="mx-1 h-4 w-px bg-border" />
                <span className="text-xs font-medium text-muted-foreground">Taux €/BS :</span>
                <RatePlafondBadge inde={inde} frozen={frozen} onSetRate={onSetRate} onClearRate={onClearRate} />
              </>
            )}
          </div>
          <Button size="sm" variant="outline" className="h-8 gap-1.5 rounded-lg"
            onClick={() => onPreview(inde.matricule)} data-testid={`paie-preview-btn-${inde.matricule}`}>
            <Eye className="h-3.5 w-3.5" /> Aperçu (vue indé)
          </Button>
        </div>
      )}
      {open && (
        <div className="overflow-x-auto border-t">
          <CardTable className="w-full min-w-[720px] text-sm">
            <thead className="bg-muted/40 text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Semaine (lun→dim)</th>
                <th className="px-2 py-2 text-right font-medium">Enregistrés</th>
                <th className="px-2 py-2 text-right font-medium">Livrés</th>
                <th className="px-2 py-2 text-right font-medium">Mandats</th>
                <th className="px-2 py-2 text-right font-medium">Pré-attr.</th>
                <th className="px-2 py-2 text-right font-medium">Refus</th>
                <th className="px-2 py-2 text-right font-medium">Net</th>
                <th className="px-3 py-2 text-left font-medium">Palier</th>
                <th className="px-2 py-2 text-right font-medium">€/BS</th>
                <th className="px-3 py-2 text-right font-medium">Montant</th>
              </tr>
            </thead>
            <tbody>
              {inde.lines.map((l) => (
                <tr key={l.monday} className={`border-t ${l.en_attente ? 'bg-amber-50/50 dark:bg-amber-950/10' : ''}`} data-testid={`paie-line-${inde.matricule}-${l.monday}`}>
                  <td className="px-3 py-2">
                    <span className="font-medium">{l.label}</span>
                    {l.en_attente && <Badge variant="outline" className="ml-2 border-amber-300 text-[10px] text-amber-700 dark:text-amber-300">en attente</Badge>}
                  </td>
                  <td className="px-2 py-2 text-right tabular text-muted-foreground">{fmtInt(l.enregistres)}</td>
                  <td className="px-2 py-2 text-right tabular">{fmtInt(l.evergiving_livres)}</td>
                  <td className="px-2 py-2 text-right tabular">
                    {l.mandats_crees == null ? '—' : (
                      <span className="inline-flex items-center justify-end gap-1.5">
                        {editable && l.consolide && (l.divergence || l.mandats_source === 'evergiving') && (
                          <SourceFaitFoiToggle value={l.mandats_source} disabled={frozen}
                            onPick={(src) => onOverride({ matricule: inde.matricule, monday: l.monday, mandats_source: src })}
                            testid={`paie-src-${inde.matricule}-${l.monday}`} />
                        )}
                        <span className={l.mandats_source === 'evergiving' ? 'font-semibold accent-text' : ''}>{fmtInt(l.mandats_crees)}</span>
                        {l.mandats_source === 'evergiving' && l.mandats_crees_fichier != null && (
                          <span className="text-[10px] text-muted-foreground">(fich. {fmtInt(l.mandats_crees_fichier)})</span>
                        )}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-right">
                    {editable && l.consolide
                      ? <PreAttrCell line={l} disabled={frozen}
                          onSave={(n) => onOverride({ matricule: inde.matricule, monday: l.monday, preattritions: n })}
                          onReset={() => onOverride({ matricule: inde.matricule, monday: l.monday, clear_preattritions: true })}
                          testid={`paie-preattr-${inde.matricule}-${l.monday}`} />
                      : <span className="tabular text-amber-700 dark:text-amber-400">{l.preattritions ? `−${fmtInt(l.preattritions)}` : (l.preattritions === 0 ? '0' : '—')}</span>}
                  </td>
                  <td className="px-2 py-2 text-right">
                    {editable && l.consolide
                      ? <ForcedCell title="les Refus" kind="int" prefix="−" effective={l.refus || 0} origine={l.refus_origine || 0}
                          forced={l.refus_forced} disabled={frozen} meta={l.force_meta}
                          onSave={(v, motif) => onOverride({ matricule: inde.matricule, monday: l.monday, refus: v, motif })}
                          onReset={() => onOverride({ matricule: inde.matricule, monday: l.monday, clear_refus: true })}
                          testid={`paie-refus-${inde.matricule}-${l.monday}`} />
                      : <span className="tabular text-amber-700 dark:text-amber-400">{l.refus ? `−${fmtInt(l.refus)}` : (l.refus === 0 ? '0' : '—')}</span>}
                  </td>
                  <td className="px-2 py-2 text-right tabular font-semibold">
                    {editable
                      ? <ForcedCell title="le Net" kind="int" effective={l.net_payable} origine={l.net_origine}
                          forced={l.net_forced} disabled={frozen} meta={l.force_meta}
                          onSave={(v, motif) => onOverride({ matricule: inde.matricule, monday: l.monday, net: v, motif })}
                          onReset={() => onOverride({ matricule: inde.matricule, monday: l.monday, clear_net: true })}
                          testid={`paie-net-${inde.matricule}-${l.monday}`} />
                      : fmtInt(l.net_payable)}
                  </td>
                  <td className="px-3 py-2 text-left text-[11px] text-muted-foreground">{l.palier}</td>
                  <td className="px-2 py-2 text-right tabular text-muted-foreground">
                    {editable ? (
                      <ForcedCell title="le €/BS" kind="eur" effective={l.taux_bs} origine={l.taux_bs_origine}
                        forced={l.rate_forced} disabled={frozen || (!l.consolide && !l.net_payable)} meta={l.force_meta}
                        onSave={(v, motif) => onOverride({ matricule: inde.matricule, monday: l.monday, taux_bs: v, motif })}
                        onReset={() => onOverride({ matricule: inde.matricule, monday: l.monday, clear_taux_bs: true })}
                        testid={`paie-rate-cell-${inde.matricule}-${l.monday}`} />
                    ) : (l.plafonne && l.taux_bs_origine != null && l.taux_bs_origine !== l.taux_bs ? (
                      <span className="inline-flex items-center justify-end gap-1" title="Plafond (avenant) appliqué — grille suspendue au-dessus">
                        <span className="text-[10px] line-through opacity-60">{fmtNum(l.taux_bs_origine, 2)}</span>
                        <span className="font-semibold text-amber-700 dark:text-amber-300">{fmtNum(l.taux_bs, 2)}</span>
                      </span>
                    ) : fmtNum(l.taux_bs, 2))}
                  </td>
                  <td className="px-3 py-2 text-right tabular font-medium">
                    <span className="inline-flex items-center justify-end gap-1.5">
                      {l.estime_sur === 'enregistres' && (
                        <Badge variant="outline"
                          className="border-amber-300 text-[9px] font-normal text-amber-700 dark:text-amber-300"
                          title="Montant estimé sur les bulletins ENREGISTRÉS (aucun livré/approuvé pour l'instant)"
                          data-testid={`paie-line-estime-${inde.matricule}-${l.monday}`}>estimé</Badge>
                      )}
                      {fmtEur(l.montant, 2)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t bg-muted/30 font-semibold">
                <td className="px-3 py-2" colSpan={6}>Sous-total bulletins (semaines consolidées)</td>
                <td className="px-2 py-2 text-right tabular">{fmtInt(inde.total_net_payable)}</td>
                <td className="px-3 py-2" colSpan={2} />
                <td className="px-3 py-2 text-right tabular">{fmtEur(inde.total_montant_bulletins ?? inde.total_montant, 2)}</td>
              </tr>
            </tfoot>
          </CardTable>
          <BonusSection inde={inde} editable={editable} frozen={frozen}
            onAdd={onAddBonus} onUpdate={onUpdateBonus} onDelete={onDeleteBonus}
            onAddCoord={onAddCoord} onDelCoord={onDelCoord} />
          {((inde.bonus && inde.bonus.length > 0) || inde.provision_retenue > 0) && (
            <div className="flex flex-wrap items-center justify-end gap-x-5 gap-y-1 border-t bg-muted/20 px-4 py-2.5 text-xs" data-testid={`paie-engage-${inde.matricule}`}>
              <span className="inline-flex items-center gap-1.5">
                <Banknote className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                Montant versé (paie / trésorerie) : <strong className="tabular" data-testid={`paie-verse-${inde.matricule}`}>{fmtEur(inde.montant_verse, 2)}</strong>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Scale className="h-3.5 w-3.5 accent-text" />
                Coût engagé (Pilotage REX) : <strong className="tabular accent-text" data-testid={`paie-engage-val-${inde.matricule}`}>{fmtEur(inde.cout_engage, 2)}</strong>
              </span>
              {inde.provision_retenue > 0 && (
                <span className="inline-flex items-center gap-1.5 text-amber-700 dark:text-amber-300">
                  <PiggyBank className="h-3.5 w-3.5" />
                  dont provision retenue : <strong className="tabular">{fmtEur(inde.provision_retenue, 2)}</strong>
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// --- Lignes manuelles typées (conditions particulières) — admin ----------- #
const LINE_TYPE_META = {
  forfait:        { label: 'Forfait mensuel',            signe: '+', ph: 'Forfait ouverture de zone', unit: '1150', cls: 'border-emerald-300 text-emerald-700 dark:text-emerald-300' },
  prime:          { label: 'Prime exceptionnelle',       signe: '+', ph: 'Aide exceptionnelle logement', unit: '300', cls: 'border-violet-300 text-violet-700 dark:text-violet-300' },
  commission:     { label: 'Commission coordination',    signe: '+', ph: 'Commission recruteurs rattachés', unit: '10', cls: 'border-sky-300 text-sky-700 dark:text-sky-300' },
  frais:          { label: 'Autres frais',               signe: '+', ph: 'Frais essence / spot rebooké', unit: '70', cls: 'border-amber-300 text-amber-700 dark:text-amber-300' },
  retenue:        { label: 'Retenue de garantie',        signe: '-', ph: 'Retenue 20 % sur assiette', unit: '0', cls: 'border-rose-300 text-rose-700 dark:text-rose-300' },
  liberation:     { label: 'Libération de retenue',      signe: '+', ph: 'Restitution retenue antérieure', unit: '0', cls: 'border-teal-300 text-teal-700 dark:text-teal-300' },
  regularisation: { label: 'Régularisation',             signe: '+', ph: 'Correction mois antérieur', unit: '0', cls: 'border-slate-300 text-slate-700 dark:text-slate-300' },
};
function lineTypeMeta(cat) { return LINE_TYPE_META[cat] || LINE_TYPE_META.prime; }

// --- Commission de coordination : ligne CALCULÉE + rattachement recruteurs - #
function CommissionPanel({ inde, editable, commissionLine, onAddCoord, onDelCoord }) {
  const [open, setOpen] = useState(false);
  const [rec, setRec] = useState('');
  const [taux, setTaux] = useState('10');
  const [deb, setDeb] = useState('');
  const [fin, setFin] = useState('');
  const detail = commissionLine?.detail || [];
  const total = commissionLine ? commissionLine.montant : 0;
  const hasAny = detail.length > 0;
  const add = () => {
    const code = rec.trim().toUpperCase();
    const t = parseFloat(String(taux).replace(',', '.'));
    if (!code) { toast.error('Code recruteur requis'); return; }
    if (Number.isNaN(t) || t < 0) { toast.error('€/BS invalide'); return; }
    onAddCoord({ coordinateur: inde.matricule, recruteur: code, taux: t, date_debut: deb || null, date_fin: fin || null });
    setRec(''); setTaux('10'); setDeb(''); setFin('');
  };
  if (!editable && !hasAny) return null;
  return (
    <div className="mt-2 rounded-lg border border-sky-200/70 bg-sky-50/30 dark:border-sky-900/40 dark:bg-sky-950/10" data-testid={`paie-coord-${inde.matricule}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left" data-testid={`paie-coord-toggle-${inde.matricule}`}>
        <span className="flex items-center gap-2 text-xs font-medium text-sky-800 dark:text-sky-300">
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          <Users className="h-3.5 w-3.5" /> Commission de coordination
          <Badge variant="outline" className="border-sky-300 text-[9px] text-sky-700 dark:text-sky-300">calculée</Badge>
          <span className="text-muted-foreground">· {detail.length} recruteur{detail.length > 1 ? 's' : ''} rattaché{detail.length > 1 ? 's' : ''}</span>
        </span>
        <span className="tabular text-sm font-semibold accent-text" data-testid={`paie-coord-total-${inde.matricule}`}>+{fmtEur(total, 2)}</span>
      </button>
      {open && (
        <div className="space-y-2 border-t border-sky-200/60 px-3 py-2.5 dark:border-sky-900/40">
          {hasAny ? (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase text-muted-foreground">
                  <tr>
                    <th className="px-2 py-1 text-left">Recruteur</th>
                    <th className="px-2 py-1 text-right">Enreg.</th>
                    <th className="px-2 py-1 text-right">Pré-attr.</th>
                    <th className="px-2 py-1 text-right">Refus</th>
                    <th className="px-2 py-1 text-right">Nets</th>
                    <th className="px-2 py-1 text-right">€/BS</th>
                    <th className="px-2 py-1 text-right">Montant</th>
                    <th className="px-2 py-1" />
                  </tr>
                </thead>
                <tbody>
                  {detail.map((d) => (
                    <tr key={d.id || d.recruteur} className="border-t border-sky-100/60 dark:border-sky-900/30" data-testid={`paie-coord-row-${inde.matricule}-${d.recruteur}`}>
                      <td className="px-2 py-1.5">
                        <span className="font-medium">{d.recruteur}</span>
                        {d.nom ? <span className="ml-1 text-muted-foreground">{d.nom}</span> : null}
                        {(d.date_debut || d.date_fin) && <span className="ml-1 text-[10px] text-muted-foreground">({d.date_debut ? fmtDateFr(d.date_debut) : '…'} → {d.date_fin ? fmtDateFr(d.date_fin) : '…'})</span>}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular">{fmtInt(d.enregistres)}</td>
                      <td className="px-2 py-1.5 text-right tabular text-amber-700 dark:text-amber-400">{d.preattritions ? `−${fmtInt(d.preattritions)}` : '0'}</td>
                      <td className="px-2 py-1.5 text-right tabular text-amber-700 dark:text-amber-400">{d.refus ? `−${fmtInt(d.refus)}` : '0'}</td>
                      <td className="px-2 py-1.5 text-right tabular font-semibold">{fmtInt(d.nets)}</td>
                      <td className="px-2 py-1.5 text-right tabular text-muted-foreground">{fmtNum(d.taux, 2)}</td>
                      <td className="px-2 py-1.5 text-right tabular font-medium accent-text">{fmtEur(d.montant, 2)}</td>
                      <td className="px-2 py-1.5 text-right">
                        {editable && d.id && (
                          <button type="button" title="Détacher le recruteur" onClick={() => onDelCoord({ id: d.id })} className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-destructive" data-testid={`paie-coord-del-${d.id}`}><X className="h-3.5 w-3.5" /></button>
                        )}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t border-sky-200 font-semibold dark:border-sky-800">
                    <td className="px-2 py-1.5" colSpan={6}>Total commission (entre dans l'assiette de la retenue)</td>
                    <td className="px-2 py-1.5 text-right tabular accent-text">{fmtEur(total, 2)}</td>
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-[11px] text-muted-foreground">Aucun recruteur rattaché. Ajoutez-en un ci-dessous : la commission se calcule automatiquement (Nets × €/BS, Nets = Enregistrés − Pré-attrition − Refus).</p>
          )}
          {editable && (
            <div className="flex flex-wrap items-end gap-2 border-t border-sky-200/50 pt-2 dark:border-sky-900/30">
              <div className="flex flex-col gap-1">
                <label className="text-[10px] text-muted-foreground">Code recruteur</label>
                <Input value={rec} onChange={(e) => setRec(e.target.value)} placeholder="AT0569" className="h-8 w-28 text-sm uppercase" data-testid={`paie-coord-rec-${inde.matricule}`} />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] text-muted-foreground">€/BS</label>
                <Input type="number" min="0" step="0.5" value={taux} onChange={(e) => setTaux(e.target.value)} className="h-8 w-20 text-right text-sm tabular" data-testid={`paie-coord-taux-${inde.matricule}`} />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] text-muted-foreground">Début (option.)</label>
                <Input type="date" value={deb} onChange={(e) => setDeb(e.target.value)} className="h-8 text-sm" data-testid={`paie-coord-deb-${inde.matricule}`} />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] text-muted-foreground">Fin (option.)</label>
                <Input type="date" value={fin} onChange={(e) => setFin(e.target.value)} className="h-8 text-sm" data-testid={`paie-coord-fin-${inde.matricule}`} />
              </div>
              <Button size="sm" className="h-8 gap-1 text-xs text-white" style={accentStyle} onClick={add} data-testid={`paie-coord-add-${inde.matricule}`}>
                <UserPlus className="h-3.5 w-3.5" /> Rattacher
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BonusSection({ inde, editable, frozen, onAdd, onUpdate, onDelete, onAddCoord, onDelCoord }) {
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [categorie, setCategorie] = useState('forfait');
  const [label, setLabel] = useState(LINE_TYPE_META.forfait.ph);
  const [qte, setQte] = useState('1');
  const [unit, setUnit] = useState(LINE_TYPE_META.forfait.unit);
  const [signe, setSigne] = useState('+');
  const allBonus = inde.bonus || [];
  const commissionLine = allBonus.find((b) => b.auto);
  const bonus = allBonus.filter((b) => !b.auto);   // lignes manuelles uniquement
  const totalBonus = inde.total_bonus || 0;
  const reset = () => { setCategorie('forfait'); setLabel(LINE_TYPE_META.forfait.ph); setQte('1'); setUnit(LINE_TYPE_META.forfait.unit); setSigne('+'); setAdding(false); setEditId(null); };
  const pickCat = (c) => {
    const m = lineTypeMeta(c);
    setCategorie(c); setLabel(m.ph); setUnit(m.unit); setSigne(m.signe);
  };
  const startEdit = (b) => {
    setEditId(b.id); setAdding(true);
    setCategorie(b.categorie || 'prime');
    setLabel(b.label || '');
    setQte(String(b.quantite ?? 1));
    setUnit(String(b.montant_unitaire ?? 0));
    setSigne(b.signe || lineTypeMeta(b.categorie).signe);
  };
  const submit = () => {
    const uStr = String(unit).trim().replace(',', '.');
    const u = uStr === '' ? NaN : parseFloat(uStr);
    const qStr = String(qte).trim();
    const q = qStr === '' ? 1 : parseInt(qStr, 10);
    if (!label.trim()) { toast.error('Un libellé est requis'); return; }
    if (Number.isNaN(u)) { toast.error('Montant unitaire requis (0 autorisé)'); return; }
    if (Number.isNaN(q) || q < 0) { toast.error('Quantité invalide (0 autorisé)'); return; }
    if (editId) {
      onUpdate({ id: editId, categorie, signe, label: label.trim(), montant_unitaire: Math.abs(u), quantite: q });
    } else {
      onAdd({ matricule: inde.matricule, categorie, signe, label: label.trim(), montant_unitaire: Math.abs(u), quantite: q });
    }
    reset();
  };
  if (!editable && bonus.length === 0 && !commissionLine) return null;
  return (
    <div className="border-t bg-muted/10 px-4 py-3" data-testid={`paie-bonus-${inde.matricule}`}>
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          <Sparkles className="h-3.5 w-3.5" /> Lignes manuelles <span className="font-normal">(forfait, prime, commission, retenue de garantie, libération, régularisation, autres frais)</span>
        </div>
        {editable && !adding && (
          <Button size="sm" variant="outline" className="h-7 gap-1 text-xs"
            onClick={() => setAdding(true)} data-testid={`paie-bonus-add-btn-${inde.matricule}`}>
            <Plus className="h-3.5 w-3.5" /> Ajouter une ligne
          </Button>
        )}
      </div>
      {editable && frozen && (
        <p className="mb-2 flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50/60 px-2.5 py-1.5 text-[11px] leading-relaxed text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-300" data-testid={`paie-bonus-frozen-note-${inde.matricule}`}>
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>Paie figée : les bulletins restent gelés. Vous pouvez cependant ajouter une ligne manuelle oubliée — la régularisation met à jour immédiatement le relevé de l'indé <strong>et</strong> le Coût engagé dans le Pilotage REX, sans écart avec le virement réel.</span>
        </p>
      )}
      {bonus.length > 0 ? (
        <div className="space-y-1">
          {bonus.map((b) => {
            const m = lineTypeMeta(b.categorie || 'prime');
            const neg = (b.montant || 0) < 0;
            return (
            <div key={b.id} className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-3 py-1.5 text-sm" data-testid={`paie-bonus-line-${b.id}`}>
              <div className="min-w-0 flex-1 truncate">
                <Badge variant="outline" className={`mr-2 text-[9px] ${m.cls}`}>{b.type_label || m.label}</Badge>
                <span className="font-medium">{b.label}</span>
                <span className="ml-2 text-[11px] text-muted-foreground">{fmtInt(b.quantite)} × {fmtEur(b.montant_unitaire, 2)}</span>
              </div>
              <span className={`tabular font-semibold ${neg ? 'text-rose-600 dark:text-rose-400' : 'accent-text'}`}>
                {neg ? '−' : '+'}{fmtEur(Math.abs(b.montant), 2)}
              </span>
              {editable && (
                <button type="button" title="Modifier la ligne"
                  onClick={() => startEdit(b)}
                  className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground" data-testid={`paie-bonus-edit-${b.id}`}>
                  <Pencil className="h-3 w-3" />
                </button>
              )}
              {editable && (
                <button type="button" title="Supprimer la ligne"
                  onClick={() => onDelete({ id: b.id })}
                  className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-destructive disabled:opacity-40" data-testid={`paie-bonus-del-${b.id}`}>
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            );
          })}
        </div>
      ) : (
        editable && !adding && <p className="text-[11px] text-muted-foreground">Aucune ligne manuelle ce mois-ci.</p>
      )}
      {adding && (
        <div className="mt-2 flex flex-wrap items-end gap-2 rounded-lg border border-dashed border-border bg-background p-2.5" data-testid={`paie-bonus-form-${inde.matricule}`}>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] text-muted-foreground">Type</label>
            <Select value={categorie} onValueChange={pickCat}>
              <SelectTrigger className="h-8 w-44 text-sm" data-testid={`paie-bonus-cat-${inde.matricule}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="forfait">Forfait mensuel (+)</SelectItem>
                <SelectItem value="prime">Prime exceptionnelle (+)</SelectItem>
                <SelectItem value="commission">Commission coordination (+)</SelectItem>
                <SelectItem value="frais">Autres frais (+)</SelectItem>
                <SelectItem value="retenue">Retenue de garantie (−)</SelectItem>
                <SelectItem value="liberation">Libération de retenue (+)</SelectItem>
                <SelectItem value="regularisation">Régularisation (±)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {categorie === 'regularisation' && (
            <div className="flex flex-col gap-1">
              <label className="text-[10px] text-muted-foreground">Signe</label>
              <Select value={signe} onValueChange={setSigne}>
                <SelectTrigger className="h-8 w-20 text-sm" data-testid={`paie-bonus-signe-${inde.matricule}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="+">+</SelectItem>
                  <SelectItem value="-">−</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] text-muted-foreground">Libellé (visible par l'indé)</label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} className="h-8 w-52 text-sm" placeholder={lineTypeMeta(categorie).ph} data-testid={`paie-bonus-label-${inde.matricule}`} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] text-muted-foreground">Quantité</label>
            <Input type="number" min="0" value={qte} onChange={(e) => setQte(e.target.value)} className="h-8 w-20 text-right text-sm tabular" data-testid={`paie-bonus-qte-${inde.matricule}`} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] text-muted-foreground">Montant unitaire (€)</label>
            <Input type="number" min="0" step="0.01" value={unit} onChange={(e) => setUnit(e.target.value)} className="h-8 w-28 text-right text-sm tabular" data-testid={`paie-bonus-unit-${inde.matricule}`} />
          </div>
          <div className="flex items-center gap-1 pb-0.5">
            <Button size="sm" className="h-8 gap-1 text-xs text-white" style={accentStyle} onClick={submit} data-testid={`paie-bonus-save-${inde.matricule}`}>
              <Check className="h-3.5 w-3.5" /> {editId ? 'Modifier' : 'Enregistrer'}
            </Button>
            <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={reset}>Annuler</Button>
          </div>
        </div>
      )}
      {(bonus.length > 0 || commissionLine) && (
        <div className="mt-2 flex items-center justify-between border-t pt-2 text-sm">
          <span className="text-xs text-muted-foreground">Total lignes (manuelles + commission) · Montant versé (bulletins + lignes)</span>
          <span className="tabular">
            <span className={`font-medium ${totalBonus < 0 ? 'text-rose-600 dark:text-rose-400' : 'accent-text'}`}>{totalBonus < 0 ? '−' : '+'}{fmtEur(Math.abs(totalBonus), 2)}</span>
            <span className="mx-2 text-muted-foreground">·</span>
            <span className="font-semibold accent-text" data-testid={`paie-bonus-grandtotal-${inde.matricule}`}>{fmtEur(inde.montant_verse ?? inde.total_montant, 2)}</span>
          </span>
        </div>
      )}
      <CommissionPanel inde={inde} editable={editable} commissionLine={commissionLine}
        onAddCoord={onAddCoord} onDelCoord={onDelCoord} />
    </div>
  );
}

function fmtDateFr(iso) {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

function IndePreviewDialog({ matricule, ym, association, open, onClose }) {
  const q = useGet('/social/paie-indes/preview-inde',
    { matricule, ym, association }, open && !!matricule);
  const r = q.data;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="paie-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-4 w-4 accent-text" /> Aperçu — vue de l'indépendant
          </DialogTitle>
          <DialogDescription>
            Rendu exact de ce que l'indépendant verra dans son espace Booking (onglet « Ma paie »).
          </DialogDescription>
        </DialogHeader>
        {q.isLoading ? (
          <div className="space-y-3"><Skeleton className="h-20 rounded-xl" /><Skeleton className="h-40 rounded-xl" /></div>
        ) : !r ? (
          <EmptyState title="Aperçu indisponible" hint="Aucune donnée pour cet indépendant sur ce mois." />
        ) : (
          <div className="space-y-4" data-testid="paie-preview-body">
            {/* Entête relevé */}
            <div className="rounded-2xl border bg-muted/20 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="font-display text-base font-semibold">{r.prenom} {r.nom}</div>
                  <div className="text-xs text-muted-foreground">{r.association} · {matricule}</div>
                </div>
                <Badge className="gap-1 text-white first-letter:uppercase" style={accentStyle}>
                  <CalendarCheck2 className="h-3.5 w-3.5" /> Paie de {r.mois_label}
                </Badge>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs sm:grid-cols-3">
                <div><div className="text-muted-foreground">Période</div><div className="font-medium">{r.periode?.label || '—'}</div></div>
                <div><div className="text-muted-foreground">Prélèvement</div><div className="font-medium">{fmtDateFr(r.prelevement_date)}</div></div>
                <div><div className="text-muted-foreground">BS nets</div><div className="font-medium tabular">{fmtInt(r.total_bs_nets)}</div></div>
              </div>
            </div>
            {/* Semaines */}
            <div className="overflow-x-auto rounded-2xl border">
              <CardTable className="w-full min-w-[520px] text-sm">
                <thead className="bg-muted/40 text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Semaine (lun→dim)</th>
                    <th className="px-2 py-2 text-right font-medium">BS nets</th>
                    <th className="px-3 py-2 text-left font-medium">Palier</th>
                    <th className="px-2 py-2 text-right font-medium">€/BS</th>
                    <th className="px-3 py-2 text-right font-medium">Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {(r.semaines || []).map((s) => (
                    <tr key={s.monday} className={`border-t ${s.en_attente ? 'bg-amber-50/50 dark:bg-amber-950/10' : ''}`}>
                      <td className="px-3 py-2">
                        <span className="font-medium">{s.label}</span>
                        {s.en_attente && <Badge variant="outline" className="ml-2 border-amber-300 text-[10px] text-amber-700 dark:text-amber-300">en attente</Badge>}
                      </td>
                      <td className="px-2 py-2 text-right tabular font-semibold">{fmtInt(s.bs_nets)}</td>
                      <td className="px-3 py-2 text-left text-[11px] text-muted-foreground">{s.palier}</td>
                      <td className="px-2 py-2 text-right tabular text-muted-foreground">{fmtNum(s.taux_bs, 2)}</td>
                      <td className="px-3 py-2 text-right tabular font-medium">{fmtEur(s.montant, 2)}</td>
                    </tr>
                  ))}
                  {(r.bonus || []).map((b, i) => {
                    const catLabel = b.type_label || lineTypeMeta(b.categorie || 'prime').label;
                    const neg = (b.montant || 0) < 0;
                    return (
                    <tr key={`bonus-${i}`} className="border-t bg-emerald-50/30 dark:bg-emerald-950/10">
                      <td className="px-3 py-2">
                        <span className="font-medium">{b.label}</span>
                        <span className="ml-1 text-[11px] text-muted-foreground">({catLabel.toLowerCase()} · {fmtInt(b.quantite)} × {fmtEur(b.montant_unitaire, 2)})</span>
                      </td>
                      <td className="px-2 py-2 text-right tabular text-muted-foreground">—</td>
                      <td className="px-3 py-2 text-left text-[11px] text-muted-foreground">{catLabel}</td>
                      <td className="px-2 py-2 text-right tabular text-muted-foreground">—</td>
                      <td className={`px-3 py-2 text-right tabular font-medium ${neg ? 'text-rose-600 dark:text-rose-400' : 'accent-text'}`}>{neg ? '−' : '+'}{fmtEur(Math.abs(b.montant), 2)}</td>
                    </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  {(r.total_bonus || 0) !== 0 && (
                    <tr className="border-t bg-muted/20 text-xs">
                      <td className="px-3 py-1.5 text-muted-foreground">Sous-total bulletins</td>
                      <td className="px-2 py-1.5 text-right tabular">{fmtInt(r.total_bs_nets)}</td>
                      <td className="px-3 py-1.5" colSpan={2} />
                      <td className="px-3 py-1.5 text-right tabular">{fmtEur(r.total_montant_bulletins ?? r.total_montant, 2)}</td>
                    </tr>
                  )}
                  <tr className="border-t bg-muted/30 font-semibold">
                    <td className="px-3 py-2">Total à facturer</td>
                    <td className="px-2 py-2 text-right tabular">{fmtInt(r.total_bs_nets)}</td>
                    <td className="px-3 py-2" colSpan={2} />
                    <td className="px-3 py-2 text-right tabular accent-text">{fmtEur(r.total_montant, 2)}</td>
                  </tr>
                </tfoot>
              </CardTable>
            </div>
            {r.nb_semaines_en_attente > 0 && (
              <p className="flex items-start gap-1.5 text-[11px] text-amber-700 dark:text-amber-300" data-testid="paie-preview-pending-note">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {r.nb_semaines_en_attente} semaine(s) « en attente » : montants provisoires (fichier association non encore consolidé), non facturables tant qu'ils ne sont pas confirmés.
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PaieIndesView({ ym }) {
  const [assoc, setAssoc] = useState('AIF');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const q = useGet('/social/paie-indes', { ym, association: assoc });
  const d = q.data;
  const qc = useQueryClient();
  const pubQ = useGet('/social/paie-indes/publication', { ym, association: assoc });
  const pub = pubQ.data;
  const pubStatus = pub?.status || 'draft';
  const [pubBusy, setPubBusy] = useState(false);
  const [previewMat, setPreviewMat] = useState(null);
  const [visBusy, setVisBusy] = useState(false);

  const upload = async () => {
    if (!file) { toast.error('Sélectionnez le fichier association (.xlsx)'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('association', assoc);
      fd.append('mois', ym);
      const { data } = await api.post('/social/import-asso', fd);
      toast.success(`Fichier ${assoc} importé : ${data.counts.mandats} mandats · ${data.counts.preattritions} pré-attritions · ${data.counts.refus} refus`);
      setFile(null);
      q.refetch();
      qc.invalidateQueries({ predicate: (query) => String(query.queryKey?.[0] || '').startsWith('/social') });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import impossible');
    } finally { setBusy(false); }
  };

  const exportXlsx = async () => {
    try {
      await downloadGet('/social/paie-indes/export', `Paie_indes_${assoc}_${ym}.xlsx`, { ym, association: assoc });
      toast.success('Relevé Excel généré');
    } catch { toast.error('Export impossible'); }
  };

  const doPub = async (action, okMsg) => {
    setPubBusy(true);
    try {
      await api.post(`/social/paie-indes/${action}`, { ym, association: assoc });
      toast.success(okMsg);
      await Promise.all([pubQ.refetch(), q.refetch()]);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action impossible'); }
    finally { setPubBusy(false); }
  };
  const toggleVisible = async (mat, val) => {
    setVisBusy(true);
    try {
      await api.post('/social/rattachement', { matricule: mat, paie_visible: val });
      toast.success(val ? "L'indé peut désormais voir sa paie" : 'Visibilité coupée');
      await q.refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Mise à jour impossible'); }
    finally { setVisBusy(false); }
  };

  const frozen = pubStatus === 'frozen' || pubStatus === 'published';
  const applyOverride = async (payload) => {
    if (frozen) { toast.error("Paie figée : défigez-la d'abord pour arbitrer."); return; }
    try {
      await api.post('/social/paie-indes/override', { ym, association: assoc, ...payload });
      toast.success('Arbitrage enregistré');
      await q.refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Modification impossible'); }
  };
  const clearOverride = async (payload) => {
    if (frozen) { toast.error("Paie figée : défigez-la d'abord."); return; }
    try {
      await api.post('/social/paie-indes/override/clear', { ym, association: assoc, ...payload });
      toast.success('Arbitrage réinitialisé');
      await q.refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Réinitialisation impossible'); }
  };

  const addBonus = async (payload) => {
    try {
      const { data } = await api.post('/social/paie-indes/bonus', { ym, association: assoc, ...payload });
      const tl = (LINE_TYPE_META[payload.categorie] || {}).label || 'Ligne';
      toast.success(data?.resynced
        ? `${tl} ajoutée · relevé indé & Coût engagé (REX) mis à jour`
        : `${tl} ajoutée`);
      await Promise.all([q.refetch(), pubQ.refetch()]);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Ajout impossible'); }
  };
  const updateBonus = async (payload) => {
    try {
      const { data } = await api.post('/social/paie-indes/bonus/update', payload);
      toast.success(data?.resynced ? 'Ligne modifiée · relevé & REX mis à jour' : 'Ligne modifiée');
      await Promise.all([q.refetch(), pubQ.refetch()]);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Modification impossible'); }
  };
  const deleteBonus = async (payload) => {
    try {
      const { data } = await api.post('/social/paie-indes/bonus/delete', payload);
      toast.success(data?.resynced ? 'Ligne supprimée · relevé & REX mis à jour' : 'Ligne supprimée');
      await Promise.all([q.refetch(), pubQ.refetch()]);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Suppression impossible'); }
  };
  const setRate = async (payload) => {
    try {
      await api.post('/social/paie-indes/rate-override', payload);
      toast.success('Plafond €/BS appliqué · la valeur calculée reste conservée par-dessous');
      await Promise.all([q.refetch(), pubQ.refetch()]);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Modification impossible'); }
  };
  const clearRate = async (payload) => {
    try {
      await api.post('/social/paie-indes/rate-override/clear', payload);
      toast.success('Valeur calculée rétablie (plafond retiré)');
      await Promise.all([q.refetch(), pubQ.refetch()]);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Réinitialisation impossible'); }
  };
  const addCoord = async (payload) => {
    try {
      await api.post('/social/paie-indes/coordination', payload);
      toast.success('Recruteur rattaché · commission recalculée');
      await Promise.all([q.refetch(), pubQ.refetch()]);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Rattachement impossible'); }
  };
  const delCoord = async (payload) => {
    try {
      await api.post('/social/paie-indes/coordination/delete', payload);
      toast.success('Recruteur détaché · commission recalculée');
      await Promise.all([q.refetch(), pubQ.refetch()]);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Détachement impossible'); }
  };

  const activeIndes = useMemo(
    () => (d?.indes || []).filter((r) => r.total_enregistres > 0 || r.total_montant > 0 || r.forecast > 0),
    [d],
  );

  const t = d?.totaux || {};
  const consolide = d?.has_file;

  return (
    <div className="space-y-5" data-testid="social-paie-view">
      {/* meta strip */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <Badge className="gap-1 border-transparent" style={{ background: consolide ? 'var(--tenant-accent-050)' : 'transparent', color: 'var(--tenant-accent-600)' }} data-testid="paie-mode-badge">
            {consolide ? <><FileCheck2 className="h-3.5 w-3.5" /> Consolidé (fichier association)</> : <><Info className="h-3.5 w-3.5" /> Provisoire — en attente du fichier association</>}
          </Badge>
          <Badge variant="secondary" data-testid="paie-assoc-badge">Association : {d?.association || assoc}</Badge>
          <Badge variant="secondary" className="first-letter:uppercase" data-testid="paie-paymonth-badge">Paie de {labelYm(ym)}</Badge>
          {d?.period?.prelevement_date && (
            <Badge variant="outline" className="gap-1" data-testid="paie-prelevement-badge"><CalendarCheck2 className="h-3.5 w-3.5" /> Prélèvement le {fmtDateFr(d.period.prelevement_date)}</Badge>
          )}
          <Badge variant="outline">Provisoire = Approuvé ∩ IBAN</Badge>
        </div>
        <Button variant="outline" className="gap-2 rounded-xl" onClick={exportXlsx} data-testid="paie-export-btn">
          <Download className="h-4 w-4" /> Exporter le relevé (Excel)
        </Button>
      </div>

      {/* Période de paie AUTOMATIQUE (semaines lun→dim) */}
      <Card className="p-5" data-testid="paie-period-card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl accent-bg-100"><CalendarCheck2 className="h-5 w-5 accent-text" /></div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-display text-sm font-semibold">Période de paie automatique</h3>
                <Badge variant={d?.period?.custom ? 'outline' : 'secondary'} className="gap-1 text-[10px]" data-testid="paie-period-badge">
                  {d?.period?.custom ? 'Réglage manuel' : 'Calcul automatique'}
                </Badge>
              </div>
              <p className="mt-0.5 max-w-2xl text-xs text-muted-foreground">
                Choisissez simplement le mois de paie (sélecteur en haut). La période (semaines lun→dim) est calculée d'après la livraison hebdo et le prélèvement — plus besoin de saisir les dates.
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                <Badge variant="outline" className="gap-1" data-testid="paie-period-range">
                  <CalendarRange className="h-3.5 w-3.5" />
                  {d?.period?.start && d?.period?.end ? `${fmtDateFr(d.period.start)} → ${fmtDateFr(d.period.end)}` : '—'}
                </Badge>
                <Badge variant="secondary" data-testid="paie-period-weeks">{d?.period?.nb_semaines || 0} semaines</Badge>
                {d?.period?.delivery_last_week && (
                  <Badge variant="outline" className="gap-1" data-testid="paie-period-delivery"><Send className="h-3.5 w-3.5" /> Livraison dernière semaine le {fmtDateFr(d.period.delivery_last_week)}</Badge>
                )}
                {d?.period?.prelevement_date && (
                  <Badge variant="outline" className="gap-1" data-testid="paie-period-prelevement"><Banknote className="h-3.5 w-3.5" /> Prélèvement le {fmtDateFr(d.period.prelevement_date)}</Badge>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Diffusion aux indépendants (figer / publier) */}
      <Card className="p-5" data-testid="paie-publication-card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl accent-bg-100"><Send className="h-5 w-5 accent-text" /></div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-display text-sm font-semibold">Diffusion aux indépendants</h3>
                <Badge className={`gap-1 text-[10px] ${pubStatus === 'published' ? 'text-white' : ''}`}
                  style={pubStatus === 'published' ? accentStyle : undefined}
                  variant={pubStatus === 'published' ? 'default' : (pubStatus === 'frozen' ? 'secondary' : 'outline')}
                  data-testid="paie-pub-status">
                  {pubStatus === 'published' ? <><CheckCircle2 className="h-3 w-3" /> Publié</>
                    : pubStatus === 'frozen' ? <><Snowflake className="h-3 w-3" /> Figé</>
                    : <>Brouillon</>}
                </Badge>
              </div>
              <p className="mt-0.5 max-w-2xl text-xs text-muted-foreground">
                <strong>Figez</strong> la paie pour geler les chiffres (ils ne bougent plus même si vous réimportez), puis <strong>Publiez</strong> : chaque indé autorisé verra son relevé dans son espace Booking. Prévisualisez ci-dessous avant de publier.
              </p>
              {pub?.published_at && (
                <p className="mt-1 text-[11px] text-muted-foreground">Publié le {new Date(pub.published_at).toLocaleString('fr-FR')} · {pub?.nb_indes ?? 0} indés</p>
              )}
              {pubStatus === 'frozen' && pub?.frozen_at && (
                <p className="mt-1 text-[11px] text-muted-foreground">Figé le {new Date(pub.frozen_at).toLocaleString('fr-FR')} — non encore publié</p>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {pubStatus === 'draft' && (
              <Button className="gap-2 rounded-xl" variant="outline" onClick={() => doPub('freeze', 'Paie figée')} disabled={pubBusy} data-testid="paie-freeze-btn">
                <Snowflake className="h-4 w-4" /> Figer la paie
              </Button>
            )}
            {pubStatus === 'frozen' && (
              <Button variant="ghost" className="gap-2 rounded-xl text-muted-foreground" onClick={() => doPub('unfreeze', 'Paie défigée')} disabled={pubBusy} data-testid="paie-unfreeze-btn">
                <Undo2 className="h-4 w-4" /> Défiger
              </Button>
            )}
            {pubStatus !== 'published' ? (
              <Button className="gap-2 rounded-xl text-white" style={accentStyle} onClick={() => doPub('publish', 'Paie publiée aux indés')} disabled={pubBusy} data-testid="paie-publish-btn">
                <Send className="h-4 w-4" /> {pubBusy ? '…' : 'Publier aux indés'}
              </Button>
            ) : (
              <Button variant="outline" className="gap-2 rounded-xl" onClick={() => doPub('unpublish', 'Paie dépubliée')} disabled={pubBusy} data-testid="paie-unpublish-btn">
                <Undo2 className="h-4 w-4" /> Dépublier
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Import fichier association */}
      <Card className="p-5" data-testid="paie-import-card">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl accent-bg-100"><UploadCloud className="h-5 w-5 accent-text" /></div>
            <div>
              <h3 className="font-display text-sm font-semibold">Importer le fichier association — {labelYm(ym)}</h3>
              <p className="mt-0.5 max-w-xl text-xs text-muted-foreground">
                Feuilles « mandats créés », « pré-attrition », « refus ». Rapprochement par Code Recruteur + Date de signature (semaine lun→dim). Import idempotent (ré-importer remplace le mois).
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label className="text-xs">Association</Label>
              <Select value={assoc} onValueChange={setAssoc}>
                <SelectTrigger className="mt-1 w-40 rounded-xl" data-testid="paie-assoc-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="AIF">Amnesty (AIF)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Fichier (.xlsx)</Label>
              <Input type="file" accept=".xlsx,.xlsm" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1 w-64 rounded-xl" data-testid="paie-import-file" />
            </div>
            <Button className="gap-2 rounded-xl text-white" style={accentStyle} onClick={upload} disabled={busy} data-testid="paie-import-btn">
              <UploadCloud className="h-4 w-4" /> {busy ? 'Import…' : 'Importer'}
            </Button>
          </div>
        </div>
        {d?.journal && (
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl bg-muted/40 px-3 py-2 text-[11px] text-muted-foreground" data-testid="paie-journal">
            <span className="font-medium text-foreground">Dernier import :</span>
            <span>{d.journal.counts?.mandats} mandats</span>
            <span>{d.journal.counts?.preattritions} pré-attritions</span>
            <span>{d.journal.counts?.refus} refus</span>
            <span data-testid="paie-journal-codes-indep">{d.journal.counts?.codes_independants ?? 0} codes indés</span>
            <span data-testid="paie-journal-codes-salarie">{d.journal.counts?.codes_salaries ?? 0} codes salariés</span>
            <span className={(d.journal.nb_unmatched || 0) > 0 ? 'font-medium text-amber-600 dark:text-amber-400' : ''} data-testid="paie-journal-unmatched">
              {d.journal.nb_unmatched || 0} codes non rattachés
            </span>
            <span>{d.journal.imported_at ? new Date(d.journal.imported_at).toLocaleString('fr-FR') : ''}</span>
          </div>
        )}
      </Card>

      {q.isLoading && !d ? (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>
      ) : !d ? (
        <EmptyState title="Paie indés indisponible" hint="Aucune donnée sur ce mois." />
      ) : (
        <>
          {/* Total ESTIMÉ du mois — somme de tous les indés (payable + provisoire en attente) */}
          <Card className="p-5" data-testid="paie-total-estime-card">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl accent-bg-100"><Sigma className="h-5 w-5 accent-text" /></div>
                <div>
                  <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Total estimé du mois — tous les indés</div>
                  <div className="kpi-value text-2xl tabular accent-text" data-testid="paie-total-estime-value">{fmtEur(t.montant_estime_total, 2)}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {t.nb_actifs} indés actifs · {fmtEur(t.montant_total, 2)} payable + {fmtEur(t.provisoire_estime_total, 2)} en attente (estimé)
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className={`kpi-value text-lg tabular ${(t.ecart_estime_total || 0) < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`} data-testid="paie-total-estime-ecart">{fmtEur(t.ecart_estime_total, 2)}</div>
                <div className="text-[11px] text-muted-foreground">vs forecast ({fmtEur(t.forecast_total, 2)})</div>
              </div>
            </div>
          </Card>

          {/* KPIs synthèse */}
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <Kpi icon={Banknote} label="Paie réelle consolidée" value={fmtEur(t.montant_total, 2)}
              sub={`${t.nb_semaines_consolidees}/${t.nb_semaines} semaines · payable`} testid="paie-kpi-payable" />
            <Kpi icon={Clock} label="Provisoire en attente" value={fmtEur(t.provisoire_estime_total, 2)}
              sub={`${t.nb_semaines_en_attente} semaine(s) sans fichier`} testid="paie-kpi-provisoire" />
            <Kpi icon={Target} label="Forecast P&L" value={fmtEur(t.forecast_total, 2)}
              sub="BS bruts × taux bloqué" testid="paie-kpi-forecast" />
            <Kpi icon={Scale} label="Écart vs Forecast"
              value={<span className={t.ecart_total < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}>{fmtEur(t.ecart_total, 2)}</span>}
              sub="réel consolidé − forecast" testid="paie-kpi-ecart" />
          </div>

          {/* Panneau d'alertes */}
          <Card className="p-5" data-testid="paie-alertes-card">
            <SectionTitle icon={AlertTriangle} title="Écarts Evergiving ↔ association"
              sub="livrés (Approuvé ∩ IBAN) vs mandats du fichier — choisissez la source qui fait foi, ligne par ligne"
              right={<div className="flex items-center gap-2">
                {d.nb_arbitrages > 0 && <Badge variant="secondary" className="gap-1 text-[10px]" data-testid="paie-arbitrages-count"><CheckCircle2 className="h-3 w-3" /> {d.nb_arbitrages} arbitré(s)</Badge>}
                <Badge variant={d.nb_alertes ? 'outline' : 'secondary'} className={`gap-1 ${d.nb_alertes ? 'border-amber-300 text-amber-700 dark:text-amber-300' : ''}`} data-testid="paie-alertes-count">
                  {d.nb_alertes ? <><CircleAlert className="h-3.5 w-3.5" /> {d.nb_alertes} à arbitrer</> : <><CheckCircle2 className="h-3.5 w-3.5" /> Rien à arbitrer</>}
                </Badge>
              </div>} />
            {frozen && d.nb_ecarts_total > 0 && (
              <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-1.5 text-[11px] text-amber-700 dark:border-amber-900 dark:bg-amber-950/20 dark:text-amber-300" data-testid="paie-alertes-frozen-hint">
                Paie figée — <button type="button" className="font-medium underline" onClick={() => doPub('unfreeze', 'Paie défigée')}>défigez-la</button> pour arbitrer les écarts.
              </p>
            )}
            {d.nb_ecarts_total ? (
              <div className="mt-3 overflow-x-auto">
                <CardTable className="w-full min-w-[720px] text-sm">
                  <thead className="text-xs text-muted-foreground">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-medium">Indé</th>
                      <th className="px-2 py-1.5 text-left font-medium">Semaine</th>
                      <th className="px-2 py-1.5 text-right font-medium">Livrés (EG)</th>
                      <th className="px-2 py-1.5 text-right font-medium">Mandats (fichier)</th>
                      <th className="px-2 py-1.5 text-right font-medium">Δ</th>
                      <th className="px-2 py-1.5 text-center font-medium">Qui fait foi ?</th>
                      <th className="px-2 py-1.5 text-right font-medium">Net effectif</th>
                      <th className="px-2 py-1.5" />
                    </tr>
                  </thead>
                  <tbody>
                    {d.alertes.map((a, i) => (
                      <tr key={`${a.matricule}-${a.monday}-${i}`} className={`border-t ${a.arbitre ? 'bg-emerald-50/40 dark:bg-emerald-950/10' : ''}`} data-testid={`paie-alerte-${a.matricule}-${a.monday}`}>
                        <td className="px-2 py-1.5"><span className="font-medium">{a.prenom} {a.nom}</span> <span className="text-[11px] text-muted-foreground">{a.matricule}</span></td>
                        <td className="px-2 py-1.5">{a.label}</td>
                        <td className="px-2 py-1.5 text-right tabular">{fmtInt(a.evergiving_livres)}</td>
                        <td className="px-2 py-1.5 text-right tabular">{fmtInt(a.mandats_crees)}</td>
                        <td className="px-2 py-1.5 text-right tabular font-semibold text-amber-700 dark:text-amber-400">{a.delta > 0 ? '+' : ''}{fmtInt(a.delta)}</td>
                        <td className="px-2 py-1.5 text-center">
                          <SourceFaitFoiToggle value={a.mandats_source} disabled={frozen}
                            onPick={(src) => applyOverride({ matricule: a.matricule, monday: a.monday, mandats_source: src })}
                            testid={`paie-ecart-src-${a.matricule}-${a.monday}`} />
                        </td>
                        <td className="px-2 py-1.5 text-right tabular font-semibold">
                          {fmtInt(a.net_effectif)} <span className="text-[10px] font-normal text-muted-foreground">· {fmtEur(a.montant_effectif, 0)}</span>
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          {a.arbitre && !frozen && (
                            <button type="button" title="Réinitialiser (retour au fichier)"
                              onClick={() => clearOverride({ matricule: a.matricule, monday: a.monday })}
                              className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground" data-testid={`paie-ecart-reset-${a.matricule}-${a.monday}`}>
                              <Undo2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </CardTable>
                <p className="mt-2 text-[11px] text-muted-foreground">
                  « Fichier » = mandats validés par l'association (défaut). « Evergiving » = bulletins livrés (Approuvé ∩ IBAN). Le choix agit sur le net payable —
                  le fichier importé n'est jamais modifié, et l'indé ne voit que le chiffre effectif.
                </p>
              </div>
            ) : (
              <p className="mt-3 text-xs text-muted-foreground">
                Contrôle actif : aucun écart entre les bulletins livrés et les mandats confirmés. L'entonnoir ci-dessous illustre la chaîne de contrôle.
              </p>
            )}
          </Card>

          {/* Entonnoir de référence */}
          <ReferenceFunnel inde={activeIndes[0]} />

          {/* Relevés type facture par indé */}
          <div className="space-y-3">
            <SectionTitle icon={ReceiptText} title="Relevés par indépendant"
              sub={`${activeIndes.length} indés actifs · barème taux plein rétroactif`} />
            {activeIndes.length === 0
              ? <EmptyState title="Aucune production indé sur ce mois" hint="Importez le fichier association ou vérifiez la période." />
              : activeIndes.map((r) => <IndeStatement key={r.matricule} inde={r} onToggleVisible={toggleVisible} onPreview={setPreviewMat} visBusy={visBusy}
                  editable frozen={frozen} onOverride={applyOverride} onClearOverride={clearOverride}
                  onAddBonus={addBonus} onUpdateBonus={updateBonus} onDeleteBonus={deleteBonus}
                  onSetRate={setRate} onClearRate={clearRate} onAddCoord={addCoord} onDelCoord={delCoord} />)}
          </div>
        </>
      )}
      <IndePreviewDialog matricule={previewMat} ym={ym} association={assoc}
        open={!!previewMat} onClose={() => setPreviewMat(null)} />
    </div>
  );
}



// --------------------------------------------------------------------------- //
//  P&L toutes populations (Lot 1)                                              //
// --------------------------------------------------------------------------- //
// ---- P&L multi-périodes : helpers & contrôles ----
const _pad = (n) => String(n).padStart(2, '0');
function isoAdd(iso, days) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return `${dt.getUTCFullYear()}-${_pad(dt.getUTCMonth() + 1)}-${_pad(dt.getUTCDate())}`;
}
function mondayOf(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const wd = (dt.getUTCDay() + 6) % 7;
  dt.setUTCDate(dt.getUTCDate() - wd);
  return `${dt.getUTCFullYear()}-${_pad(dt.getUTCMonth() + 1)}-${_pad(dt.getUTCDate())}`;
}
function addMonth(ym7, delta) {
  let y = +ym7.slice(0, 4);
  let m = +ym7.slice(5, 7) + delta;
  while (m > 12) { m -= 12; y += 1; }
  while (m < 1) { m += 12; y -= 1; }
  return `${y}-${_pad(m)}`;
}
function pnlBounds(gran, anchor, cs, ce) {
  const y = +anchor.slice(0, 4);
  const m = +anchor.slice(5, 7);
  if (gran === 'jour') return { start: anchor, end: anchor };
  if (gran === 'semaine') { const s = mondayOf(anchor); return { start: s, end: isoAdd(s, 6) }; }
  if (gran === 'mois') {
    const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
    return { start: `${anchor.slice(0, 7)}-01`, end: `${anchor.slice(0, 7)}-${_pad(last)}` };
  }
  if (gran === 'annee') return { start: `${y}-01-01`, end: `${y}-12-31` };
  return { start: cs, end: ce };
}

const GRANS = [
  { k: 'jour', label: 'Jour' }, { k: 'semaine', label: 'Semaine' },
  { k: 'mois', label: 'Mois' }, { k: 'annee', label: 'Année' },
  { k: 'custom', label: 'Plage' },
];

function PnlPeriodControls({ gran, setGran, anchor, setAnchor, cs, setCs, ce, setCe, nav, label, idPrefix = 'pnl' }) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-2xl border bg-muted/20 p-2" data-testid={`${idPrefix}-period-controls`}>
      <Select value={gran} onValueChange={setGran}>
        <SelectTrigger className="h-9 w-32 rounded-xl" data-testid={`${idPrefix}-granularity`}><SelectValue /></SelectTrigger>
        <SelectContent>
          {GRANS.map((g) => <SelectItem key={g.k} value={g.k}>{g.label}</SelectItem>)}
        </SelectContent>
      </Select>
      {gran !== 'custom' ? (
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-9 w-9 rounded-xl" onClick={() => nav(-1)} data-testid={`${idPrefix}-prev`} aria-label="Période précédente"><ChevronLeft className="h-4 w-4" /></Button>
          {gran === 'annee' ? (
            <Input type="number" value={anchor.slice(0, 4)} onChange={(e) => setAnchor(`${String(e.target.value || '2026').padStart(4, '0')}-01-01`)} className="h-9 w-24 rounded-xl tabular text-center" data-testid={`${idPrefix}-anchor`} />
          ) : gran === 'mois' ? (
            <Input type="month" value={anchor.slice(0, 7)} onChange={(e) => e.target.value && setAnchor(`${e.target.value}-01`)} className="h-9 w-40 rounded-xl tabular" data-testid={`${idPrefix}-anchor`} />
          ) : (
            <Input type="date" value={anchor} onChange={(e) => e.target.value && setAnchor(e.target.value)} className="h-9 w-40 rounded-xl tabular" data-testid={`${idPrefix}-anchor`} />
          )}
          <Button variant="outline" size="icon" className="h-9 w-9 rounded-xl" onClick={() => nav(1)} data-testid={`${idPrefix}-next`} aria-label="Période suivante"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      ) : (
        <div className="flex items-center gap-1">
          <Input type="date" value={cs} onChange={(e) => setCs(e.target.value)} className="h-9 w-40 rounded-xl tabular" data-testid={`${idPrefix}-custom-start`} />
          <span className="text-xs text-muted-foreground">→</span>
          <Input type="date" value={ce} onChange={(e) => setCe(e.target.value)} className="h-9 w-40 rounded-xl tabular" data-testid={`${idPrefix}-custom-end`} />
        </div>
      )}
      <Badge variant="secondary" className="ml-auto gap-1 first-letter:uppercase" data-testid={`${idPrefix}-period-label`}><CalendarRange className="h-3.5 w-3.5" /> {label}</Badge>
    </div>
  );
}

function PnlView({ ym }) {
  const [gran, setGran] = useState('mois');
  const [anchor, setAnchor] = useState(`${ym}-01`);
  const [cs, setCs] = useState(`${ym}-01`);
  const [ce, setCe] = useState(`${ym}-15`);
  const { start, end } = pnlBounds(gran, anchor, cs, ce);
  const isMonth = gran === 'mois';
  const monthQ = useGet('/social/pnl', { ym: anchor.slice(0, 7) }, isMonth);
  const rangeQ = useGet('/social/pnl-range', { start, end, granularity: gran }, !isMonth);
  const q = isMonth ? monthQ : rangeQ;
  const d = q.data;
  const nav = (dir) => {
    if (gran === 'jour') setAnchor(isoAdd(anchor, dir));
    else if (gran === 'semaine') setAnchor(isoAdd(anchor, dir * 7));
    else if (gran === 'mois') setAnchor(`${addMonth(anchor.slice(0, 7), dir)}-01`);
    else if (gran === 'annee') setAnchor(`${+anchor.slice(0, 4) + dir}-01-01`);
  };
  const periodLabel = d?.label || (isMonth ? labelYm(anchor.slice(0, 7)) : `${start} → ${end}`);
  const showProjection = gran !== 'jour';

  const totalCost = (d?.cout_toutes_populations || 0) + (d?.structure || 0);
  const loss = (d?.resultat_net || 0) < 0;
  const proj = d?.projection || {};
  const salTotal = d?.cout_salaries || 0;
  const forfaitTotal = d?.cout_inde_forfait || 0;
  const bulletinRows = d?.inde_bulletin || [];
  const fcTotal = d?.cout_inde_bulletin_forecast != null ? d.cout_inde_bulletin_forecast : bulletinRows.reduce((s, r) => s + (r.forecast || 0), 0);
  const reelTotal = d?.cout_inde_bulletin_reel != null ? d.cout_inde_bulletin_reel : bulletinRows.reduce((s, r) => s + (r.reel || 0), 0);
  const ecartTotal = fcTotal - reelTotal;

  const controls = (
    <PnlPeriodControls gran={gran} setGran={setGran} anchor={anchor} setAnchor={setAnchor}
      cs={cs} setCs={setCs} ce={ce} setCe={setCe} nav={nav} label={periodLabel} />
  );

  if (!d) {
    return (
      <div className="space-y-4" data-testid="social-pnl-view">
        {controls}
        {q.isLoading
          ? (<><div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div><Skeleton className="h-40 rounded-2xl" /></>)
          : <EmptyState title="P&L indisponible sur cette période" hint="Aucune donnée BS/heures sur la plage sélectionnée." />}
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="social-pnl-view">
      {controls}
      {/* meta strip */}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Badge variant="secondary" data-testid="pnl-bs-source">BS : {d.bs_source_pnl === 'reference' ? 'Référence' : d.bs_source_pnl === 'evergiving' ? 'Terrain (Evergiving live)' : d.bs_source_pnl}</Badge>
        <Badge variant="secondary">Mode indé (totaux) : {d.mode_inde_totaux === 'forecast' ? 'Forecast' : 'Réel contrat'}</Badge>
        <Badge variant="outline" className="gap-1" data-testid="pnl-avancement">
          <CalendarClock className="h-3.5 w-3.5" /> Réalisé à date : {d.jours_ecoules}/{d.jours_attendus} j ({fmtPct(d.taux_avancement, 0)})
        </Badge>
        <Badge variant="outline" className="gap-1"><Scale className="h-3.5 w-3.5" /> Toutes populations</Badge>
      </div>

      {/* Top KPI — the 7 « juge de paix » figures live here */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6" data-testid="pnl-kpi-grid">
        <Kpi icon={Gift} label="BS bruts" value={fmtInt(d.bs_bruts)} testid="pnl-kpi-bs" />
        <Kpi icon={Coins} label="CA net" value={fmtEur(d.ca_net, 2)} sub={`${fmtEur(d.ca_par_bs, 2)} / BS`} testid="pnl-kpi-ca" />
        <Kpi icon={ReceiptText} label="Coût total" value={fmtEur(totalCost, 2)} sub="réalisé à date" testid="pnl-kpi-cout" />
        <Card className="p-4" data-testid="pnl-kpi-resultat">
          <div className="flex items-center gap-2 text-muted-foreground">
            {loss ? <TrendingDown className="h-4 w-4 text-amber-600" /> : <TrendingUp className="h-4 w-4 accent-text" />}
            <span className="text-xs font-medium">Résultat net estimé</span>
          </div>
          <div className={`mt-2 kpi-value text-xl tabular ${loss ? 'text-amber-600' : 'accent-text'}`}>{fmtEur(d.resultat_net, 2)}</div>
          <div className="mt-1 text-[11px] text-muted-foreground">{loss ? 'perte estimée' : 'bénéfice estimé'}</div>
        </Card>
        <Kpi icon={PiggyBank} label="Dividendes estimés" value={fmtEur(d.dividendes, 2)} sub="MAX(0 ; rés. ×0,75×0,6×0,685)" testid="pnl-kpi-dividendes" />
        <Kpi icon={TrendingUp} label="Coût / acquisition" value={fmtEur(d.cout_acquisition_all, 2)} sub="toutes populations" testid="pnl-kpi-cout-acq" />
      </div>

      {/* 4 population cost cards */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <PopCostCard metaKey="salaries" cost={d.cout_salaries} count={d.nb_salaries} total={totalCost} prefix="pnl-pop" />
        <PopCostCard metaKey="forfait" cost={d.cout_inde_forfait} count={d.nb_inde_forfait} total={totalCost} prefix="pnl-pop" />
        <PopCostCard metaKey="bulletin" cost={d.cout_inde_bulletin} count={d.nb_inde_bulletin} total={totalCost} prefix="pnl-pop" />
        <PopCostCard metaKey="structure" cost={d.structure} total={totalCost} prefix="pnl-pop" />
      </div>

      {/* Réalisé à date vs Projection fin de période */}
      {showProjection && (
      <Card className="p-5" data-testid="pnl-projection">
        <SectionTitle icon={CalendarClock} title="Réalisé à date & projection fin de période"
          sub={`${proj.jours_ecoules || 0} / ${proj.jours_activite || 0} jours d'activité`} />
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="rounded-2xl border bg-muted/30 p-4" data-testid="pnl-realise">
            <div className="mb-3 flex items-center gap-2 text-xs font-semibold text-muted-foreground">
              <BarChart3 className="h-4 w-4" /> RÉALISÉ À DATE ({proj.jours_ecoules || 0} j)
            </div>
            <dl className="space-y-2 text-sm tabular">
              <div className="flex items-center justify-between"><dt className="text-muted-foreground">CA net</dt><dd className="font-medium">{fmtEur(d.ca_net, 2)}</dd></div>
              <div className="flex items-center justify-between"><dt className="text-muted-foreground">Coût total</dt><dd className="font-medium">{fmtEur(totalCost, 2)}</dd></div>
              <Separator />
              <div className="flex items-center justify-between"><dt className="font-medium">Résultat net</dt><dd className={`font-semibold ${loss ? 'text-amber-600' : 'accent-text'}`}>{fmtEur(d.resultat_net, 2)}</dd></div>
            </dl>
          </div>
          <div className="rounded-2xl border p-4" style={{ borderColor: 'var(--tenant-accent-200)', background: 'var(--tenant-accent-050)' }} data-testid="pnl-projection-values">
            <div className="mb-3 flex items-center gap-2 text-xs font-semibold accent-text">
              <CalendarClock className="h-4 w-4" /> PROJECTION FIN DE PÉRIODE ({proj.jours_activite || 0} j)
            </div>
            <dl className="space-y-2 text-sm tabular">
              <div className="flex items-center justify-between"><dt className="text-muted-foreground">CA net projeté</dt><dd className="font-medium" data-testid="pnl-proj-ca">{fmtEur(proj.ca_net, 2)}</dd></div>
              <div className="flex items-center justify-between"><dt className="text-muted-foreground">Coût total projeté</dt><dd className="font-medium" data-testid="pnl-proj-cout">{fmtEur(proj.cout, 2)}</dd></div>
              <Separator />
              <div className="flex items-center justify-between"><dt className="font-medium">Résultat net projeté</dt><dd className={`font-semibold ${(proj.resultat || 0) < 0 ? 'text-amber-600' : 'accent-text'}`} data-testid="pnl-proj-resultat">{fmtEur(proj.resultat, 2)}</dd></div>
            </dl>
          </div>
        </div>
        <p className="mt-3 text-[11px] text-muted-foreground">
          Projection = moyenne journalière des jours d'activité écoulés × jours d'activité de la période (coûts fixes structure &amp; forfaits comptés pleins).
        </p>
      </Card>
      )}

      {/* Salariés (CDD/CDI) */}
      <div className="space-y-2">
        <SectionTitle icon={Users} title="Salariés — coût bloc « CT Personnel »" sub={`${d.nb_salaries} collaborateur(s) · CDI = sans congés/précarité`} />
        <TableWrap>
          <CardTable className="w-full min-w-[880px] text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 font-medium">Collaborateur</th>
                <th className="px-2 py-2 text-center font-medium">Statut</th>
                <th className="px-2 py-2 text-right font-medium">Heures</th>
                <th className="px-2 py-2 text-right font-medium">Taux</th>
                <th className="px-2 py-2 text-right font-medium">Brut</th>
                <th className="px-2 py-2 text-right font-medium">Prime</th>
                <th className="px-2 py-2 text-right font-medium">Transport</th>
                <th className="px-2 py-2 text-right font-medium">TR</th>
                <th className="px-3 py-2 text-right font-medium">Coût P&L</th>
              </tr>
            </thead>
            <tbody className="tabular">
              {d.salaries.map((r) => (
                <tr key={r.matricule} className="border-b last:border-0 hover:bg-muted/40" data-testid={`pnl-salarie-row-${r.matricule}`}>
                  <td className="px-3 py-2"><span className="font-medium">{r.prenom} {r.nom}</span> <span className="text-[11px] text-muted-foreground">{r.matricule}</span></td>
                  <td className="px-2 py-2 text-center"><StatutEmploiBadge statut={r.statut_emploi} /></td>
                  <td className="px-2 py-2 text-right">{fmtNum(r.heures, 2)}</td>
                  <td className="px-2 py-2 text-right">{fmtEur(r.heures ? r.brut / r.heures : 0, 2)}</td>
                  <td className="px-2 py-2 text-right">{fmtEur(r.brut, 2)}</td>
                  <td className="px-2 py-2 text-right">{r.prime ? fmtEur(r.prime, 2) : '—'}</td>
                  <td className="px-2 py-2 text-right">{r.transport ? fmtEur(r.transport, 2) : '—'}</td>
                  <td className="px-2 py-2 text-right">{fmtInt(r.nb_tr)}</td>
                  <td className="px-3 py-2 text-right font-semibold accent-text">{fmtEur(r.cout_pnl, 2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 bg-muted/30 font-semibold tabular" data-testid="pnl-salaries-total">
                <td className="px-3 py-2" colSpan={8}>Total salariés ({d.nb_salaries})</td>
                <td className="px-3 py-2 text-right accent-text">{fmtEur(salTotal, 2)}</td>
              </tr>
            </tfoot>
          </CardTable>
        </TableWrap>
      </div>

      {/* Indés au forfait */}
      <div className="space-y-2">
        <SectionTitle icon={Briefcase} title="Indés au forfait" sub="montant mensuel lissé sur les jours d'activité" />
        <TableWrap>
          <CardTable className="w-full min-w-[620px] text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 font-medium">Indépendant</th>
                <th className="px-2 py-2 text-right font-medium">Montant mensuel</th>
                <th className="px-2 py-2 text-right font-medium">BS bruts</th>
                <th className="px-2 py-2 text-right font-medium">Coût / jour</th>
                <th className="px-2 py-2 text-center font-medium">Statut</th>
                <th className="px-3 py-2 text-right font-medium">Coût réalisé</th>
              </tr>
            </thead>
            <tbody className="tabular">
              {d.inde_forfait.map((r) => (
                <tr key={r.matricule} className="border-b last:border-0 hover:bg-muted/40" data-testid={`pnl-forfait-row-${r.matricule}`}>
                  <td className="px-3 py-2"><span className="font-medium">{r.prenom} {r.nom}</span> <span className="text-[11px] text-muted-foreground">{r.matricule}</span></td>
                  <td className="px-2 py-2 text-right">{fmtEur(r.montant, 2)}</td>
                  <td className="px-2 py-2 text-right">{fmtInt(r.bs_bruts)}</td>
                  <td className="px-2 py-2 text-right">{fmtEur(r.cout_jour, 2)}</td>
                  <td className="px-2 py-2 text-center"><Badge variant={r.actif ? 'secondary' : 'outline'} className="text-[10px]">{r.actif ? 'actif' : r.statut}</Badge></td>
                  <td className="px-3 py-2 text-right font-semibold accent-text">{fmtEur(r.cout, 2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 bg-muted/30 font-semibold tabular" data-testid="pnl-forfait-total">
                <td className="px-3 py-2" colSpan={5}>Total indés forfait ({d.nb_inde_forfait})</td>
                <td className="px-3 py-2 text-right accent-text">{fmtEur(forfaitTotal, 2)}</td>
              </tr>
            </tfoot>
          </CardTable>
        </TableWrap>
      </div>

      {/* Indés au bulletin — 2 modes côte à côte + écart */}
      <div className="space-y-2">
        <SectionTitle icon={ArrowRightLeft} title="Indés au bulletin — Forecast vs Réel contrat"
          sub={`mode retenu dans les totaux : ${d.mode_inde_totaux === 'forecast' ? 'Forecast (taux bloqué)' : 'Réel contrat'}`} />
        <TableWrap>
          <CardTable className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 font-medium">Indépendant</th>
                <th className="px-2 py-2 text-right font-medium">Taux</th>
                <th className="px-2 py-2 text-right font-medium">BS bruts</th>
                <th className="px-2 py-2 text-right font-medium">BS nets</th>
                <th className="px-2 py-2 text-right font-medium">Forecast</th>
                <th className="px-2 py-2 text-right font-medium">Réel contrat</th>
                <th className="px-3 py-2 text-right font-medium">Écart</th>
              </tr>
            </thead>
            <tbody className="tabular">
              {d.inde_bulletin.map((r) => (
                <tr key={r.matricule} className="border-b last:border-0 hover:bg-muted/40" data-testid={`pnl-bulletin-row-${r.matricule}`}>
                  <td className="px-3 py-2"><span className="font-medium">{r.prenom} {r.nom}</span> <span className="text-[11px] text-muted-foreground">{r.matricule}</span></td>
                  <td className="px-2 py-2 text-right">{fmtEur(r.taux, 2)}</td>
                  <td className="px-2 py-2 text-right">{fmtInt(r.bs_bruts)}</td>
                  <td className="px-2 py-2 text-right">{fmtInt(r.bs_nets)}</td>
                  <td className="px-2 py-2 text-right font-medium">{fmtEur(r.forecast, 2)}</td>
                  <td className="px-2 py-2 text-right text-muted-foreground">{fmtEur(r.reel, 2)}</td>
                  <td className="px-3 py-2 text-right">{fmtEur(r.ecart, 2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 bg-muted/30 font-semibold tabular" data-testid="pnl-bulletin-total">
                <td className="px-3 py-2" colSpan={4}>Total indés bulletin ({d.nb_inde_bulletin})</td>
                <td className="px-2 py-2 text-right accent-text">{fmtEur(fcTotal, 2)}</td>
                <td className="px-2 py-2 text-right text-muted-foreground">{fmtEur(reelTotal, 2)}</td>
                <td className="px-3 py-2 text-right">{fmtEur(ecartTotal, 2)}</td>
              </tr>
            </tfoot>
          </CardTable>
        </TableWrap>
      </div>

      {/* Structure */}
      <Card className="p-5" data-testid="pnl-structure-detail">
        <SectionTitle icon={Landmark} title="Structure (mensuel, lissé)" sub="paramétrable & versionnée" />
        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div className="rounded-2xl border bg-muted/30 p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground"><Building2 className="h-4 w-4 accent-text" /> Holding ECH GROUP</div>
            <div className="mt-2 kpi-value text-lg tabular">{fmtEur(d.holding, 2)}</div>
            <div className="mt-1 text-[10px] text-muted-foreground">mensuel</div>
          </div>
          <div className="rounded-2xl border bg-muted/30 p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground"><ReceiptText className="h-4 w-4 accent-text" /> Frais généraux</div>
            <div className="mt-2 kpi-value text-lg tabular">{fmtEur(d.frais_generaux, 2)}</div>
            <div className="mt-1 text-[10px] text-muted-foreground">mensuel</div>
          </div>
          <div className="rounded-2xl border bg-muted/30 p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground"><Landmark className="h-4 w-4 accent-text" /> Total mensuel</div>
            <div className="mt-2 kpi-value text-lg tabular">{fmtEur(d.structure_mensuel, 2)}</div>
            <div className="mt-1 text-[10px] text-muted-foreground">plein</div>
          </div>
          <div className="rounded-2xl border p-4" style={{ borderColor: 'var(--tenant-accent-200)', background: 'var(--tenant-accent-050)' }}>
            <div className="flex items-center gap-2 text-xs accent-text"><CalendarClock className="h-4 w-4" /> Quote-part réalisée</div>
            <div className="mt-2 kpi-value text-lg tabular accent-text">{fmtEur(d.structure, 2)}</div>
            <div className="mt-1 text-[10px] text-muted-foreground">{d.jours_ecoules}/{d.jours_attendus} j</div>
          </div>
        </div>
      </Card>

      {/* Détail par mois (Vue Année / multi-mois) */}
      {d.months?.length > 1 && (
        <div className="space-y-2" data-testid="pnl-months-detail">
          <SectionTitle icon={BarChart3} title="Détail par mois" sub={`${d.months.length} mois cumulés`} />
          <TableWrap>
            <CardTable className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="border-b bg-muted/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Mois</th>
                  <th className="px-2 py-2 text-right font-medium">CA net</th>
                  <th className="px-2 py-2 text-right font-medium">Salariés</th>
                  <th className="px-2 py-2 text-right font-medium">Indés forfait</th>
                  <th className="px-2 py-2 text-right font-medium">Indés bulletin</th>
                  <th className="px-2 py-2 text-right font-medium">Structure</th>
                  <th className="px-3 py-2 text-right font-medium">Résultat net</th>
                </tr>
              </thead>
              <tbody className="tabular">
                {d.months.map((mo) => (
                  <tr key={mo.ym} className="border-b last:border-0 hover:bg-muted/40" data-testid={`pnl-month-row-${mo.ym}`}>
                    <td className="px-3 py-2 font-medium capitalize">{mo.label}</td>
                    <td className="px-2 py-2 text-right">{fmtEur(mo.ca_net, 2)}</td>
                    <td className="px-2 py-2 text-right">{fmtEur(mo.cout_salaries, 2)}</td>
                    <td className="px-2 py-2 text-right">{fmtEur(mo.cout_inde_forfait, 2)}</td>
                    <td className="px-2 py-2 text-right">{fmtEur(mo.cout_inde_bulletin, 2)}</td>
                    <td className="px-2 py-2 text-right">{fmtEur(mo.structure, 2)}</td>
                    <td className={`px-3 py-2 text-right font-semibold ${mo.resultat_net < 0 ? 'text-amber-600' : 'accent-text'}`}>{fmtEur(mo.resultat_net, 2)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 bg-muted/30 font-semibold tabular" data-testid="pnl-months-total">
                  <td className="px-3 py-2">Cumul période</td>
                  <td className="px-2 py-2 text-right">{fmtEur(d.ca_net, 2)}</td>
                  <td className="px-2 py-2 text-right">{fmtEur(d.cout_salaries, 2)}</td>
                  <td className="px-2 py-2 text-right">{fmtEur(d.cout_inde_forfait, 2)}</td>
                  <td className="px-2 py-2 text-right">{fmtEur(d.cout_inde_bulletin, 2)}</td>
                  <td className="px-2 py-2 text-right">{fmtEur(d.structure, 2)}</td>
                  <td className={`px-3 py-2 text-right ${loss ? 'text-amber-600' : 'accent-text'}`}>{fmtEur(d.resultat_net, 2)}</td>
                </tr>
              </tfoot>
            </CardTable>
          </TableWrap>
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------- //
//  Rattachement manuel des indés (Lot 1, point 1)                              //
// --------------------------------------------------------------------------- //
// Rattacher / reclasser un code précis — fonctionne même si le code n'est pas
// listé dans « Codes à rattacher » (ex. collaborateur passé indépendant en cours
// d'année, encore vu comme salarié par PREREC).
function ManualRattach({ onSaved }) {
  const [mat, setMat] = useState('');
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [pop, setPop] = useState('independant');
  const [mode, setMode] = useState('bulletin');
  const [montant, setMontant] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const code = mat.trim().toUpperCase();
    if (!code) { toast.error('Saisissez un matricule (ex. AT0342)'); return; }
    if (pop === 'independant' && !mode) { toast.error('Choisissez un mode (forfait/bulletin)'); return; }
    setBusy(true);
    try {
      await api.post('/social/rattachement', {
        matricule: code,
        population: pop,
        mode: pop === 'independant' ? mode : null,
        montant: (pop === 'independant' && montant !== '' && montant != null) ? parseFloat(montant) : null,
        prenom: prenom.trim() || null, nom: nom.trim() || null, statut: 'active',
      });
      toast.success(`Rattachement enregistré : ${code}`);
      setMat(''); setPrenom(''); setNom(''); setMontant('');
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Rattachement impossible'); }
    finally { setBusy(false); }
  };
  const Fld = ({ label, children }) => (
    <div className="flex flex-col gap-1"><span className="text-[10px] font-medium text-muted-foreground">{label}</span>{children}</div>
  );
  return (
    <Card className="p-4" data-testid="rattach-manual-card">
      <SectionTitle icon={UserPlus} title="Rattacher / reclasser un collaborateur précis"
        sub="Ex. un salarié passé indépendant. Saisissez son matricule même s'il n'apparaît pas dans « Codes à rattacher » ci-dessous." />
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <Fld label="Matricule"><Input value={mat} onChange={(e) => setMat(e.target.value)} placeholder="AT0342" className="h-9 w-28 rounded-lg uppercase" data-testid="rattach-manual-mat" /></Fld>
        <Fld label="Prénom"><Input value={prenom} onChange={(e) => setPrenom(e.target.value)} placeholder="Néfis" className="h-9 w-28 rounded-lg" data-testid="rattach-manual-prenom" /></Fld>
        <Fld label="Nom"><Input value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Makhlouf" className="h-9 w-32 rounded-lg" data-testid="rattach-manual-nom" /></Fld>
        <Fld label="Population">
          <Select value={pop} onValueChange={setPop}>
            <SelectTrigger className="h-9 w-32 rounded-lg" data-testid="rattach-manual-pop"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="independant">Indépendant</SelectItem><SelectItem value="salarie">Salarié</SelectItem></SelectContent>
          </Select>
        </Fld>
        {pop === 'independant' && (
          <>
            <Fld label="Mode">
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="h-9 w-28 rounded-lg" data-testid="rattach-manual-mode"><SelectValue placeholder="Mode" /></SelectTrigger>
                <SelectContent><SelectItem value="bulletin">Bulletin</SelectItem><SelectItem value="forfait">Forfait</SelectItem></SelectContent>
              </Select>
            </Fld>
            <Fld label={mode === 'forfait' ? 'Forfait (€/mois)' : 'Taux (€/BS)'}>
              <Input type="number" min="0" step="0.01" value={montant} onChange={(e) => setMontant(e.target.value)} placeholder={mode === 'forfait' ? '€/mois' : '80'} className="h-9 w-28 rounded-lg text-right tabular" data-testid="rattach-manual-montant" />
            </Fld>
          </>
        )}
        <Button className="h-9 gap-1 rounded-lg text-white" style={accentStyle} disabled={busy} onClick={save} data-testid="rattach-manual-save">
          <Save className="h-4 w-4" /> {busy ? '…' : 'Rattacher'}
        </Button>
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">
        La source de vérité reste <strong className="text-foreground">PREREC / Booking</strong> (<code>?population=all</code>).
        Ce rattachement manuel est un <strong className="text-foreground">correctif immédiat</strong> versionné en base : il fait apparaître
        le collaborateur dans « Paie indés » sans attendre la synchro. Astuce : si le collaborateur avait encore des
        <strong className="text-foreground"> heures salarié</strong> ce mois-ci, corrigez aussi la source pour éviter un double comptage au P&amp;L.
      </p>
    </Card>
  );
}

function RattachementView() {
  const qc = useQueryClient();
  const q = useGet('/social/rattachement', {}, true);
  const d = q.data;
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);
  const setD = (mat, k, v) => setDrafts((s) => ({ ...s, [mat]: { ...(s[mat] || {}), [k]: v } }));

  const save = async (mat) => {
    const f = drafts[mat] || {};
    const population = f.population || 'independant';
    if (population === 'independant' && !f.mode) { toast.error('Choisissez un mode (forfait/bulletin)'); return; }
    setSaving(mat);
    try {
      await api.post('/social/rattachement', {
        matricule: mat,
        population,
        mode: population === 'independant' ? f.mode : null,
        montant: (population === 'independant' && f.montant !== '' && f.montant != null) ? parseFloat(f.montant) : null,
        prenom: f.prenom || null, nom: f.nom || null, statut: f.statut || 'active',
      });
      toast.success(`Rattachement enregistré : ${mat}`);
      qc.invalidateQueries({ predicate: (query) => String(query.queryKey?.[0] || '').startsWith('/social') });
    } catch (e) { toast.error('Rattachement impossible'); }
    finally { setSaving(null); }
  };

  if (q.isLoading && !d) return <Skeleton className="h-40 rounded-2xl" />;
  const aRattacher = d?.a_rattacher || [];
  const rat = d?.rattachement || [];

  return (
    <div className="space-y-5" data-testid="social-rattachement-view">
      <div className="rounded-2xl border bg-muted/20 p-3 text-[12px] text-muted-foreground">
        En production, les rémunérations proviennent de <strong className="text-foreground">PREREC&nbsp;<code>?population=all</code></strong>.
        Les indés historiques que PREREC n'expose pas encore sont rattachés ici manuellement
        (population + mode + montant), enregistré et <strong className="text-foreground">versionné en base</strong> (jamais en dur dans le code).
      </div>

      <ManualRattach onSaved={() => qc.invalidateQueries({ predicate: (query) => String(query.queryKey?.[0] || '').startsWith('/social') })} />

      {/* à rattacher */}
      <div className="space-y-2">
        <SectionTitle icon={UserSearch} title="Codes à rattacher"
          sub={`${d?.nb_a_rattacher || 0} code(s) BS sans population identifiée`} />
        {aRattacher.length === 0 ? (
          <EmptyState title="Aucun code à rattacher"
            hint="Toutes les populations présentes dans les BS sont déjà identifiées (PREREC ?population=all ou rattachement manuel)." />
        ) : (
          <TableWrap>
            <CardTable className="w-full min-w-[960px] text-sm">
              <thead>
                <tr className="border-b bg-muted/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Code</th>
                  <th className="px-2 py-2 text-right font-medium">BS bruts</th>
                  <th className="px-2 py-2 font-medium">Prénom / Nom</th>
                  <th className="px-2 py-2 font-medium">Population</th>
                  <th className="px-2 py-2 font-medium">Mode</th>
                  <th className="px-2 py-2 font-medium">Montant / Taux</th>
                  <th className="px-3 py-2 text-right font-medium">Action</th>
                </tr>
              </thead>
              <tbody className="tabular">
                {aRattacher.map((r) => {
                  const f = drafts[r.matricule] || {};
                  const pop = f.population || 'independant';
                  return (
                    <tr key={r.matricule} className="border-b align-top last:border-0" data-testid={`rattach-pending-${r.matricule}`}>
                      <td className="px-3 py-2 font-medium">{r.matricule}</td>
                      <td className="px-2 py-2 text-right">{fmtInt(r.bs_bruts)}</td>
                      <td className="px-2 py-2">
                        <div className="flex gap-1">
                          <Input placeholder="Prénom" value={f.prenom || ''} onChange={(e) => setD(r.matricule, 'prenom', e.target.value)} className="h-8 w-24 rounded-lg" />
                          <Input placeholder="Nom" value={f.nom || ''} onChange={(e) => setD(r.matricule, 'nom', e.target.value)} className="h-8 w-28 rounded-lg" />
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <Select value={pop} onValueChange={(v) => setD(r.matricule, 'population', v)}>
                          <SelectTrigger className="h-8 w-32 rounded-lg" data-testid={`rattach-pop-${r.matricule}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="independant">Indépendant</SelectItem>
                            <SelectItem value="salarie">Salarié</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-2 py-2">
                        {pop === 'independant' ? (
                          <Select value={f.mode || ''} onValueChange={(v) => setD(r.matricule, 'mode', v)}>
                            <SelectTrigger className="h-8 w-28 rounded-lg" data-testid={`rattach-mode-${r.matricule}`}><SelectValue placeholder="Mode" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="forfait">Forfait</SelectItem>
                              <SelectItem value="bulletin">Bulletin</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : <span className="text-[11px] text-muted-foreground">—</span>}
                      </td>
                      <td className="px-2 py-2">
                        {pop === 'independant' ? (
                          <Input type="number" placeholder={f.mode === 'forfait' ? '€/mois' : '€/BS'} value={f.montant ?? ''} onChange={(e) => setD(r.matricule, 'montant', e.target.value)} className="h-8 w-24 rounded-lg tabular" data-testid={`rattach-montant-${r.matricule}`} />
                        ) : <span className="text-[11px] text-muted-foreground">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" className="h-8 gap-1 rounded-lg text-white" style={accentStyle} disabled={saving === r.matricule} onClick={() => save(r.matricule)} data-testid={`rattach-save-${r.matricule}`}>
                          <Save className="h-3.5 w-3.5" /> {saving === r.matricule ? '…' : 'Rattacher'}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </CardTable>
          </TableWrap>
        )}
      </div>

      {/* rattachements existants */}
      <div className="space-y-2">
        <SectionTitle icon={Link2} title="Rattachements enregistrés" sub={`${rat.length} enregistrement(s) versionné(s) en base`} />
        <TableWrap>
          <CardTable className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 font-medium">Matricule</th>
                <th className="px-2 py-2 font-medium">Collaborateur</th>
                <th className="px-2 py-2 font-medium">Population</th>
                <th className="px-2 py-2 font-medium">Mode</th>
                <th className="px-2 py-2 text-right font-medium">Montant / Taux</th>
                <th className="px-3 py-2 font-medium">Statut</th>
              </tr>
            </thead>
            <tbody className="tabular">
              {rat.map((r) => (
                <tr key={r.matricule} className="border-b last:border-0 hover:bg-muted/40" data-testid={`rattach-row-${r.matricule}`}>
                  <td className="px-3 py-2 font-medium">{r.matricule}</td>
                  <td className="px-2 py-2">{r.prenom} {r.nom}</td>
                  <td className="px-2 py-2"><Badge variant={r.population === 'independant' ? 'secondary' : 'outline'} className="text-[10px]">{r.population}</Badge></td>
                  <td className="px-2 py-2">{r.mode || '—'}</td>
                  <td className="px-2 py-2 text-right">{r.montant != null ? fmtEur(r.montant, 2) : '—'}</td>
                  <td className="px-3 py-2">{r.statut || 'active'}</td>
                </tr>
              ))}
            </tbody>
          </CardTable>
        </TableWrap>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- //
//  Rentabilité RD (aide à la décision de reconduction)                         //
// --------------------------------------------------------------------------- //
const STATUT_META = {
  rentable: { label: 'Rentable', dot: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400', chip: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300' },
  surveille: { label: 'À surveiller', dot: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-400', chip: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300' },
  non_rentable: { label: 'Non rentable', dot: 'bg-rose-500', text: 'text-rose-600 dark:text-rose-400', chip: 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300' },
  na: { label: 'N/A', dot: 'bg-muted-foreground/40', text: 'text-muted-foreground', chip: 'border-border bg-muted text-muted-foreground' },
};
const POP_LABEL = { salarie: 'Salarié', inde_forfait: 'Indé forfait', inde_bulletin: 'Indé bulletin' };

function StatutBadge({ statut }) {
  const m = STATUT_META[statut] || STATUT_META.na;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium ${m.chip}`} data-testid={`rentab-statut-${statut}`}>
      <span className={`h-2 w-2 rounded-full ${m.dot}`} aria-hidden /> {m.label}
    </span>
  );
}

// --------------------------------------------------------------------------- //
//  Indicateur de rentabilité discret (réutilise STATUT_META + logique backend) //
// --------------------------------------------------------------------------- //
// Reproduit M.rentab_statut (backend) côté client pour des agrégats (populations).
function statutFromRatio(ratio, seuil, borneMin = 1) {
  if (ratio === null || ratio === undefined) return 'na';
  if (seuil !== null && seuil !== undefined && ratio >= seuil) return 'rentable';
  if (ratio >= (borneMin ?? 1)) return 'surveille';
  return 'non_rentable';
}

// Pastille colorée + tooltip : aperçu instantané « rentable / à surveiller / non rentable ».
function RentabDot({ info, size = 'sm', className = '' }) {
  if (!info || !info.statut) return null;
  const m = STATUT_META[info.statut] || STATUT_META.na;
  const dim = size === 'md' ? 'h-3 w-3' : 'h-2.5 w-2.5';
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          role="img"
          aria-label={`Rentabilité : ${m.label}`}
          data-testid={`rentab-dot-${info.matricule || info.population || 'x'}`}
          className={`inline-flex ${dim} shrink-0 cursor-help rounded-full ring-2 ring-background ${m.dot} ${className}`}
        />
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[230px]">
        <div className="flex items-center gap-1.5 text-xs font-semibold">
          <span className={`h-2 w-2 rounded-full ${m.dot}`} aria-hidden /> {m.label}
        </div>
        <div className="mt-1 space-y-0.5 text-[11px] text-muted-foreground">
          {info.ratio_proj != null && (
            <div>Couverture&nbsp;: <span className="tabular text-foreground">×{fmtNum(info.ratio_proj, 2)}</span></div>
          )}
          {info.marge_proj != null && (
            <div>Marge projetée&nbsp;: <span className="tabular text-foreground">{fmtEur(info.marge_proj, 2)}</span></div>
          )}
          {info.ca_attribue != null && info.cout != null && (
            <div>CA {fmtEur(info.ca_attribue, 2)} · coût {fmtEur(info.cout, 2)}</div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

// Bandeau récap (légende + compteurs) — visibilité globale sans quitter la vue.
function RentabLegend({ nb, contexte, loading }) {
  const safe = nb || {};
  const items = [['rentable', safe.rentable], ['surveille', safe.surveille], ['non_rentable', safe.non_rentable]];
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 rounded-xl border bg-muted/30 px-4 py-2.5 text-xs" data-testid="rentab-legend">
      <span className="mr-1 inline-flex items-center gap-1.5 font-medium text-foreground">
        <Gauge className="h-3.5 w-3.5 accent-text" /> Rentabilité
      </span>
      {loading ? (
        <span className="text-muted-foreground">calcul…</span>
      ) : (
        <>
          {items.map(([s, n]) => {
            const m = STATUT_META[s];
            return (
              <span key={s} className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 font-medium ${m.chip}`} data-testid={`rentab-legend-${s}`}>
                <span className={`h-2 w-2 rounded-full ${m.dot}`} aria-hidden /> {fmtInt(n || 0)} {m.label.toLowerCase()}
              </span>
            );
          })}
          {safe.na ? <span className="text-[11px] text-muted-foreground">· {fmtInt(safe.na)} n/c</span> : null}
        </>
      )}
      {contexte ? <span className="ml-auto text-[11px] text-muted-foreground">{contexte}</span> : null}
    </div>
  );
}

function RatioPill({ ratio, statut, size = 'md' }) {
  const m = STATUT_META[statut] || STATUT_META.na;
  if (ratio === null || ratio === undefined) return <span className="text-muted-foreground">—</span>;
  const cls = size === 'lg' ? 'text-2xl' : size === 'sm' ? 'text-sm' : 'text-base';
  return <span className={`font-display font-bold tabular ${m.text} ${cls}`}>×{fmtNum(ratio, 2)}</span>;
}

function DualCell({ proj, real, fmt = (v) => fmtEur(v, 2), split = false, mainClass = 'font-medium' }) {
  return (
    <div className="leading-tight">
      <div className={`tabular ${mainClass}`}>{fmt(proj)}</div>
      {split && <div className="text-[10px] text-muted-foreground tabular">réel {fmt(real)}</div>}
    </div>
  );
}

function StatCountCard({ statut, n, total, testid }) {
  const m = STATUT_META[statut];
  const share = total ? Math.max(0, Math.min(1, n / total)) : 0;
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-center gap-2">
        <span className={`h-2.5 w-2.5 rounded-full ${m.dot}`} aria-hidden />
        <span className="text-xs font-medium text-muted-foreground">{m.label}</span>
      </div>
      <div className={`mt-2 kpi-value text-2xl tabular ${m.text}`}>{fmtInt(n)}</div>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted" aria-hidden>
        <div className={`h-full rounded-full ${m.dot} transition-[width] duration-200`} style={{ width: `${share * 100}%` }} />
      </div>
    </Card>
  );
}

function ExpressRow({ r }) {
  const m = STATUT_META[r.statut] || STATUT_META.na;
  return (
    <div className="flex items-center justify-between gap-2 rounded-xl border bg-card px-3 py-2" data-testid={`rentab-express-${r.matricule}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <span className={`h-2 w-2 shrink-0 rounded-full ${m.dot}`} aria-hidden />
          <span className="truncate text-sm font-medium">{r.prenom} {r.nom}</span>
        </div>
        <div className="text-[10px] text-muted-foreground">{POP_LABEL[r.population] || r.population}</div>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        {r.ratio_proj != null ? <span className={`font-display text-sm font-bold ${m.text}`}>×{fmtNum(r.ratio_proj, 2)}</span> : <span className="text-xs text-muted-foreground">—</span>}
        <span className={`w-24 text-right text-sm font-semibold tabular ${(r.marge_proj || 0) >= 0 ? 'accent-text' : 'text-rose-600 dark:text-rose-400'}`}>{fmtEur(r.marge_proj, 2)}</span>
      </div>
    </div>
  );
}

function CoherenceLine({ label, terms, expected, ok, testid }) {
  return (
    <div className="rounded-xl border bg-background/60 p-3" data-testid={testid}>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${ok ? STATUT_META.rentable.chip : STATUT_META.non_rentable.chip}`}>
          {ok ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />} {ok ? 'Cohérent' : 'Écart'}
        </span>
      </div>
      <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-1 text-sm tabular">
        {terms}
        <span className="text-muted-foreground">=</span>
        <span className="font-semibold accent-text">{fmtEur(expected, 2)}</span>
      </div>
    </div>
  );
}

function RentabiliteView({ ym }) {
  const [gran, setGran] = useState('mois');
  const [anchor, setAnchor] = useState(`${ym}-01`);
  const [cs, setCs] = useState(`${ym}-01`);
  const [ce, setCe] = useState(`${ym}-15`);
  const [allouer, setAllouer] = useState(false);
  const [popFilter, setPopFilter] = useState('all');
  const [statFilter, setStatFilter] = useState('all');
  const [sortKey, setSortKey] = useState('marge_proj');
  const [sortDir, setSortDir] = useState('desc');

  const { start, end } = pnlBounds(gran, anchor, cs, ce);
  const isMonth = gran === 'mois';
  const params = isMonth
    ? { type: 'mois', ym: anchor.slice(0, 7), allouer_structure: allouer }
    : { type: gran, start, end, allouer_structure: allouer };
  const q = useGet('/social/rentabilite', params);
  const d = q.data;

  const nav = (dir) => {
    if (gran === 'jour') setAnchor(isoAdd(anchor, dir));
    else if (gran === 'semaine') setAnchor(isoAdd(anchor, dir * 7));
    else if (gran === 'mois') setAnchor(`${addMonth(anchor.slice(0, 7), dir)}-01`);
    else if (gran === 'annee') setAnchor(`${+anchor.slice(0, 4) + dir}-01-01`);
  };
  const periodLabel = d?.label || (isMonth ? labelYm(anchor.slice(0, 7)) : `${start} → ${end}`);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir((s) => (s === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir('desc'); }
  };

  const split = (d?.taux_avancement || 1) < 0.999;
  const rows = useMemo(() => {
    let arr = (d?.rows || []).slice();
    if (popFilter !== 'all') arr = arr.filter((r) => r.population === popFilter);
    if (statFilter !== 'all') arr = arr.filter((r) => r.statut === statFilter);
    const mul = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      const av = a[sortKey]; const bv = b[sortKey];
      const an = av === null || av === undefined ? -Infinity : av;
      const bn = bv === null || bv === undefined ? -Infinity : bv;
      if (an === bn) return 0;
      return an > bn ? mul : -mul;
    });
    return arr;
  }, [d, popFilter, statFilter, sortKey, sortDir]);

  const controls = (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex-1 min-w-[280px]">
        <PnlPeriodControls gran={gran} setGran={setGran} anchor={anchor} setAnchor={setAnchor}
          cs={cs} setCs={setCs} ce={ce} setCe={setCe} nav={nav} label={periodLabel} idPrefix="rentab" />
      </div>
      <div className="flex items-center gap-2 rounded-2xl border bg-muted/20 px-3 py-2" data-testid="rentab-alloc-toggle-wrap">
        <Layers className="h-4 w-4 accent-text" />
        <Label htmlFor="rentab-alloc" className="text-xs">Allouer la structure</Label>
        <Switch id="rentab-alloc" checked={allouer} onCheckedChange={setAllouer} data-testid="rentab-alloc-toggle" />
      </div>
    </div>
  );

  if (!d) {
    return (
      <div className="space-y-4" data-testid="social-rentab-view">
        {controls}
        {q.isLoading
          ? (<><div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div><Skeleton className="h-40 rounded-2xl" /></>)
          : <EmptyState title="Rentabilité indisponible sur cette période" hint="Aucune donnée BS/coût sur la plage sélectionnée." />}
      </div>
    );
  }

  const nb = d.nb || {};
  const seuilTxt = d.seuil != null ? `×${fmtNum(d.seuil, 2)}` : 'n/c';
  const SortTh = ({ k, label, align = 'right' }) => (
    <th className={`px-2 py-2 font-medium cursor-pointer select-none hover:text-foreground ${align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'}`}
      onClick={() => toggleSort(k)} data-testid={`rentab-sort-${k}`}>
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {label}
        {sortKey === k ? (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
      </span>
    </th>
  );

  return (
    <div className="space-y-5" data-testid="social-rentab-view">
      {controls}

      {/* Aide à la décision (panneau IA) — en tête de l'onglet Rentabilité */}
      {isMonth ? (
        <DecisionPanel ym={anchor.slice(0, 7)} allouer={allouer} population={popFilter} statut={statFilter} />
      ) : (
        <Card className="flex items-center gap-2 p-3 text-xs text-muted-foreground" data-testid="decision-month-only">
          <Info className="h-4 w-4" /> L'aide à la décision (verdict mensuel) s'affiche en vue « mois ». Sélectionnez une granularité mensuelle ci-dessus.
        </Card>
      )}

      {/* meta strip */}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Badge variant="secondary" data-testid="rentab-bs-source">BS : {d.bs_source_pnl === 'reference' ? 'Référence' : d.bs_source_pnl === 'evergiving' ? 'Terrain (Evergiving live)' : d.bs_source_pnl}</Badge>
        <Badge variant="secondary">Mode indé : {d.mode_inde_totaux === 'forecast' ? 'Forecast' : 'Réel contrat'}</Badge>
        <Badge variant="outline" className="gap-1"><CalendarClock className="h-3.5 w-3.5" /> Réalisé à date : {d.jours_ecoules}/{d.jours_attendus} j ({fmtPct(d.taux_avancement, 0)})</Badge>
        <Badge variant="outline" className="gap-1"><Scale className="h-3.5 w-3.5" /> Statut décidé sur la <strong className="mx-1">projection</strong></Badge>
        {d.allouer_structure && <Badge className="gap-1 border-transparent" style={{ background: 'var(--tenant-accent-050)', color: 'var(--tenant-accent-600)' }} data-testid="rentab-alloc-badge"><Layers className="h-3.5 w-3.5" /> Structure allouée aux coûts</Badge>}
      </div>

      {/* Bandeau synthèse */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6" data-testid="rentab-synthese">
        <StatCountCard statut="rentable" n={nb.rentable || 0} total={d.nb_collaborateurs} testid="rentab-count-rentable" />
        <StatCountCard statut="surveille" n={nb.surveille || 0} total={d.nb_collaborateurs} testid="rentab-count-surveille" />
        <StatCountCard statut="non_rentable" n={nb.non_rentable || 0} total={d.nb_collaborateurs} testid="rentab-count-non-rentable" />
        <Card className="p-4" style={{ borderColor: 'var(--tenant-accent-200)', background: 'var(--tenant-accent-050)' }} data-testid="rentab-seuil-card">
          <div className="flex items-center gap-2 accent-text"><Target className="h-4 w-4" /><span className="text-xs font-medium">Seuil de couverture</span></div>
          <div className="mt-2 kpi-value text-2xl accent-text tabular">{seuilTxt}</div>
          <div className="mt-1 text-[11px] text-muted-foreground">{d.seuil == null ? 'non calculable (projection précoce)' : d.seuil_mode === 'manuel' ? 'seuil manuel figé' : 'dynamique'}</div>
        </Card>
        <Kpi icon={Coins} label="Marge attribuée (proj.)" value={fmtEur(d.marge_totale_proj, 2)} sub="toutes populations" testid="rentab-marge-tot" />
        <Kpi icon={Gauge} label="CA net attribué" value={fmtEur(d.ca_net, 2)} sub={`${fmtEur(d.value_per_bs, 2)} / BS`} testid="rentab-ca-net" />
      </div>

      {/* Bandeau seuil expliqué */}
      <Card className="flex flex-wrap items-center gap-x-2 gap-y-1 p-4 text-sm" data-testid="rentab-seuil-bandeau">
        <Target className="h-4 w-4 accent-text" />
        <span className="font-semibold">Seuil de la période : <span className="accent-text">{seuilTxt}</span></span>
        <span className="text-muted-foreground">—</span>
        <span className="text-muted-foreground">
          {d.allouer_structure
            ? 'structure allouée aux coûts (marge nette)'
            : <>structure <strong className="text-foreground">{fmtPct(d.part_structure, 0)}</strong> du CA</>}
          , cible nette <strong className="text-foreground">{fmtPct(d.cible_resultat_net, 0)}</strong> (IS {fmtPct(d.taux_is, 0)} → avant IS {fmtPct(d.cible_avant_is, 0)})
          {d.seuil == null && <span className="ml-1 text-amber-600 dark:text-amber-400">— cible inatteignable sur la projection en cours</span>}
        </span>
        <span className="ml-auto inline-flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500" /> ≥ {seuilTxt}</span>
          <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-amber-500" /> ≥ ×{fmtNum(d.borne_min, 2)}</span>
          <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-rose-500" /> &lt; ×{fmtNum(d.borne_min, 2)}</span>
        </span>
      </Card>

      {/* Décision express : Top 5 / Flop 5 */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="p-4" data-testid="rentab-top5">
          <SectionTitle icon={Trophy} title="Décision express — Top 5" sub="meilleures marges projetées" />
          <div className="mt-3 space-y-2">
            {(d.top5 || []).map((r) => <ExpressRow key={r.matricule} r={r} />)}
            {(!d.top5 || d.top5.length === 0) && <p className="text-xs text-muted-foreground">Aucune ligne.</p>}
          </div>
        </Card>
        <Card className="p-4" data-testid="rentab-flop5">
          <SectionTitle icon={TrendingDown} title="Décision express — Flop 5" sub="marges projetées les plus faibles" />
          <div className="mt-3 space-y-2">
            {(d.flop5 || []).map((r) => <ExpressRow key={r.matricule} r={r} />)}
            {(!d.flop5 || d.flop5.length === 0) && <p className="text-xs text-muted-foreground">Aucune ligne.</p>}
          </div>
        </Card>
      </div>

      {/* Contrôle de cohérence (re-ventilation du P&L) */}
      <Card className="p-4" data-testid="rentab-coherence">
        <SectionTitle icon={ShieldCheck} title="Contrôle de cohérence" sub="la Rentabilité est une re-ventilation du P&L (jamais un 2ᵉ calcul)"
          right={<Badge variant="outline" className={`gap-1 ${d.coherence.ca.ok && d.coherence.cout.ok ? 'border-emerald-300 text-emerald-700 dark:text-emerald-300' : 'border-rose-300 text-rose-700 dark:text-rose-300'}`} data-testid="rentab-coherence-global">
            {d.coherence.ca.ok && d.coherence.cout.ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />} {d.coherence.ca.ok && d.coherence.cout.ok ? 'À l\u2019euro près' : 'Écart détecté'}
          </Badge>} />
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
          <CoherenceLine label="CA : Σ attribués + non attribué = CA net P&L" ok={d.coherence.ca.ok} testid="rentab-coh-ca"
            expected={d.coherence.ca.ca_net_pnl}
            terms={<>
              <span>{fmtEur(d.coherence.ca.attribue, 2)}</span>
              <span className="text-muted-foreground">+</span>
              <span title="BS sans code recruteur ou non rattaché">{fmtEur(d.coherence.ca.non_attribue, 2)}<span className="ml-1 text-[10px] text-muted-foreground">(non attribué)</span></span>
            </>} />
          <CoherenceLine label="Coûts : Σ coûts individuels = coût toutes populations" ok={d.coherence.cout.ok} testid="rentab-coh-cout"
            expected={d.coherence.cout.cout_toutes_populations}
            terms={<span>{fmtEur(d.coherence.cout.somme_directs, 2)}<span className="ml-1 text-[10px] text-muted-foreground">(marge directe, hors structure)</span></span>} />
        </div>
      </Card>

      {/* Filtres + tableau */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <SectionTitle icon={Users} title="Rentabilité par collaborateur" sub={`${rows.length} / ${d.nb_collaborateurs} ligne(s) · tri par ${sortKey === 'marge_proj' ? 'marge projetée' : sortKey}`} />
          <div className="ml-auto flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={popFilter} onValueChange={setPopFilter}>
              <SelectTrigger className="h-9 w-40 rounded-xl" data-testid="rentab-filter-pop"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes populations</SelectItem>
                <SelectItem value="salarie">Salariés</SelectItem>
                <SelectItem value="inde_forfait">Indés — forfait</SelectItem>
                <SelectItem value="inde_bulletin">Indés — bulletin</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statFilter} onValueChange={setStatFilter}>
              <SelectTrigger className="h-9 w-40 rounded-xl" data-testid="rentab-filter-statut"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous statuts</SelectItem>
                <SelectItem value="rentable"><span className="inline-flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Rentable</span></SelectItem>
                <SelectItem value="surveille"><span className="inline-flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-amber-500" /> À surveiller</span></SelectItem>
                <SelectItem value="non_rentable"><span className="inline-flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-rose-500" /> Non rentable</span></SelectItem>
                <SelectItem value="na">N/A</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <TableWrap>
          <CardTable className="w-full min-w-[980px] text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 text-left font-medium">Collaborateur</th>
                <th className="px-2 py-2 text-center font-medium">Statut</th>
                <SortTh k="bs_bruts_proj" label="BS bruts" />
                <SortTh k="bs_nets_proj" label="BS nets" />
                <SortTh k="ca_attribue_proj" label="CA attribué" />
                <SortTh k="cout_proj" label="Coût" />
                <SortTh k="marge_proj" label="Marge €" />
                <SortTh k="ratio_proj" label="Ratio ×" align="center" />
                <SortTh k="point_mort_bs" label="Point mort" />
              </tr>
            </thead>
            <tbody className="tabular">
              {rows.map((r) => {
                const mc = (r.marge_proj || 0) >= 0 ? 'font-semibold accent-text' : 'font-semibold text-rose-600 dark:text-rose-400';
                return (
                  <tr key={r.matricule} className="border-b last:border-0 hover:bg-muted/40" data-testid={`rentab-row-${r.matricule}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium">{r.prenom} {r.nom}</div>
                      <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                        <span>{r.matricule}</span><span>·</span><span>{POP_LABEL[r.population] || r.population}</span>
                        {r.population === 'inde_bulletin' && <Badge variant="outline" className="h-4 px-1 text-[9px]">{r.mode_retenu === 'forecast' ? 'Forecast' : 'Réel'}</Badge>}
                      </div>
                    </td>
                    <td className="px-2 py-2 text-center"><StatutBadge statut={r.statut} /></td>
                    <td className="px-2 py-2 text-right"><DualCell proj={r.bs_bruts_proj} real={r.bs_bruts} fmt={(v) => fmtInt(v)} split={split} /></td>
                    <td className="px-2 py-2 text-right text-muted-foreground">{fmtInt(r.bs_nets_proj)}</td>
                    <td className="px-2 py-2 text-right"><DualCell proj={r.ca_attribue_proj} real={r.ca_attribue} split={split} /></td>
                    <td className="px-2 py-2 text-right"><DualCell proj={r.cout_proj} real={r.cout} split={split} /></td>
                    <td className="px-2 py-2 text-right"><DualCell proj={r.marge_proj} real={r.marge} split={split} mainClass={mc} /></td>
                    <td className="px-2 py-2 text-center"><RatioPill ratio={r.ratio_proj} statut={r.statut} /></td>
                    <td className="px-3 py-2 text-right">
                      {r.point_mort_bs != null ? (
                        <div className="leading-tight">
                          <div className="font-medium tabular">{fmtNum(r.point_mort_bs, 1)} BS</div>
                          <div className={`text-[10px] tabular ${(r.ecart_point_mort || 0) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>{(r.ecart_point_mort || 0) >= 0 ? '+' : ''}{fmtNum(r.ecart_point_mort, 1)} vs réel</div>
                        </div>
                      ) : '—'}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">Aucun collaborateur pour ces filtres.</td></tr>
              )}
            </tbody>
            <tfoot>
              <tr className="border-t bg-muted/30 text-[12px] font-semibold">
                <td colSpan={4} className="px-3 py-2 text-right text-muted-foreground">Totaux attribués (réalisé à date)</td>
                <td className="px-2 py-2 text-right tabular">{fmtEur(d.coherence.ca.attribue, 2)}</td>
                <td className="px-2 py-2 text-right tabular">{fmtEur(d.cout_toutes_populations, 2)}</td>
                <td className="px-2 py-2 text-right tabular accent-text">{fmtEur(d.marge_directe_totale, 2)}</td>
                <td colSpan={2} />
              </tr>
              <tr className="text-[11px] text-muted-foreground">
                <td colSpan={4} className="px-3 py-1.5 text-right">CA non attribué (BS sans code recruteur ou non rattaché)</td>
                <td className="px-2 py-1.5 text-right tabular" data-testid="rentab-ca-non-attribue">{fmtEur(d.ca_non_attribue, 2)}</td>
                <td colSpan={4} />
              </tr>
            </tfoot>
          </CardTable>
        </TableWrap>
      </div>

      {/* Détail indés au bulletin : marge Forecast vs Réel (point du brief) */}
      {(d.rows || []).some((r) => r.population === 'inde_bulletin') && (
        <div className="space-y-2" data-testid="rentab-bulletin-detail">
          <SectionTitle icon={ReceiptText} title="Indés au bulletin — marge Forecast vs Réel"
            sub={`mode retenu dans le P&L : ${d.mode_inde_totaux === 'forecast' ? 'Forecast (taux bloqué)' : 'Réel contrat (barème)'}`} />
          <TableWrap>
            <CardTable className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="border-b bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2 text-left font-medium">Collaborateur</th>
                  <th className="px-2 py-2 text-right font-medium">CA attribué</th>
                  <th className="px-2 py-2 text-right font-medium">Coût Forecast</th>
                  <th className="px-2 py-2 text-right font-medium">Marge Forecast</th>
                  <th className="px-2 py-2 text-right font-medium">Coût Réel</th>
                  <th className="px-2 py-2 text-right font-medium">Marge Réel</th>
                  <th className="px-3 py-2 text-center font-medium">Retenu</th>
                </tr>
              </thead>
              <tbody className="tabular">
                {(d.rows || []).filter((r) => r.population === 'inde_bulletin').map((r) => (
                  <tr key={r.matricule} className="border-b last:border-0 hover:bg-muted/40" data-testid={`rentab-bulletin-row-${r.matricule}`}>
                    <td className="px-3 py-2"><span className="font-medium">{r.prenom} {r.nom}</span> <span className="text-[10px] text-muted-foreground">{r.matricule}</span></td>
                    <td className="px-2 py-2 text-right">{fmtEur(r.ca_attribue, 2)}</td>
                    <td className="px-2 py-2 text-right text-muted-foreground">{fmtEur(r.cout_forecast, 2)}</td>
                    <td className={`px-2 py-2 text-right font-medium ${(r.marge_forecast || 0) >= 0 ? 'accent-text' : 'text-rose-600 dark:text-rose-400'}`}>{fmtEur(r.marge_forecast, 2)}</td>
                    <td className="px-2 py-2 text-right text-muted-foreground">{fmtEur(r.cout_reel, 2)}</td>
                    <td className={`px-2 py-2 text-right font-medium ${(r.marge_reel || 0) >= 0 ? 'accent-text' : 'text-rose-600 dark:text-rose-400'}`}>{fmtEur(r.marge_reel, 2)}</td>
                    <td className="px-3 py-2 text-center"><Badge variant="outline" className="text-[10px]">{r.mode_retenu === 'forecast' ? 'Forecast' : 'Réel'}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </CardTable>
          </TableWrap>
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------- //
//  Analyse Horaire (salariés uniquement) — Itération 2 « décision & vérité »   //
// --------------------------------------------------------------------------- //
const FMT_LABEL = { '3h': '3\u202fh', '4h': '4\u202fh', '5h': '5\u202fh', autres: 'Autres' };

const FIAB_META = {
  insuffisant: { label: 'Volume insuffisant', cls: 'border-rose-300 text-rose-700', icon: AlertTriangle },
  indicatif: { label: 'Indicatif', cls: 'border-amber-300 text-amber-700', icon: Info },
  fiable: { label: 'Fiable', cls: 'border-emerald-300 text-emerald-700', icon: CheckCircle2 },
};

// Badge de fiabilité en 3 niveaux (§3/§5) — plus de "vert" quand le volume ne le justifie pas.
function FiabBadge({ level, jours }) {
  const m = FIAB_META[level] || FIAB_META.insuffisant;
  const Icon = m.icon;
  const tip = `${fmtInt(jours)} recruteur-jours. Seuils : moins de 300 = insuffisant, 300 à 800 = indicatif, plus de 800 = fiable.`;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge variant="outline" className={`gap-1 text-[10px] ${m.cls}`} data-testid={`fiab-${level}`}>
          <Icon className="h-3 w-3" /> {m.label}
        </Badge>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{tip}</TooltipContent>
    </Tooltip>
  );
}

// Puce Gain/Perte sans ambiguïté (§1) — le signe et le sens sont explicites.
function GainPerteChip({ sens, annuel, testid }) {
  if (sens === 'neutre') {
    return <Badge variant="secondary" className="rounded-full" data-testid={testid}>Référence actuelle</Badge>;
  }
  const gain = sens === 'gain';
  return (
    <span data-testid={testid}
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold tabular ${gain ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
      {gain ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />}
      {gain ? 'Gain estimé\u00a0: +' : 'Perte estimée\u00a0: −'}{fmtEur(Math.abs(annuel || 0), 0)}/an
    </span>
  );
}

const HOR_STATUT_META = {
  vert: { label: 'Rentable', dot: 'bg-emerald-500', cls: 'text-emerald-700' },
  orange: { label: 'À surveiller', dot: 'bg-amber-500', cls: 'text-amber-700' },
  rouge: { label: 'Non rentable', dot: 'bg-rose-500', cls: 'text-rose-700' },
};
function StatutDot({ statut }) {
  const m = HOR_STATUT_META[statut] || HOR_STATUT_META.rouge;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${m.cls}`}>
      <span className={`h-2.5 w-2.5 rounded-full ${m.dot}`} /> {m.label}
    </span>
  );
}

function TendanceBadge({ t }) {
  if (t === 'hausse') return <span className="inline-flex items-center gap-1 text-xs text-emerald-600"><TrendingUp className="h-3.5 w-3.5" /> Progression</span>;
  if (t === 'baisse') return <span className="inline-flex items-center gap-1 text-xs text-rose-600"><TrendingDown className="h-3.5 w-3.5" /> Décrochage</span>;
  if (t === 'stable') return <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><ArrowRight className="h-3.5 w-3.5" /> Stable</span>;
  return <span className="text-xs text-muted-foreground">—</span>;
}

// Section technique repliable (3ᵉ niveau de lecture, §9).
function DetailsBlock({ title, icon: Icon, defaultOpen = false, children, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="overflow-hidden" data-testid={testid}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-muted/40"
        data-testid={`${testid}-toggle`}>
        <span className="flex items-center gap-2 font-display text-sm font-semibold">
          {Icon && <Icon className="h-4 w-4 accent-text" />} {title}
        </span>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="border-t p-4">{children}</div>}
    </Card>
  );
}

// Carte comparative d'un format (4 h / 5 h) avec niveau de fiabilité.
function FmtCompareCard({ title, f, highlight, testid }) {
  return (
    <Card className={`p-4 ${highlight ? 'ring-1 ring-[color:var(--tenant-accent-100)]' : ''}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[color:var(--tenant-accent-100)] font-display text-sm font-bold text-[color:var(--tenant-accent-600)]">{title}</span>
          <div>
            <div className="text-[11px] text-muted-foreground">Créneau</div>
            <div className="font-display text-sm font-semibold">Format {title}</div>
          </div>
        </div>
        <FiabBadge level={f.fiabilite} jours={f.jours} />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <div>
          <div className="text-[11px] text-muted-foreground">Bulletins / jour</div>
          <div className="kpi-value text-lg text-foreground tabular" data-testid={`${testid}-bsjour`}>{fmtNum(f.bs_par_jour, 2)}</div>
        </div>
        <div>
          <div className="text-[11px] text-muted-foreground">Bulletins / heure</div>
          <div className="kpi-value text-lg text-foreground tabular" data-testid={`${testid}-bsh`}>{fmtNum(f.bs_par_h, 3)}</div>
        </div>
        <div>
          <div className="text-[11px] text-muted-foreground">Recruteur-jours</div>
          <div className="text-sm font-semibold tabular">{fmtInt(f.jours)}</div>
        </div>
        <div>
          <div className="text-[11px] text-muted-foreground">Marge directe</div>
          <div className={`text-sm font-semibold tabular ${(f.marge_directe || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{fmtEur(f.marge_directe, 0)}</div>
        </div>
      </div>
    </Card>
  );
}

function HoraireView({ ym }) {
  // --- Période ---
  const [gran, setGran] = useState('annee');
  const [anchor, setAnchor] = useState(`${ym}-01`);
  const [cs, setCs] = useState(`${ym}-01`);
  const [ce, setCe] = useState(`${ym}-06-30`);
  const { start, end } = pnlBounds(gran, anchor, cs, ce);

  // --- Hypothèses économiques (pilotent tous les € + le point mort) ---
  const [coutH, setCoutH] = useState(21.6);
  const [valBs, setValBs] = useState(130);
  const [structM, setStructM] = useState(25000);
  const [allocKey, setAllocKey] = useState('heures');
  const [tauxPct, setTauxPct] = useState(0);
  const [annualiser, setAnnualiser] = useState(true);
  const [applied, setApplied] = useState({
    cout_horaire: 21.6, valeur_bs: 130, structure_mensuel: 25000,
    alloc_key: 'heures', taux_report: 0, annualiser: true,
  });

  // --- Filtres bloc recruteurs ---
  const [rActifs, setRActifs] = useState(false);
  const [rAlertes, setRAlertes] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingRec, setExportingRec] = useState(false);

  const q = useGet('/social/horaire', {
    start, end, cout_horaire: applied.cout_horaire, valeur_bs: applied.valeur_bs,
    structure_mensuel: applied.structure_mensuel, alloc_key: applied.alloc_key,
    taux_report: applied.taux_report, annualiser: applied.annualiser,
  }, !!(start && end));
  const d = q.data;

  const nav = (dir) => {
    if (gran === 'jour') setAnchor(isoAdd(anchor, dir));
    else if (gran === 'semaine') setAnchor(isoAdd(anchor, dir * 7));
    else if (gran === 'mois') setAnchor(`${addMonth(anchor.slice(0, 7), dir)}-01`);
    else if (gran === 'annee') setAnchor(`${+anchor.slice(0, 4) + dir}-01-01`);
  };
  const periodLabel = gran === 'mois' ? labelYm(anchor.slice(0, 7))
    : gran === 'annee' ? anchor.slice(0, 4) : `${start} → ${end}`;

  const dirty = coutH !== applied.cout_horaire || valBs !== applied.valeur_bs
    || structM !== applied.structure_mensuel || allocKey !== applied.alloc_key
    || (tauxPct / 100) !== applied.taux_report || annualiser !== applied.annualiser;
  const applyParams = () => setApplied({
    cout_horaire: Number(coutH) || 0, valeur_bs: Number(valBs) || 0,
    structure_mensuel: Number(structM) || 0, alloc_key: allocKey,
    taux_report: (Number(tauxPct) || 0) / 100, annualiser,
  });
  const resetParams = () => {
    setCoutH(21.6); setValBs(130); setStructM(25000); setAllocKey('heures'); setTauxPct(0); setAnnualiser(true);
    setApplied({ cout_horaire: 21.6, valeur_bs: 130, structure_mensuel: 25000, alloc_key: 'heures', taux_report: 0, annualiser: true });
  };

  const doExport = async () => {
    setExporting(true);
    try {
      await downloadGet('/social/horaire/export.csv', `Analyse_Horaire_${start}_${end}.csv`,
        { start, end, cout_horaire: applied.cout_horaire, valeur_bs: applied.valeur_bs,
          structure_mensuel: applied.structure_mensuel, alloc_key: applied.alloc_key });
      toast.success('Export CSV généré');
    } catch (e) { toast.error('Export CSV indisponible'); } finally { setExporting(false); }
  };
  const doExportRec = async () => {
    setExportingRec(true);
    try {
      await downloadGet('/social/horaire/recruteurs.csv', `Rentabilite_Recruteurs_${start}_${end}.csv`,
        { start, end, cout_horaire: applied.cout_horaire, valeur_bs: applied.valeur_bs,
          structure_mensuel: applied.structure_mensuel, alloc_key: applied.alloc_key, actifs_only: rActifs });
      toast.success('Export recruteurs généré');
    } catch (e) { toast.error('Export indisponible'); } finally { setExportingRec(false); }
  };

  const controls = (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex-1 min-w-[280px]">
        <PnlPeriodControls gran={gran} setGran={setGran} anchor={anchor} setAnchor={setAnchor}
          cs={cs} setCs={setCs} ce={ce} setCe={setCe} nav={nav} label={periodLabel} idPrefix="horaire" />
      </div>
      <Button variant="outline" className="gap-2 rounded-xl" onClick={doExport} disabled={exporting || !d} data-testid="horaire-export-csv">
        <Download className="h-4 w-4" /> Export CSV
      </Button>
    </div>
  );

  if (!d) {
    return (
      <div className="space-y-4" data-testid="social-horaire-view">
        {controls}
        {q.isLoading
          ? (<><div className="grid grid-cols-2 gap-4 md:grid-cols-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div><Skeleton className="h-40 rounded-2xl" /><Skeleton className="h-64 rounded-2xl" /></>)
          : <EmptyState title="Analyse horaire indisponible sur cette période" hint="Aucune heure de recruteur salarié n'a été trouvée sur la plage sélectionnée. Élargissez la période." />}
      </div>
    );
  }

  const diag = d.diagnostic_horaire || {};
  const per = d.perimetre || {};
  const eco = d.economie || {};
  const lev = d.levier || {};
  const cnr = lev.cout_journees_non_rentables || {};
  const quartiles = lev.quartiles || [];
  const reco = d.recommandation || {};
  const princ = reco.principale || {};
  const secondaires = reco.secondaires || [];
  const cmp = d.comparaison_5h_4h || {};
  const autres = d.autres_detail || {};
  const sel = autres.effet_selection || {};
  const sim = d.simulateur || {};
  const annu = sim.annualisation || {};
  const scenarios = sim.scenarios || [];
  const recruteurs = d.recruteurs || [];
  const rMeta = d.recruteurs_meta || {};
  const responsables = d.segments?.responsables || [];
  const evolution = d.evolution || [];
  const maxEvoBsh = Math.max(0.0001, ...evolution.map((e) => e.bs_par_h || 0));
  const fmts = d.formats || [];

  const sousPointMort = (eco.bs_h_global || 0) < (eco.point_mort_bsh_complet || 0);
  const recFiltered = recruteurs.filter((r) => (!rActifs || r.actif_terrain) && (!rAlertes || r.alerte));
  const maxPartQ4 = Math.max(0.0001, ...Object.values(sel.part_q4_par_format || {}));

  return (
    <TooltipProvider>
      <div className="space-y-5" data-testid="social-horaire-view">
        {controls}

        {/* ============ NIVEAU 1 — CONCLUSION ============ */}
        <Card className="overflow-hidden" data-testid="horaire-reco">
          <div className="flex flex-col gap-3 p-5"
            style={{ background: 'linear-gradient(120deg, var(--tenant-accent-100) 0%, transparent 72%)' }}>
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[color:var(--tenant-accent-500)] text-white">
                <Trophy className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Recommandation principale</div>
                <h3 className="font-display text-base font-semibold text-foreground sm:text-lg" data-testid="horaire-reco-title">{princ.titre}</h3>
                <p className="mt-1.5 max-w-3xl text-sm leading-relaxed text-foreground/80" data-testid="horaire-reco-text">{princ.phrase}</p>
                <div className="mt-2.5 flex flex-wrap items-center gap-2">
                  <Badge className="gap-1 border-transparent bg-rose-100 text-rose-700" data-testid="horaire-reco-impact">
                    <Banknote className="h-3.5 w-3.5" /> Impact ~{fmtEur(Math.abs(princ.impact_eur_annuel || 0), 0)}/an
                  </Badge>
                  <Badge variant="outline" className="gap-1 text-[11px]"><ShieldCheck className="h-3.5 w-3.5" /> Confiance {princ.confiance}</Badge>
                  <Badge variant="outline" className="gap-1 text-[11px]"><Layers className="h-3.5 w-3.5" /> {princ.base_volume}</Badge>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* KPIs de pilotage */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Kpi icon={Gauge} label="Productivité globale" value={`${fmtNum(eco.bs_h_global, 3)} BS/h`}
            sub={sousPointMort ? `sous le point mort (${fmtNum(eco.point_mort_bsh_complet, 3)})` : 'au-dessus du point mort'}
            testid="horaire-kpi-bsh" />
          <Kpi icon={Target} label="Point mort (structure incl.)" value={`${fmtNum(eco.point_mort_bsh_complet, 3)} BS/h`}
            sub={`sans structure : ${fmtNum(eco.point_mort_bsh_direct, 3)}`} testid="horaire-kpi-pointmort" />
          <Kpi icon={PiggyBank} label="Marge directe" value={fmtEur(eco.marge_directe_total, 0)}
            sub="recette BS − coût des heures" testid="horaire-kpi-marge-directe" />
          <Kpi icon={Landmark} label="Marge complète" value={fmtEur(eco.marge_complete_total, 0)}
            sub={eco.couvre_structure ? 'structure couverte' : 'structure NON couverte'} testid="horaire-kpi-marge-complete" />
        </div>

        {/* ============ NIVEAU 1bis — LE VRAI LEVIER (quartiles) ============ */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_1.4fr]">
          <Card className="flex flex-col justify-between border-rose-200 bg-rose-50/50 p-4" data-testid="horaire-cout-non-rentables">
            <div>
              <div className="flex items-center gap-2 text-rose-700">
                <AlertTriangle className="h-4 w-4" />
                <h4 className="font-display text-sm font-semibold">Coût des journées non rentables</h4>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">Journées des quartiles déficitaires : elles coûtent plus qu'elles ne rapportent.</p>
            </div>
            <div className="mt-3">
              <div className="kpi-value text-3xl text-rose-600 tabular" data-testid="horaire-cnr-montant">{fmtEur(cnr.montant, 0)}</div>
              <div className="mt-1 text-xs text-muted-foreground">
                {fmtInt(cnr.jours)} journées-recruteur ({fmtPct(cnr.part_du_parc, 0)} du parc)
                {lev.ratio_q4_q1_bs_jour ? ` · Q4 = ${fmtNum(lev.ratio_q4_q1_bs_jour, 1)}× le Q1` : ''}
              </div>
            </div>
          </Card>

          <div className="space-y-2">
            <SectionTitle icon={Trophy} title="Le vrai levier : le niveau des recruteurs (quartiles)" sub="classés sur BS/h — le plus fort écart de performance" />
            <TableWrap>
              <CardTable className="w-full text-sm">
                <thead className="text-xs text-muted-foreground">
                  <tr className="border-b">
                    <th className="px-2 py-2 text-left font-medium">Quartile</th>
                    <th className="px-2 py-2 text-right font-medium">Jours</th>
                    <th className="px-2 py-2 text-right font-medium">BS/jour</th>
                    <th className="px-2 py-2 text-right font-medium">BS/h</th>
                    <th className="px-2 py-2 text-right font-medium">Marge directe</th>
                    <th className="px-2 py-2 text-right font-medium">Marge complète</th>
                  </tr>
                </thead>
                <tbody>
                  {quartiles.map((qq) => (
                    <tr key={qq.label} className="border-b last:border-0 hover:bg-muted/30" data-testid={`horaire-quartile-${qq.label}`}>
                      <td className="px-2 py-2 font-semibold">{qq.label}</td>
                      <td className="px-2 py-2 text-right tabular">{fmtInt(qq.jours)}</td>
                      <td className="px-2 py-2 text-right tabular">{fmtNum(qq.bs_par_jour, 2)}</td>
                      <td className="px-2 py-2 text-right tabular font-semibold">{fmtNum(qq.bs_par_h, 3)}</td>
                      <td className={`px-2 py-2 text-right tabular ${(qq.marge_directe || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{fmtEur(qq.marge_directe, 0)}</td>
                      <td className={`px-2 py-2 text-right tabular font-semibold ${(qq.marge_complete || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{fmtEur(qq.marge_complete, 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </CardTable>
            </TableWrap>
          </div>
        </div>

        {/* ============ NIVEAU 2 — CHIFFRES QUI FONDENT LA CONCLUSION ============ */}

        {/* Hypothèses économiques */}
        <Card className="p-4" data-testid="horaire-hypotheses">
          <SectionTitle icon={Settings2} title="Hypothèses économiques" sub="pilotent tous les montants € et le point mort ci-dessus" />
          <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <Label className="text-xs">Coût horaire chargé (€/h)</Label>
              <Input type="number" step="0.1" value={coutH}
                onChange={(e) => setCoutH(e.target.value === '' ? '' : parseFloat(e.target.value))}
                className="mt-1 rounded-xl tabular" data-testid="horaire-input-cout" />
            </div>
            <div>
              <Label className="text-xs">Valeur d'un bulletin (€)</Label>
              <Input type="number" step="1" value={valBs}
                onChange={(e) => setValBs(e.target.value === '' ? '' : parseFloat(e.target.value))}
                className="mt-1 rounded-xl tabular" data-testid="horaire-input-valbs" />
            </div>
            <div>
              <Label className="text-xs">Structure mensuelle (€)</Label>
              <Input type="number" step="500" value={structM}
                onChange={(e) => setStructM(e.target.value === '' ? '' : parseFloat(e.target.value))}
                className="mt-1 rounded-xl tabular" data-testid="horaire-input-structure" />
            </div>
            <div>
              <Label className="text-xs">Allocation structure</Label>
              <Select value={allocKey} onValueChange={setAllocKey}>
                <SelectTrigger className="mt-1 rounded-xl" data-testid="horaire-input-alloc"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="heures">Au prorata des heures</SelectItem>
                  <SelectItem value="jours">Au prorata des journées</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs">Taux de report des BS</Label>
                <span className="text-xs font-semibold tabular accent-text" data-testid="horaire-taux-value">{fmtInt(tauxPct)}{'\u202f'}%</span>
              </div>
              <Slider value={[tauxPct]} min={0} max={80} step={5}
                onValueChange={(v) => setTauxPct(v[0])} className="mt-3" data-testid="horaire-input-taux" />
              <p className="mt-1.5 text-[10px] text-muted-foreground">Part des BS d'une heure retirée que le recruteur produirait quand même ailleurs.</p>
            </div>
            <div className="flex items-center justify-between rounded-xl border p-3">
              <div>
                <Label className="text-xs">Annualisation</Label>
                <p className="text-[10px] text-muted-foreground">{annualiser ? 'Profil mensuel réel (saisonnalité)' : 'Période observée uniquement'}</p>
              </div>
              <Switch checked={annualiser} onCheckedChange={setAnnualiser} data-testid="horaire-input-annualiser" />
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button size="sm" className="rounded-xl" style={accentStyle} onClick={applyParams} disabled={!dirty} data-testid="horaire-apply">
              <Play className="mr-1.5 h-3.5 w-3.5" /> Appliquer
            </Button>
            <Button size="sm" variant="outline" className="rounded-xl" onClick={resetParams} data-testid="horaire-reset">Réinitialiser</Button>
            {q.isFetching && <span className="text-[11px] text-muted-foreground">Recalcul…</span>}
            {dirty && !q.isFetching && <span className="text-[11px] text-amber-700">Paramètres modifiés — cliquez sur « Appliquer ».</span>}
          </div>
        </Card>

        {/* Simulateur cohérent (§1) */}
        <div className="space-y-3" data-testid="horaire-simulateur">
          <SectionTitle icon={FlaskConical} title="Simulateur de changement de format"
            sub={annu.methode === 'profil_mensuel_saisonnier' ? `annualisé par saisonnalité (×${fmtNum(annu.facteur, 2)}, couverture ${fmtPct(annu.couverture_annuelle, 0)})` : 'période observée uniquement (sans annualisation)'} />
          <Card className="flex items-start gap-2 p-3 text-xs text-muted-foreground">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Chaque scénario indique clairement s'il génère un <strong className="text-emerald-700">gain</strong> ou une <strong className="text-rose-700">perte</strong>, sur le même périmètre. {annu.note}</span>
          </Card>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {scenarios.map((s) => (
              <Card key={s.id} className={`p-4 ${s.id === 'supprimer_journees_non_rentables' ? 'border-emerald-200' : ''}`} data-testid={`horaire-scenario-${s.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <ArrowRightLeft className="h-4 w-4 shrink-0 accent-text" />
                    <span className="font-display text-sm font-semibold leading-tight">{s.label}</span>
                  </div>
                  <GainPerteChip sens={s.sens} annuel={s.impact_net_annualise} testid={`horaire-scenario-${s.id}-chip`} />
                </div>
                {s.id !== 'actuel' && (
                  <div className="mt-3 grid grid-cols-2 gap-y-1.5 gap-x-3 text-xs">
                    <span className="text-muted-foreground">Journées touchées</span>
                    <span className="text-right font-semibold tabular">{fmtInt(s.jours_touches)}</span>
                    <span className="text-muted-foreground">Heures économisées</span>
                    <span className="text-right font-semibold tabular">{fmtNum(s.heures_economisees, 0)}{'\u202f'}h</span>
                    <span className="text-muted-foreground">Bulletins perdus (estim.)</span>
                    <span className="text-right font-semibold tabular">{fmtNum(s.bs_perdus, 1)}</span>
                    <span className="text-muted-foreground">Impact net (période)</span>
                    <span className={`text-right font-semibold tabular ${(s.impact_net || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{(s.impact_net || 0) >= 0 ? '+' : ''}{fmtEur(s.impact_net, 0)}</span>
                  </div>
                )}
                {s.id === 'raccourcir_tout_vers_4h' && (
                  <p className="mt-2 text-[10px] text-amber-700">⚠ Raccourcit aussi les journées longues « Autres », les plus productives.</p>
                )}
                {s.id === 'supprimer_journees_non_rentables' && (
                  <p className="mt-2 text-[10px] text-muted-foreground">Plafond théorique : cesser toutes les journées à marge négative.</p>
                )}
              </Card>
            ))}
          </div>
        </div>

        {/* Comparaison 4h vs 5h + significativité (§5) */}
        <div className="space-y-3" data-testid="horaire-comparaison">
          <SectionTitle icon={Scale} title="Comparaison 4 h vs 5 h" sub="l'expérience naturelle déjà en cours (niveau recruteur-jour)" />
          {!cmp.significatif && (
            <Card className="flex items-start gap-2 border-amber-300/70 bg-amber-50/60 p-3 text-xs text-amber-900" data-testid="horaire-not-significant">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{cmp.message_significativite}</span>
            </Card>
          )}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <FmtCompareCard title="4 h" f={cmp.f4 || {}} testid="horaire-card-4h" />
            <FmtCompareCard title="5 h" f={cmp.f5 || {}} highlight testid="horaire-card-5h" />
            <Card className="p-4" data-testid="horaire-card-heuresup">
              <div className="flex items-center gap-2">
                <ArrowRightLeft className="h-4 w-4 accent-text" />
                <h4 className="font-display text-sm font-semibold">La 5ᵉ heure rapporte-t-elle son coût ?</h4>
              </div>
              <div className="mt-3 space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Bulletins/jour gagnés</span>
                  <span className="font-semibold tabular">{fmtNum(cmp.heure_sup_bs, 3)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Ce qu'elle rapporte</span>
                  <span className="font-semibold tabular text-emerald-600">{fmtEur(cmp.heure_sup_recette, 2)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Ce qu'elle coûte</span>
                  <span className="font-semibold tabular text-rose-600">−{fmtEur(cmp.heure_sup_cout, 2)}</span>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="font-semibold">Résultat par jour</span>
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold tabular ${(cmp.heure_sup_resultat || 0) >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`} data-testid="horaire-heuresup-res">
                    {(cmp.heure_sup_resultat || 0) >= 0 ? '+' : ''}{fmtEur(cmp.heure_sup_resultat, 2)}
                  </span>
                </div>
                <p className="pt-1 text-[10px] text-muted-foreground">Sur {fmtInt(cmp.stats_5h?.n)} journées de 5 h — {cmp.significatif ? 'écart significatif' : 'écart non significatif'}.</p>
              </div>
            </Card>
          </div>
        </div>

        {/* « Autres » : identification + effet de sélection (§2) */}
        <div className="space-y-3" data-testid="horaire-autres">
          <SectionTitle icon={Layers} title="La catégorie « Autres » : ce que c'est et pourquoi elle surperforme"
            sub={`${fmtInt(autres.jours)} journées (${fmtPct(autres.part_du_parc, 0)} du parc) — durées hors 3/4/5 h`} />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.3fr_1fr]">
            <div>
              <TableWrap>
                <CardTable className="w-full text-sm">
                  <thead className="text-xs text-muted-foreground">
                    <tr className="border-b">
                      <th className="px-2 py-2 text-left font-medium">Durée réelle</th>
                      <th className="px-2 py-2 text-right font-medium">Jours</th>
                      <th className="px-2 py-2 text-right font-medium">BS/jour</th>
                      <th className="px-2 py-2 text-right font-medium">BS/h</th>
                      <th className="px-2 py-2 text-right font-medium">Marge directe</th>
                      <th className="px-2 py-2 text-center font-medium">Fiab.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(autres.buckets || []).map((b) => (
                      <tr key={b.borne} className="border-b last:border-0 hover:bg-muted/30" data-testid={`horaire-bucket-${b.borne}`}>
                        <td className="px-2 py-2 font-semibold">{b.label}</td>
                        <td className="px-2 py-2 text-right tabular">{fmtInt(b.jours)}</td>
                        <td className="px-2 py-2 text-right tabular">{fmtNum(b.bs_par_jour, 2)}</td>
                        <td className="px-2 py-2 text-right tabular font-semibold">{fmtNum(b.bs_par_h, 3)}</td>
                        <td className={`px-2 py-2 text-right tabular ${(b.marge_directe || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{fmtEur(b.marge_directe, 0)}</td>
                        <td className="px-2 py-2 text-center"><FiabBadge level={b.fiabilite} jours={b.jours} /></td>
                      </tr>
                    ))}
                  </tbody>
                </CardTable>
              </TableWrap>
            </div>
            <Card className="p-4" data-testid="horaire-effet-selection">
              <div className="flex items-center gap-2">
                <UserSearch className="h-4 w-4 accent-text" />
                <h4 className="font-display text-sm font-semibold">Durée ou effet de sélection ?</h4>
              </div>
              <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground" data-testid="horaire-selection-msg">{sel.message}</p>
              <div className="mt-3 space-y-2">
                <div className="text-[11px] font-medium text-muted-foreground">Part des meilleurs recruteurs (Q4) par format</div>
                {Object.entries(sel.part_q4_par_format || {}).map(([f, v]) => (
                  <div key={f} className="flex items-center gap-2" data-testid={`horaire-partq4-${f}`}>
                    <span className="w-14 shrink-0 text-xs">{FMT_LABEL[f] || f}</span>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                      <div className="h-full rounded-full" style={{ width: `${(v / maxPartQ4) * 100}%`, background: 'var(--tenant-accent-500)' }} />
                    </div>
                    <span className="w-12 shrink-0 text-right text-xs font-semibold tabular">{fmtPct(v, 0)}</span>
                  </div>
                ))}
                <p className="pt-1 text-[10px] text-muted-foreground">Moyenne du parc : {fmtPct(sel.part_q4_global, 0)}.</p>
              </div>
            </Card>
          </div>
          <DetailsBlock title="Répartition des journées « Autres » (responsable, canal, région)" icon={Filter} testid="horaire-autres-rep">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {[['Responsable', autres.par_responsable], ['Canal', autres.par_canal], ['Région', autres.par_region]].map(([t, rows]) => (
                <div key={t}>
                  <div className="mb-1 text-xs font-semibold">{t}</div>
                  <div className="space-y-1">
                    {(rows || []).map((r) => (
                      <div key={r.label} className="flex items-center justify-between text-xs">
                        <span className="truncate pr-2">{r.label}</span>
                        <span className="shrink-0 tabular text-muted-foreground">{fmtInt(r.jours)} j · {fmtNum(r.bs_par_h, 3)} BS/h</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {!autres.campagne_disponible && <p className="mt-3 text-[10px] text-muted-foreground">Répartition par campagne : non disponible (aucun champ campagne exploitable dans les sources).</p>}
          </DetailsBlock>
        </div>

        {/* ============ NIVEAU 2bis — PILOTAGE : recruteurs nominatifs (§6) ============ */}
        <div className="space-y-3" data-testid="horaire-recruteurs">
          <SectionTitle icon={Users} title="Rentabilité par recruteur" sub="trié par marge complète croissante — les plus urgents en premier"
            right={(
              <Button variant="outline" size="sm" className="gap-2 rounded-xl" onClick={doExportRec} disabled={exportingRec} data-testid="horaire-export-recruteurs">
                <Download className="h-4 w-4" /> Export CSV
              </Button>
            )} />
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 rounded-xl border px-3 py-1.5">
              <Switch checked={rActifs} onCheckedChange={setRActifs} data-testid="horaire-filter-actifs" />
              <span className="text-xs">Actifs uniquement <span className="text-muted-foreground">({fmtInt(rMeta.actifs)}/{fmtInt(rMeta.total)})</span></span>
            </div>
            <div className="flex items-center gap-2 rounded-xl border px-3 py-1.5">
              <Switch checked={rAlertes} onCheckedChange={setRAlertes} data-testid="horaire-filter-alertes" />
              <span className="text-xs">Alertes seulement</span>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Badge variant="outline" className="gap-1 border-emerald-300 text-emerald-700"><span className="h-2 w-2 rounded-full bg-emerald-500" /> {fmtInt(rMeta.vert)} rentables</Badge>
              <Badge variant="outline" className="gap-1 border-amber-300 text-amber-700"><span className="h-2 w-2 rounded-full bg-amber-500" /> {fmtInt(rMeta.orange)} à surveiller</Badge>
              <Badge variant="outline" className="gap-1 border-rose-300 text-rose-700"><span className="h-2 w-2 rounded-full bg-rose-500" /> {fmtInt(rMeta.rouge)} non rentables</Badge>
              {rMeta.alertes > 0 && <Badge className="gap-1 border-transparent bg-rose-600 text-white" data-testid="horaire-alertes-count"><AlertTriangle className="h-3 w-3" /> {fmtInt(rMeta.alertes)} alertes 2 semaines</Badge>}
            </div>
          </div>
          <TableWrap>
            <div className="max-h-[520px] overflow-y-auto">
              <CardTable className="w-full text-sm">
                <thead className="sticky top-0 z-10 bg-card text-xs text-muted-foreground">
                  <tr className="border-b">
                    <th className="px-2 py-2 text-left font-medium">Recruteur</th>
                    <th className="px-2 py-2 text-center font-medium">Statut</th>
                    <th className="px-2 py-2 text-right font-medium">Jours</th>
                    <th className="px-2 py-2 text-right font-medium">BS/h</th>
                    <th className="px-2 py-2 text-right font-medium">Marge directe</th>
                    <th className="px-2 py-2 text-right font-medium">Marge complète</th>
                    <th className="px-2 py-2 text-right font-medium">Ancienneté</th>
                    <th className="px-2 py-2 text-center font-medium">Tendance 4 sem.</th>
                  </tr>
                </thead>
                <tbody>
                  {recFiltered.length === 0 && (
                    <tr><td colSpan={8} className="px-2 py-6 text-center text-xs text-muted-foreground">Aucun recruteur pour ce filtre.</td></tr>
                  )}
                  {recFiltered.map((r) => (
                    <tr key={r.matricule} className={`border-b last:border-0 hover:bg-muted/30 ${r.alerte ? 'bg-rose-50/40' : ''}`} data-testid={`horaire-rec-${r.matricule}`}>
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-1.5 font-medium">
                          {r.nom_complet}
                          {r.actif_terrain && <Badge variant="outline" className="border-emerald-300 px-1 py-0 text-[9px] text-emerald-700">actif</Badge>}
                          {r.alerte && (
                            <Tooltip><TooltipTrigger asChild><AlertTriangle className="h-3.5 w-3.5 text-rose-600" /></TooltipTrigger>
                              <TooltipContent>Sous le point mort 2 semaines consécutives · perte cumulée {fmtEur(r.alerte_perte_cumulee, 0)}</TooltipContent></Tooltip>
                          )}
                        </div>
                        <div className="text-[10px] text-muted-foreground">{r.responsable}</div>
                      </td>
                      <td className="px-2 py-2 text-center"><StatutDot statut={r.statut_rentab} /></td>
                      <td className="px-2 py-2 text-right tabular">{fmtInt(r.jours)}</td>
                      <td className="px-2 py-2 text-right tabular font-semibold">{fmtNum(r.bs_par_h, 3)}</td>
                      <td className={`px-2 py-2 text-right tabular ${(r.marge_directe || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{fmtEur(r.marge_directe, 0)}</td>
                      <td className={`px-2 py-2 text-right tabular font-semibold ${(r.marge_complete || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{fmtEur(r.marge_complete, 0)}</td>
                      <td className="px-2 py-2 text-right tabular text-muted-foreground">{r.anciennete_jours != null ? `${fmtInt(Math.floor(r.anciennete_jours / 30))} mois` : '—'}</td>
                      <td className="px-2 py-2 text-center"><TendanceBadge t={r.tendance} /></td>
                    </tr>
                  ))}
                </tbody>
              </CardTable>
            </div>
          </TableWrap>
          <p className="text-[10px] text-muted-foreground">Ancienneté estimée à partir de la 1ʳᵉ activité observée (pas de date d'entrée en base). Vert = couvre la structure · Orange = couvre ses heures mais pas la structure · Rouge = ne couvre pas ses heures.</p>
        </div>

        {/* ============ NIVEAU 3 — DÉTAILS TECHNIQUES (repliables) ============ */}
        <DetailsBlock title="Détail par format de créneau (3 h / 4 h / 5 h / Autres)" icon={Layers} testid="horaire-formats">
          <TableWrap>
            <CardTable className="w-full text-sm">
              <thead className="text-xs text-muted-foreground">
                <tr className="border-b">
                  <th className="px-2 py-2 text-left font-medium">Format</th>
                  <th className="px-2 py-2 text-right font-medium">Jours</th>
                  <th className="px-2 py-2 text-right font-medium">Heures</th>
                  <th className="px-2 py-2 text-right font-medium">BS/jour</th>
                  <th className="px-2 py-2 text-right font-medium">BS/h</th>
                  <th className="px-2 py-2 text-right font-medium">Don moyen</th>
                  <th className="px-2 py-2 text-right font-medium">Marge directe</th>
                  <th className="px-2 py-2 text-right font-medium">Marge complète</th>
                  <th className="px-2 py-2 text-center font-medium">Fiabilité</th>
                </tr>
              </thead>
              <tbody>
                {fmts.map((f) => (
                  <tr key={f.format} className="border-b last:border-0 hover:bg-muted/30" data-testid={`horaire-fmt-row-${f.format}`}>
                    <td className="px-2 py-2 font-semibold">{FMT_LABEL[f.format] || f.format}</td>
                    <td className="px-2 py-2 text-right tabular">{fmtInt(f.jours)}</td>
                    <td className="px-2 py-2 text-right tabular">{fmtNum(f.heures, 0)}</td>
                    <td className="px-2 py-2 text-right tabular">{fmtNum(f.bs_par_jour, 2)}</td>
                    <td className="px-2 py-2 text-right tabular font-semibold">{fmtNum(f.bs_par_h, 3)}</td>
                    <td className="px-2 py-2 text-right tabular">{fmtEur(f.don_moyen, 2)}</td>
                    <td className={`px-2 py-2 text-right tabular ${(f.marge_directe || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{fmtEur(f.marge_directe, 0)}</td>
                    <td className={`px-2 py-2 text-right tabular font-semibold ${(f.marge_complete || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{fmtEur(f.marge_complete, 0)}</td>
                    <td className="px-2 py-2 text-center"><FiabBadge level={f.fiabilite} jours={f.jours} /></td>
                  </tr>
                ))}
              </tbody>
            </CardTable>
          </TableWrap>
        </DetailsBlock>

        <DetailsBlock title="Par responsable" icon={Users} testid="horaire-responsables">
          <TableWrap>
            <CardTable className="w-full text-sm">
              <thead className="text-xs text-muted-foreground">
                <tr className="border-b">
                  <th className="px-2 py-2 text-left font-medium">Responsable</th>
                  <th className="px-2 py-2 text-right font-medium">Jours</th>
                  <th className="px-2 py-2 text-right font-medium">BS/jour</th>
                  <th className="px-2 py-2 text-right font-medium">BS/h</th>
                  <th className="px-2 py-2 text-right font-medium">Part 5 h</th>
                  <th className="px-2 py-2 text-right font-medium">Marge complète</th>
                </tr>
              </thead>
              <tbody>
                {responsables.map((r) => (
                  <tr key={r.label} className="border-b last:border-0 hover:bg-muted/30" data-testid={`horaire-resp-${r.label}`}>
                    <td className="px-2 py-2 font-semibold">{r.label}</td>
                    <td className="px-2 py-2 text-right tabular">{fmtInt(r.jours)}</td>
                    <td className="px-2 py-2 text-right tabular">{fmtNum(r.bs_par_jour, 2)}</td>
                    <td className="px-2 py-2 text-right tabular font-semibold">{fmtNum(r.bs_par_h, 3)}</td>
                    <td className="px-2 py-2 text-right tabular">{fmtPct(r.part_5h, 0)}</td>
                    <td className={`px-2 py-2 text-right tabular font-semibold ${(r.marge_complete || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{fmtEur(r.marge_complete, 0)}</td>
                  </tr>
                ))}
              </tbody>
            </CardTable>
          </TableWrap>
        </DetailsBlock>

        <DetailsBlock title="Évolution mensuelle de la productivité (BS/h)" icon={TrendingUp} testid="horaire-evolution">
          {evolution.length === 0 ? (
            <p className="text-xs text-muted-foreground">Aucun mois observé sur la période.</p>
          ) : (
            <div className="space-y-2.5">
              {evolution.map((e) => (
                <div key={e.ym} className="flex items-center gap-3" data-testid={`horaire-evo-${e.ym}`}>
                  <span className="w-28 shrink-0 text-xs font-medium first-letter:uppercase">{labelYm(e.ym)}</span>
                  <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-muted">
                    <div className="h-full rounded-full" style={{ width: `${((e.bs_par_h || 0) / maxEvoBsh) * 100}%`, background: 'var(--tenant-accent-500)' }} />
                  </div>
                  <span className="w-16 shrink-0 text-right text-xs font-semibold tabular">{fmtNum(e.bs_par_h, 3)}</span>
                  <span className={`w-24 shrink-0 text-right text-xs font-semibold tabular ${(e.marge_complete || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{fmtEur(e.marge_complete, 0)}</span>
                </div>
              ))}
            </div>
          )}
        </DetailsBlock>

        {/* Bannière honnêteté intraday + ce qui deviendra possible (§8) */}
        <DetailsBlock title="Pourquoi pas d'analyse heure par heure ? (et ce qui deviendra possible)" icon={Info} testid="horaire-intraday-notice">
          <div className="space-y-3">
            <p className="text-xs leading-relaxed text-muted-foreground">{diag.raison}</p>
            <div className="flex flex-wrap gap-1.5" data-testid="horaire-intraday-blocked-list">
              {(diag.analyses_indisponibles || []).map((a, i) => (
                <Badge key={i} variant="secondary" className="gap-1 bg-amber-100/70 text-[10px] text-amber-900"><CircleAlert className="h-3 w-3" /> {a}</Badge>
              ))}
            </div>
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3">
              <div className="flex items-center gap-2 text-emerald-800">
                <CalendarClock className="h-4 w-4" />
                <span className="text-xs font-semibold">Ce qui deviendra possible une fois l'horodatage collecté</span>
              </div>
              <ul className="mt-2 space-y-1">
                {(diag.futur?.ce_qui_deviendra_possible || []).map((x, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-[11px] text-emerald-900"><Check className="mt-0.5 h-3 w-3 shrink-0" /> {x}</li>
                ))}
              </ul>
              <p className="mt-2 text-[10px] text-emerald-900/70">À collecter : {(diag.futur?.donnees_a_collecter || []).join(' · ')}. {diag.futur?.note} (données présentes depuis {diag.futur?.collecte_depuis}).</p>
            </div>
          </div>
        </DetailsBlock>

        <DetailsBlock title="Autres conclusions (secondaires) & méthode" icon={FileCheck2} testid="horaire-secondaires">
          <div className="space-y-2.5">
            {secondaires.map((s, i) => (
              <div key={i} className="rounded-lg border p-3" data-testid={`horaire-secondaire-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold">{s.titre}</span>
                  {s.impact_eur_annuel != null && (
                    <span className={`text-xs font-semibold tabular ${s.impact_eur_annuel >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{s.impact_eur_annuel >= 0 ? '+' : ''}{fmtEur(s.impact_eur_annuel, 0)}/an</span>
                  )}
                </div>
                <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{s.phrase}</p>
              </div>
            ))}
            <p className="pt-1 text-[10px] text-muted-foreground" data-testid="horaire-source-note">
              Source : {d.source_horodatage?.message} Salariés uniquement ; indépendants exclus
              ({fmtInt(per.exclusions?.independants)} BS) et P.A./sous-traitants sans heures ({fmtInt(per.exclusions?.sans_heures_pa_soustraitants)} BS).
              Périmètre : {fmtInt(per.salaries_inclus)} salariés · {fmtInt(per.recruteur_jours)} recruteur-jours.
              Annualisation : {annu.methode === 'profil_mensuel_saisonnier' ? `profil saisonnier depuis ${annu.periode_reference?.depuis}` : 'aucune (période observée)'}.
            </p>
          </div>
        </DetailsBlock>
      </div>
    </TooltipProvider>
  );
}

// --------------------------------------------------------------------------- //
//  Page                                                                        //
// --------------------------------------------------------------------------- //
export default function SuiviSocial() {
  const status = useGet('/social/status');
  const qc = useQueryClient();
  const [ymSel, setYmSel] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncingBk, setSyncingBk] = useState(false);
  const [diag, setDiag] = useState(null);
  const [diagOpen, setDiagOpen] = useState(false);
  const [tab, setTab] = useState('mois');

  const months = status.data?.months || [];
  const ym = ymSel || status.data?.latest_month || (months[0]) || '2026-07';

  const syncPrerec = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/social/sync-prerec');
      toast.success(`PREREC synchronisé : ${data.imported} collaborateurs`);
      qc.invalidateQueries({ predicate: (query) => String(query.queryKey?.[0] || '').startsWith('/social') });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Synchronisation PREREC indisponible');
    } finally { setSyncing(false); }
  };

  const syncBooking = async () => {
    setSyncingBk(true);
    try {
      const { data } = await api.post('/social/sync-booking', {}, { params: { ym } });
      if (data?.ok && data.imported > 0) {
        toast.success(`Booking synchronisé : ${data.imported} collaborateurs · ${data.rows} lignes (${labelYm(ym)})`);
        qc.invalidateQueries({ predicate: (query) => String(query.queryKey?.[0] || '').startsWith('/social') });
      } else if (data?.ok) {
        // 0 ligne : ne JAMAIS le présenter comme un succès silencieux → diagnostic complet
        const dg = data.diagnostic || {};
        toast.warning(`Booking : 0 ligne importée — HTTP ${dg.status ?? '?'}, ${dg.raw_count ?? 0} élément(s) brut(s). Diagnostic complet ouvert…`,
          { duration: 6000 });
        await runDiagnostic();
      } else {
        toast.warning(data?.detail || 'Booking indisponible (import Excel toujours actif)');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Synchronisation Booking indisponible');
    } finally { setSyncingBk(false); }
  };

  const runDiagnostic = async () => {
    try {
      const { data } = await api.get('/social/booking-diagnostic', { params: { ym } });
      setDiag({ mode: 'diagnostic', ...data });
      setDiagOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Diagnostic Booking indisponible');
    }
  };

  return (
    <div className="space-y-6 fade-up">
      <PageHeader
        title="Suivi Social"
        icon={Wallet}
        subtitle="P&L social & automatisation de la paie PayFit — réservé à l'administration SHARITI"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Select value={ym} onValueChange={setYmSel}>
              <SelectTrigger className="w-40 rounded-xl sm:w-44" data-testid="social-month-picker"><SelectValue /></SelectTrigger>
              <SelectContent>
                {months.map((m) => <SelectItem key={m} value={m}>{labelYm(m)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" className="gap-2 rounded-xl" onClick={syncPrerec} disabled={syncing} data-testid="social-sync-btn">
              <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} /> PREREC
            </Button>
            {status.data?.booking_configured && (
              <>
                <Button variant="outline" className="gap-2 rounded-xl" onClick={syncBooking} disabled={syncingBk} data-testid="social-sync-booking-btn">
                  <RefreshCw className={`h-4 w-4 ${syncingBk ? 'animate-spin' : ''}`} /> Booking
                </Button>
                <Button variant="ghost" size="icon" className="rounded-xl" onClick={runDiagnostic} title="Diagnostic Booking" data-testid="social-booking-diag-btn">
                  <Stethoscope className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        }
      />

      {/* Status strip */}
      <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="social-status-strip">
        <Badge className="gap-1 border-transparent" style={{ background: 'var(--tenant-accent-050)', color: 'var(--tenant-accent-600)' }}>
          <ShieldCheck className="h-3.5 w-3.5" /> Accès exclusif SHARITI_ADMIN
        </Badge>
        <Badge variant={status.data?.prerec_configured ? 'secondary' : 'outline'} className="gap-1" data-testid="social-prerec-badge">
          <Plug className="h-3.5 w-3.5" /> PREREC {status.data?.prerec_configured ? 'connecté' : 'non configuré'}
        </Badge>
        <Badge variant={status.data?.booking_configured ? 'secondary' : 'outline'} className="gap-1" data-testid="social-booking-badge">
          <Plug className="h-3.5 w-3.5" /> Booking {status.data?.booking_configured ? 'connecté' : 'absent (import Excel actif)'}
        </Badge>
        <Badge variant="secondary">{status.data?.nb_collaborateurs || 0} collaborateurs référencés</Badge>
      </div>

      <Dialog open={diagOpen} onOpenChange={setDiagOpen}>
        <DialogContent className="max-w-3xl" data-testid="booking-diagnostic-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Stethoscope className="h-5 w-5" /> Diagnostic Booking — {labelYm(ym)}</DialogTitle>
            <DialogDescription>
              Réponse réelle de l'API Booking (lecture seule). Copie ce contenu et transmets-le : le mapping sera calé sur les vrais champs.
            </DialogDescription>
          </DialogHeader>
          <pre className="max-h-[60vh] overflow-auto rounded-xl bg-muted/60 p-4 text-[11px] leading-relaxed" data-testid="booking-diagnostic-json">
{diag ? JSON.stringify(diag, null, 2) : ''}
          </pre>
        </DialogContent>
      </Dialog>

      <Tabs value={tab} onValueChange={setTab}>
        {/* Navigation mobile — sélecteur pleine largeur (toutes les vues accessibles) */}
        <div className="lg:hidden">
          <Select value={tab} onValueChange={setTab}>
            <SelectTrigger className="h-11 w-full rounded-xl" data-testid="social-tabs-mobile">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>Vues</SelectLabel>
                {MAIN_TABS.map(({ k, label, icon: Icon }) => (
                  <SelectItem key={k} value={k} data-testid={`mtab-${k}`}>
                    <span className="flex items-center gap-2"><Icon className="h-4 w-4" /> {label}</span>
                  </SelectItem>
                ))}
              </SelectGroup>
              <SelectSeparator />
              <SelectGroup>
                <SelectLabel>Administration</SelectLabel>
                {CONFIG_TAB_ITEMS.map(({ k, label, icon: Icon }) => (
                  <SelectItem key={k} value={k} data-testid={`mtab-${k}`}>
                    <span className="flex items-center gap-2"><Icon className="h-4 w-4" /> {label}</span>
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        {/* Navigation desktop — onglets + menu Configuration (≥ lg) */}
        <div className="hidden items-center justify-between gap-3 border-b pb-3 lg:flex">
          <TabsList className="flex flex-nowrap gap-1 overflow-x-auto" data-testid="social-tabs">
            <TabsTrigger value="mois" className="gap-1.5 px-3.5 py-2 whitespace-nowrap" data-testid="tab-mois"><Wallet className="h-4 w-4" /> Vue Mois</TabsTrigger>
            <TabsTrigger value="pnl" className="gap-1.5 px-3.5 py-2 whitespace-nowrap" data-testid="tab-pnl"><BarChart3 className="h-4 w-4" /> P&L</TabsTrigger>
            <TabsTrigger value="rentab" className="gap-1.5 px-3.5 py-2 whitespace-nowrap" data-testid="tab-rentab"><Gauge className="h-4 w-4" /> Rentabilité</TabsTrigger>
            <TabsTrigger value="horaire" className="gap-1.5 px-3.5 py-2 whitespace-nowrap" data-testid="tab-horaire"><Clock className="h-4 w-4" /> Analyse Horaire</TabsTrigger>
            <TabsTrigger value="paie" className="gap-1.5 px-3.5 py-2 whitespace-nowrap" data-testid="tab-paie"><Receipt className="h-4 w-4" /> Paie indés</TabsTrigger>
            <TabsTrigger value="payfit" className="gap-1.5 px-3.5 py-2 whitespace-nowrap" data-testid="tab-payfit"><FileSpreadsheet className="h-4 w-4" /> Export PayFit</TabsTrigger>
            <TabsTrigger value="jour" className="gap-1.5 px-3.5 py-2 whitespace-nowrap" data-testid="tab-jour"><CalendarDays className="h-4 w-4" /> Vue Jour</TabsTrigger>
            <TabsTrigger value="collab" className="gap-1.5 px-3.5 py-2 whitespace-nowrap" data-testid="tab-collab"><UserSearch className="h-4 w-4" /> Collaborateur</TabsTrigger>
          </TabsList>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant={CONFIG_TABS.includes(tab) ? 'default' : 'outline'}
                className="gap-2 rounded-xl whitespace-nowrap"
                style={CONFIG_TABS.includes(tab) ? accentStyle : undefined}
                data-testid="social-config-menu">
                <Settings2 className="h-4 w-4" />
                {CONFIG_TABS.includes(tab) ? CONFIG_LABELS[tab] : 'Configuration'}
                <ChevronDown className="h-4 w-4 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel>Administration</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setTab('rattach')} className="gap-2" data-testid="menu-tab-rattach">
                <Link2 className="h-4 w-4" /> Rattachement
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTab('params')} className="gap-2" data-testid="menu-tab-params">
                <Settings2 className="h-4 w-4" /> Paramètres
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTab('import')} className="gap-2" data-testid="menu-tab-import">
                <UploadCloud className="h-4 w-4" /> Import heures
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="mt-6">
          <TabsContent value="mois"><MonthView ym={ym} /></TabsContent>
          <TabsContent value="pnl"><PnlView ym={ym} /></TabsContent>
          <TabsContent value="rentab"><RentabiliteView ym={ym} /></TabsContent>
          <TabsContent value="horaire"><HoraireView ym={ym} /></TabsContent>
          <TabsContent value="paie"><PaieIndesView ym={ym} /></TabsContent>
          <TabsContent value="payfit"><PayFitExport ym={ym} /></TabsContent>
          <TabsContent value="jour"><DayView ym={ym} /></TabsContent>
          <TabsContent value="collab"><CollabView /></TabsContent>
          <TabsContent value="rattach"><RattachementView /></TabsContent>
          <TabsContent value="params"><ParamsView ym={ym} /></TabsContent>
          <TabsContent value="import"><ImportView /></TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
